import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {Canvas, useFrame, useThree} from "@react-three/fiber";
import {Edges, Html, OrbitControls, OrthographicCamera, PerspectiveCamera, TransformControls} from "@react-three/drei";
import * as THREE from "three";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {Link, useNavigate} from "react-router-dom";
import {EditLocalStoragePagesData} from "../../../utils/EditLocalStoragePagesData";
import {
    compareDetailsBySceneDrawOrder,
    getDetailSceneDrawOrder,
    sortDetailsBySequenceNumber,
} from "../../../utils/processing/orderContourPoints";
import ModuleDetailMesh from "./module3d/ModuleDetailMesh";
import HandleMesh from "./module3d/HandleMesh";
import DetailCornerHandles from "./module3d/DetailCornerHandles";
import Module3DToolbar from "./module3d/Module3DToolbar";
import Module3DViewBar from "./module3d/Module3DViewBar";
import {
    CAMERA_VIEW,
    applyOrthoCameraView,
    applyPerspectiveCameraView,
    getCameraViewPose,
    isOrthographicView,
    isPlanarOrthoView,
} from "./module3d/cameraViews";
import {
    clampTextureScaleMm,
    createDefaultTextureCatalog,
    DEFAULT_TEXTURE_ROTATION_DEG,
    DEFAULT_TEXTURE_SCALE_MM,
    loadImageUrlAsDataUrl,
    normalizeTextureRotationDeg,
    readImageFileAsUploadSource,
    TEST_TEXTURES,
} from "./module3d/detailTextures";
import CustomTextureUploadDialog from "./module3d/CustomTextureUploadDialog";
import TextureDeleteConfirmDialog from "./module3d/TextureDeleteConfirmDialog";
import ModalConfirm from "../../../components/UI/modal/ModalConfirm";
import DxfImportDialog, {applyHolesDepth} from "./module3d/DxfImportDialog";
import HandleImportDialog from "./module3d/HandleImportDialog";
import ImportSourceDialog, {
    IMPORT_SOURCE_3DS,
} from "./module3d/ImportSourceDialog";
import {parseDxfToDetailData} from "./module3d/dxf/parseDxfToDetailData";
import {
    DEFAULT_HANDLE_SIZE_MM,
    getHandleMaterialPreviewFromBase64,
    isHandleDetail,
    load3dsHandleFromFile,
} from "./module3d/tds/parse3dsToHandle";
import {
    DEFAULT_HANDLE_MATERIAL_ID,
    createDefaultHandleMaterialCatalog,
    getHandleMaterialById,
    hydrateHandleMaterialCatalog,
    isHandleMaterialFromFile,
    resolveHandleMaterialProps,
} from "./module3d/handleMaterials";
import HandleMaterialDialog from "./module3d/HandleMaterialDialog";
import {exportModuleSceneToGlb} from "./module3d/exportGlb";
import {
    buildModule3dAssemblyFile,
    downloadModule3dAssemblyFile,
    getCompositionPosition,
    parseAssemblyPose,
    placementsFromPlacedDetails,
    readModule3dAssemblyFile,
} from "./module3d/assemblyFile";
import {isThroughFaceHole} from "./module3d/detailHoles";
import {
    hydrateImportedDetails,
    hydratePlacedDetails,
    hydrateTextureCatalog,
    loadModule3DState,
    saveModule3DState,
} from "./module3d/module3dPersistence";
import ModalAlert from "../../../components/UI/table/customer-detales/ModalAlert";
import {constantsErrorMessages} from "../../../Constants";
import DetailCompositionController from "../../../API/organization/DetailCompositionController";
import {
    applyAxesToObject,
    axesToEulerDegrees,
    cloneAxes,
    IDENTITY_AXES,
    mirrorAxesWithFaceFlip,
    normalizeAxesWithFaceFlip,
    eulerForInstancePersist,
    normalizeDeg,
    rotateAxesBy90,
    snapEulerTo90,
    quantizeAxesTo90Degrees,
    isCardinalAxes,
} from "./module3d/orientationAxes";
import {
    anglesOnSingleAxis,
    composePanelAxes,
    enforceSingleAxisAngles,
    extractLocalAnglesDeg,
    lockedPanelRotationAxisKey,
    normalizeLocalRotationAngles,
    resolvePanelPoseFromAxes,
    resolvePanelRotationState,
} from "./module3d/localRotation";
import {deletePacedDetailPlacement, extractPacedRecordId, fetchPacedDetailPlacement, getPacedDetailModuleId, getPacedDtoHide, getPostDetailId, hideAllPacedDetails, hideAllPacedDetailsExcept, isModuleDetailPlacedDto, parsePacedDetailModuleDto, parsePacedPlacementFromDetailRow, persistPacedDetailPlacement, resolveModuleDetailWithPlacement, resolvePacedDtoHidden, resolvePacedExistingDto, resolvePlacementPathId, showAllPacedDetails, stampCompositionDetailId, stampCompositionDetailList, updatePacedDetailHidden, updatePacedDetailScenePose} from "./module3d/pacedDetailModule";
import PlacedEmbeddedModuleGroup from "./module3d/PlacedEmbeddedModuleGroup";
import DetailPanelAxes from "./module3d/DetailPanelAxes";
import PanelRotationAxisIndicator from "./module3d/PanelRotationAxisIndicator";
import ModuleCubeAxes from "./module3d/ModuleCubeAxes";
import {
    buildPlacedEmbeddedInstanceIfPlaced,
    buildPlacedEmbeddedInstanceShellFromPose,
    embeddedModulePlacementDetailData,
    getDependModuleId,
    getDependModuleKey,
    invalidateEmbeddedModuleDetailsCache,
    isEmbeddedModulePlacedDto,
    loadEmbeddedModuleChildren,
    normalizeDependModuleRow,
} from "./module3d/embeddedModuleScene";
import {
    createEmbeddedModulePlacement,
    deleteEmbeddedModulePlacement,
    getPacedModuleId,
    hideAllPacedEmbeddedModules,
    hideAllPacedEmbeddedExcept,
    invalidateEmbeddedModulePlacementCache,
    resolveEmbeddedPacedDeleteId,
    resolveEmbeddedPacedExistingDto,
    showAllPacedEmbeddedModules,
    updateEmbeddedModuleScenePose,
    updatePacedModuleHidden,
} from "./module3d/pacedModule";
import {
    resolveDragPose,
    resolvePlacementPose,
    snapLocalToOtherDetails,
    cubeLocalToWorld,
    worldPointToCubeLocal,
    getDominantOrthoAxis,
    orthoAxisFromTransformControls,
    projectLocalToOrthoAxis,
    moduleDimensionsMmToSizeM,
    getCubeOrigin,
    getSnapApproachPreview,
} from "./module3d/snapPlacement";
import {
    applyFreeRotationDragStep,
    createFreeRotationDragState,
    getRotationAxisDirInPoseParent,
    raycastScreenToCameraPlane,
} from "./module3d/freeRotationDrag";
import {
    createHistorySnapshot,
    loadModule3DHistory,
    saveModule3DHistory,
    pushHistorySnapshot,
    undoHistory,
    redoHistory,
} from "./module3d/module3dHistory";

/** ПКМ-клик vs панорама: меньше этого сдвига — контекстное меню, больше — орбита. */
const POINTER_CLICK_SLOP_PX = 5;

const DETAIL_ADD_ORIENTATION_OPTIONS = [
    {
        id: "horizontal",
        label: "Горизонтально",
        axes: IDENTITY_AXES,
    },
    {
        id: "vertical",
        label: "Вертикально",
        axes: {
            x: [0, 1, 0],
            y: [1, 0, 0],
            z: [0, 0, -1],
        },
    },
    {
        id: "frontal",
        label: "Фронтально",
        axes: {
            x: [1, 0, 0],
            y: [0, 0, 1],
            z: [0, -1, 0],
        },
    },
];

/**
 * Зелёный полупрозрачный объём — ориентир габаритов модуля (м: width×height×depth).
 * В режиме добавления клики не ловит (иначе бьётся ближняя грань вместо задней стенки).
 */
const ReferenceCube = ({sizeM, addingMode, ignorePointer = false}) => {
    const {width, height, depth} = sizeM;
    const skipRaycast = addingMode || ignorePointer;
    return (
        <mesh
            position={[0, height / 2, 0]}
            userData={{module3dRole: "helper"}}
            raycast={skipRaycast ? () => null : undefined}
        >
            <boxGeometry args={[width, height, depth]}/>
            <meshStandardMaterial
                color="#2e7d32"
                transparent
                opacity={0.28}
                depthWrite={false}
            />
            <Edges color="#1b5e20" threshold={15}/>
        </mesh>
    );
};

/**
 * Невидимые плоскости на полу и двух задних стенках — куда кликать при добавлении.
 * Ориентация всегда горизонтальная; плоскости только задают точку клика.
 */
const AnchorPlacementFaces = ({sizeM, enabled, onAddAt}) => {
    if (!enabled) return null;

    const {width, height, depth} = sizeM;
    const inset = 0.002;
    const mat = {
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
    };

    const place = (e) => {
        e.stopPropagation();
        onAddAt?.(e.point, e, "cube");
    };

    return (
        <group userData={{module3dRole: "placement"}}>
            <mesh
                position={[0, inset, 0]}
                rotation={[-Math.PI / 2, 0, 0]}
                onPointerDown={place}
                userData={{module3dRole: "placement"}}
            >
                <planeGeometry args={[Math.max(width * 4, 8), Math.max(depth * 4, 8)]}/>
                <meshBasicMaterial {...mat}/>
            </mesh>
            <mesh
                position={[-width / 2 + inset, height / 2, 0]}
                rotation={[0, Math.PI / 2, 0]}
                onPointerDown={place}
                userData={{module3dRole: "placement"}}
            >
                <planeGeometry args={[Math.max(depth * 2, 4), Math.max(height * 2, 4)]}/>
                <meshBasicMaterial {...mat}/>
            </mesh>
            <mesh
                position={[0, height / 2, -depth / 2 + inset]}
                onPointerDown={place}
                userData={{module3dRole: "placement"}}
            >
                <planeGeometry args={[Math.max(width * 2, 4), Math.max(height * 2, 4)]}/>
                <meshBasicMaterial {...mat}/>
            </mesh>
        </group>
    );
};

/**
 * В режиме добавления ЛКМ не крутит орбиту: ловим клик на канвасе и ставим деталь.
 * Иначе первый клик «съедает» OrbitControls, и кажется, что сцена зависла.
 */
const PlacementClickCatcher = ({enabled, onAddAt}) => {
    const {camera, gl, scene} = useThree();

    useEffect(() => {
        if (!enabled) return undefined;
        const el = gl.domElement;

        const onDown = (ev) => {
            if (ev.button !== 0) return;

            const rect = el.getBoundingClientRect();
            const ndc = new THREE.Vector2(
                ((ev.clientX - rect.left) / rect.width) * 2 - 1,
                -((ev.clientY - rect.top) / rect.height) * 2 + 1,
            );
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(ndc, camera);
            const hits = raycaster.intersectObjects(scene.children, true);
            const hit = hits.find((entry) => {
                let obj = entry.object;
                while (obj) {
                    const role = obj.userData?.module3dRole;
                    if (role === "placement" || role === "detail") return true;
                    obj = obj.parent;
                }
                return false;
            });

            const point = new THREE.Vector3();
            if (hit?.point) {
                point.copy(hit.point);
            } else {
                const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
                if (!raycaster.ray.intersectPlane(floor, point)) return;
            }

            ev.stopImmediatePropagation();
            onAddAt?.(point, {
                point,
                object: hit?.object,
                face: hit?.face,
            }, hit ? "detail" : "cube");
        };

        el.addEventListener("pointerdown", onDown, true);
        return () => el.removeEventListener("pointerdown", onDown, true);
    }, [camera, enabled, gl, onAddAt, scene]);

    return null;
};

/** Оси в углу объёма с читаемыми подписями X / Y / Z. */
const CubeAxes = ({sizeM, emphasizedAxis = null}) => (
    <ModuleCubeAxes sizeM={sizeM} emphasizedAxis={emphasizedAxis}/>
);

const getDetailLabel = (detail, index) =>
    detail?.name ?? detail?.position ?? detail?.id ?? `detail-${index + 1}`;

const getDetailKey = (detail, index) =>
    detail?.compositionDetailId ?? detail?.id ?? detail?.position ?? getDetailLabel(detail, index);

const PACED_MODULE_TAB = 9;

/** Тот же URL, что в ModulesTable на вкладке «Встраиваемые модули». */
const buildDependModulePagePath = (dependModuleRow, parentModuleId, parentModuleName) => {
    const dependId = dependModuleRow?.id;
    if (dependId == null || dependId === "") {
        return null;
    }
    if (parentModuleId != null && parentModuleId !== "") {
        return `/organization/module/${dependId}/depend/${parentModuleId}/${encodeURIComponent(parentModuleName ?? "")}`;
    }
    return `/organization/module/${dependId}`;
};

const formatEmbeddedModuleSize = (row) => {
    const parts = [row?.width, row?.height, row?.depth]
        .filter((value) => value != null && value !== "")
        .map((value) => {
            const n = Number(value);
            return Number.isFinite(n) ? String(Math.round(n * 10) / 10) : String(value);
        });
    return parts.length ? parts.join(" × ") : "-";
};

const MODULE3D_TABLE_SX = {
    width: "100%",
    minWidth: "100%",
    tableLayout: "fixed",
    "& th": {
        border: "1px solid #EAEBEA",
        backgroundColor: "#F5F6FA",
        padding: "10px",
        paddingBottom: "0.2rem",
        paddingTop: "0.2rem",
        color: "black",
    },
    "& td": {
        padding: "0px 0px 0px 10px",
        m: "0px 0px 0px 0px",
        border: "none",
    },
    "& td:not(:last-of-type)": {
        borderRight: "1px solid #EAEBEA",
    },
};

const TABLE_ADD_BUTTON_SX = {
    minWidth: "30px",
    width: "30px",
    height: "30px",
    padding: 0,
    borderRadius: "4px",
    margin: 0,
    flexShrink: 0,
    "&:hover:not(.Mui-disabled)": {
        backgroundColor: (theme) => theme.palette.primary.light,
    },
    "&.Mui-disabled:not(.table-add-muted)": {
        opacity: 1,
    },
    "&.Mui-disabled:not(.table-add-muted).MuiButton-outlinedPrimary": {
        borderColor: (theme) => theme.palette.primary.main,
        color: (theme) => theme.palette.primary.main,
    },
    "&.Mui-disabled:not(.table-add-muted).MuiButton-containedSecondary": {
        backgroundColor: (theme) => theme.palette.secondary.main,
        color: (theme) => theme.palette.secondary.contrastText,
    },
    "&.Mui-disabled.table-add-muted": {
        opacity: 0.38,
    },
    "&.Mui-disabled.table-add-muted.MuiButton-outlinedPrimary": {
        borderColor: (theme) => theme.palette.action.disabled,
        color: (theme) => theme.palette.action.disabled,
    },
};

const TableAddButton = ({
    showMinus = false,
    disabled = false,
    muted = false,
    tooltip = "",
    ariaLabel,
    onClick,
}) => (
    <Tooltip title={tooltip} arrow placement="top">
        <span>
            <Button
                size="small"
                variant={showMinus ? "contained" : "outlined"}
                color={showMinus ? "secondary" : "primary"}
                disabled={disabled}
                className={muted ? "table-add-muted" : undefined}
                onClick={(event) => {
                    event.stopPropagation();
                    onClick?.(event);
                }}
                aria-label={ariaLabel}
                sx={TABLE_ADD_BUTTON_SX}
            >
                {showMinus ? "−" : "+"}
            </Button>
        </span>
    </Tooltip>
);

const TABLE_VISIBILITY_BUTTON_SX = {
    minWidth: 28,
    width: 28,
    height: 28,
    p: 0,
    flexShrink: 0,
    borderRadius: "4px",
    border: (theme) => `1px solid ${theme.palette.primary.main}`,
    color: (theme) => theme.palette.primary.main,
    "&:hover": {
        backgroundColor: (theme) => theme.palette.primary.light50,
    },
    "&.Mui-disabled": {
        opacity: 0.38,
        borderColor: (theme) => theme.palette.action.disabled,
        color: (theme) => theme.palette.action.disabled,
    },
};

const TableVisibilityButton = ({
    hidden = false,
    disabled = false,
    tooltip,
    onClick,
}) => {
    const defaultTooltip = hidden ? "Показать на сцене" : "Скрыть на сцене";
    const ariaLabel = hidden ? "Показать на сцене" : "Скрыть на сцене";
    return (
        <Tooltip
            title={tooltip ?? defaultTooltip}
            arrow
            placement="top"
        >
            <span>
                <IconButton
                    size="small"
                    disabled={disabled}
                    aria-label={ariaLabel}
                    onClick={(event) => {
                        event.stopPropagation();
                        onClick?.(event);
                    }}
                    sx={TABLE_VISIBILITY_BUTTON_SX}
                >
                    {hidden
                        ? <VisibilityIcon sx={{fontSize: 18}}/>
                        : <VisibilityOffIcon sx={{fontSize: 18}}/>}
                </IconButton>
            </span>
        </Tooltip>
    );
};

const withAssemblyPose = (inst, parsed) => {
    const axes = parsed?.axes || IDENTITY_AXES;
    return {
        ...inst,
        localPosition: parsed.localPosition,
        axes,
        faceFlip: Boolean(parsed.faceFlip),
        euler: axesToEulerDegrees(axes),
        hidden: false,
    };
};

const TABLE_TEXT_LINK_SX = {
    color: "#006CCC",
    cursor: "pointer",
    "&:hover": {textDecoration: "underline"},
};

/** Ссылка только по тексту; клик по пустому месту ячейки всплывает на строку. */
const TABLE_TEXT_LINK_INLINE_SX = {
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
};

const TableTextLink = ({title, onClick, children, sx}) => (
    <Box
        component="span"
        role="link"
        tabIndex={0}
        title={title}
        onClick={(event) => {
            event.stopPropagation();
            onClick?.();
        }}
        onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
            }
        }}
        sx={{...TABLE_TEXT_LINK_SX, ...sx}}
    >
        {children}
    </Box>
);

const buildPlacedInstanceFromModuleDetail = (detail, index = 0) => {
    const row = stampCompositionDetailId(detail);
    const paced = parsePacedDetailModuleDto(row?.pacedDetailModule);
    if (!row || !paced?.localPosition || !paced?.axes) return null;

    const detailKey = getDetailKey(row, index);
    const pacedId = getPacedDetailModuleId(row);
    const instanceId = pacedId != null
        ? `paced-${pacedId}`
        : `module-${detailKey}`;

    const pose = resolvePanelPoseFromAxes({
        axes: paced.axes,
        faceFlip: paced.faceFlip,
        referenceAxes: paced.referenceAxes,
        localRotationAngles: paced.localRotationAngles,
    });

    return {
        instanceId,
        detail: row,
        detailData: row,
        detailKey,
        localPosition: paced.localPosition,
        axes: pose.axes,
        faceFlip: pose.faceFlip,
        euler: eulerForInstancePersist(pose.axes, axesToEulerDegrees(pose.axes)),
        referenceAxes: pose.referenceAxes,
        localRotationAngles: pose.localRotationAngles,
        textureId: null,
        textureUrl: null,
        textureScaleMm: DEFAULT_TEXTURE_SCALE_MM,
        textureRotationDeg: DEFAULT_TEXTURE_ROTATION_DEG,
        handleMaterialId: isHandleDetail(row)
            ? DEFAULT_HANDLE_MATERIAL_ID
            : null,
        pacedUrlId: pacedId,
        pacedDetailModuleId: pacedId,
        pacedDetailModule: row.pacedDetailModule,
        fromImport: false,
        hidden: getPacedDtoHide(row?.pacedDetailModule),
    };
};

const hasContour = (detail) => Boolean(detail?.detailContour?.points?.length);

const isReadyToPlace = (detail) =>
    Boolean(
        detail
        && (
            hasContour(detail)
            || detail?.source === "dxf"
            || detail?.source === "3ds"
            || detail?.kind === "handle"
            || (Number(detail?.length) > 0 && Number(detail?.width) > 0)
        ),
    );

const detailCache = new Map();

/** Геометрия модуля уже в строке таблицы — не ходим в DetailService (это заказные детали). */
const resolveDetailData = async (detail) => {
    if (isReadyToPlace(detail)) {
        return detail;
    }
    const id = detail?.id;
    if (id == null) {
        return detail;
    }
    const key = String(id);
    const cached = detailCache.get(key);
    if (cached) {
        return cached;
    }
    return detail;
};

/**
 * Размещённая деталь: позиция из стейта, при выборе — TransformControls (только move).
 * Во время драга OrbitControls отключаются; в стейт пишем по отпусканию мыши.
 */
const hideMultiAxisTransformHandles = (controls) => {
    if (!controls) return;
    controls.traverse((obj) => {
        const name = obj.name;
        if (name === "XY" || name === "YZ" || name === "XZ" || name === "XYZ") {
            obj.visible = false;
            obj.scale.setScalar(1e-10);
        }
    });
};

const PlacedDetailInstance = ({
    inst,
    selected,
    selectionActive = false,
    addingMode,
    cubeSize,
    snapEnabled,
    orthoEnabled = false,
    otherInstances = [],
    gizmoDraggingRef,
    onAddAt,
    onSelect,
    onTransformEnd,
    showHoles = true,
    showGrooves = true,
    showOtherMachining = true,
    /** Скрыть гизмо и подсветку (модалка текстуры/материала) */
    suppressSelectionVisual = false,
    interactionLocked = false,
    shellTransparent = false,
    snapApproach = null,
    onSnapApproachChange = null,
    onSelectedRightPointerDown = null,
    freeRotationEnabled = false,
    freeRotationAxis = "z",
    sceneDrawOrder = 0,
}) => {
    const panelRotationState = useMemo(
        () => resolvePanelRotationState(inst),
        [inst.axes, inst.referenceAxes, inst.localRotationAngles],
    );
    const poseGroupRef = useRef();
    const orientGroupRef = useRef();
    const transformControlsRef = useRef(null);
    const [object, setObject] = useState(null);
    const [orientObject, setOrientObject] = useState(null);
    const draggingRef = useRef(false);
    const dragSourceRef = useRef(null); // 'gizmo' | 'corner' | null
    const liveAxesRef = useRef(null);
    const liveLocalAnglesRef = useRef(null);
    const liveSnapPrevLocalRef = useRef(null);
    const orthoStartLocalRef = useRef(null);
    const orthoLockedAxisRef = useRef(null);
    const activeSnapNodeRef = useRef(null);
    const {controls: orbitControls, camera, gl} = useThree();
    const freeRotationDragRef = useRef(null);
    const frozenRotationAxisDirInPoseRef = useRef(null);

    const setPoseGroupRef = useCallback((node) => {
        poseGroupRef.current = node;
        setObject((prev) => (prev === node ? prev : node));
    }, []);

    const setOrientGroupRef = useCallback((node) => {
        orientGroupRef.current = node;
        setOrientObject((prev) => (prev === node ? prev : node));
    }, []);

    const applyInstanceOrientation = useCallback((axes, faceFlip) => {
        if (!orientGroupRef.current) return;
        applyAxesToObject(orientGroupRef.current, axes, faceFlip);
    }, []);

    useLayoutEffect(() => {
        if (draggingRef.current || !poseGroupRef.current) return;
        const world = cubeLocalToWorld(inst.localPosition, cubeSize);
        poseGroupRef.current.position.set(world.x, world.y, world.z);
        applyInstanceOrientation(inst.axes, inst.faceFlip);
    }, [inst.localPosition, inst.axes, inst.faceFlip, cubeSize, applyInstanceOrientation]);

    // В орто оставляем только стрелки X/Y/Z — плоскости XY/YZ/XZ и свободный XYZ дают диагонали.
    useFrame(() => {
        if (!orthoEnabled) return;
        hideMultiAxisTransformHandles(transformControlsRef.current);
    });

    const resolveOrthoAxis = useCallback((rawLocal) => {
        const start = orthoStartLocalRef.current;
        if (!start) return null;
        // Ось гизмо учитываем только если тянем стрелку, не угол.
        if (dragSourceRef.current === "gizmo") {
            const fromGizmo = orthoAxisFromTransformControls(transformControlsRef.current?.axis);
            if (fromGizmo) {
                orthoLockedAxisRef.current = fromGizmo;
                return fromGizmo;
            }
            if (orthoLockedAxisRef.current) {
                return orthoLockedAxisRef.current;
            }
        }
        // Углы / свободный жест: ось по текущему сдвигу (X, Y или Z), без залипания.
        return getDominantOrthoAxis(start, rawLocal);
    }, []);

    const applyOrthoToLocal = useCallback((rawLocal) => {
        const start = orthoStartLocalRef.current;
        if (!start) return rawLocal;
        return projectLocalToOrthoAxis(start, rawLocal, resolveOrthoAxis(rawLocal));
    }, [resolveOrthoAxis]);

    const commitTransform = useCallback(() => {
        const obj = poseGroupRef.current;
        if (!obj) return;
        const dragSource = dragSourceRef.current;
        const rotationOnly = dragSource === "freeRotationAxis"
            || (freeRotationEnabled && dragSource === "corner");
        const axes = liveAxesRef.current != null
            ? cloneAxes(liveAxesRef.current)
            : cloneAxes(inst.axes || IDENTITY_AXES);
        const referenceAxes = cloneAxes(inst.referenceAxes || axes);
        const localRotationAngles = enforceSingleAxisAngles(
            normalizeLocalRotationAngles(
                liveLocalAnglesRef.current
                ?? inst.localRotationAngles
                ?? extractLocalAnglesDeg(referenceAxes, axes),
            ),
            freeRotationAxis,
        );
        liveAxesRef.current = null;
        liveLocalAnglesRef.current = null;
        const faceFlip = Boolean(inst.faceFlip);
        const lockedLocal = orthoStartLocalRef.current
            ? {...orthoStartLocalRef.current}
            : {...(inst.localPosition || {x: 0, y: 0, z: 0})};
        let localPosition = rotationOnly
            ? lockedLocal
            : worldPointToCubeLocal(obj.position, cubeSize);
        if (!rotationOnly && orthoEnabled && orthoStartLocalRef.current) {
            localPosition = applyOrthoToLocal(localPosition);
        }
        const raw = {
            localPosition,
            axes,
            faceFlip,
            euler: axesToEulerDegrees(axes),
        };
        const snapped = rotationOnly
            ? {...raw, localPosition: lockedLocal}
            : resolveDragPose({
                ...raw,
                inst,
                others: otherInstances,
                snapEnabled,
                cubeSize,
                activeNodeLocal: activeSnapNodeRef.current,
            });
        if (!rotationOnly && orthoEnabled && orthoStartLocalRef.current) {
            const axis = resolveOrthoAxis(localPosition);
            snapped.localPosition = projectLocalToOrthoAxis(
                orthoStartLocalRef.current,
                snapped.localPosition,
                axis,
            );
        }
        const world = cubeLocalToWorld(snapped.localPosition, cubeSize);
        obj.position.set(world.x, world.y, world.z);
        applyInstanceOrientation(snapped.axes, faceFlip);
        onTransformEnd?.(inst.instanceId, {
            localPosition: snapped.localPosition,
            axes: snapped.axes,
            faceFlip,
            euler: snapped.euler || raw.euler,
            referenceAxes,
            localRotationAngles,
        });
    }, [
        applyInstanceOrientation,
        applyOrthoToLocal,
        cubeSize,
        freeRotationAxis,
        freeRotationEnabled,
        inst,
        onTransformEnd,
        otherInstances,
        resolveOrthoAxis,
        snapEnabled,
        orthoEnabled,
    ]);

    const applyLiveSnap = useCallback((dragPayload) => {
        if (dragPayload?.axes) {
            liveAxesRef.current = cloneAxes(dragPayload.axes);
            if (dragPayload.localRotationAngles) {
                liveLocalAnglesRef.current = normalizeLocalRotationAngles(
                    dragPayload.localRotationAngles,
                );
            }
            applyInstanceOrientation(dragPayload.axes, Boolean(inst.faceFlip));
            if (orthoStartLocalRef.current && poseGroupRef.current) {
                const lockedWorld = cubeLocalToWorld(orthoStartLocalRef.current, cubeSize);
                poseGroupRef.current.position.set(lockedWorld.x, lockedWorld.y, lockedWorld.z);
            }
            return;
        }
        if (!poseGroupRef.current) return;
        const obj = poseGroupRef.current;
        let local = worldPointToCubeLocal(obj.position, cubeSize);
        if (orthoEnabled && orthoStartLocalRef.current) {
            const constrained = applyOrthoToLocal(local);
            if (
                constrained.x !== local.x
                || constrained.y !== local.y
                || constrained.z !== local.z
            ) {
                const world = cubeLocalToWorld(constrained, cubeSize);
                obj.position.set(world.x, world.y, world.z);
                local = constrained;
            }
        }
        if (snapEnabled) {
            const preview = getSnapApproachPreview({
                inst,
                localPosition: local,
                axes: cloneAxes(inst.axes || IDENTITY_AXES),
                others: otherInstances,
                cubeSize,
                activeNodeLocal: activeSnapNodeRef.current,
            });
            onSnapApproachChange?.((prev) => {
                if (!preview && !prev) return prev;
                return preview;
            });
        }
        if (!snapEnabled) return;
        let snapped = snapLocalToOtherDetails({
            inst,
            localPosition: local,
            axes: cloneAxes(inst.axes || IDENTITY_AXES),
            others: otherInstances,
            cubeSize,
            mode: "live",
            previousLocalPosition: liveSnapPrevLocalRef.current,
            activeNodeLocal: activeSnapNodeRef.current,
        });
        if (orthoEnabled && orthoStartLocalRef.current) {
            const axis = resolveOrthoAxis(local);
            snapped = projectLocalToOrthoAxis(orthoStartLocalRef.current, snapped, axis);
        }
        liveSnapPrevLocalRef.current = {...local};
        if (
            snapped.x === local.x
            && snapped.y === local.y
            && snapped.z === local.z
        ) {
            return;
        }
        const world = cubeLocalToWorld(snapped, cubeSize);
        obj.position.set(world.x, world.y, world.z);
        liveSnapPrevLocalRef.current = {...snapped};
    }, [
        applyOrthoToLocal,
        cubeSize,
        inst,
        otherInstances,
        snapEnabled,
        orthoEnabled,
        onSnapApproachChange,
        resolveOrthoAxis,
        applyInstanceOrientation,
    ]);

    const beginDrag = useCallback((source) => {
        const startLocal = poseGroupRef.current
            ? worldPointToCubeLocal(poseGroupRef.current.position, cubeSize)
            : null;
        draggingRef.current = true;
        dragSourceRef.current = source;
        liveSnapPrevLocalRef.current = startLocal;
        orthoStartLocalRef.current = startLocal ? {...startLocal} : null;
        orthoLockedAxisRef.current = source === "gizmo"
            ? orthoAxisFromTransformControls(transformControlsRef.current?.axis)
            : null;
        if (gizmoDraggingRef) gizmoDraggingRef.current = true;
        if (orbitControls) orbitControls.enabled = false;
    }, [cubeSize, gizmoDraggingRef, orbitControls]);

    const endDrag = useCallback(() => {
        draggingRef.current = false;
        liveSnapPrevLocalRef.current = null;
        onSnapApproachChange?.(null);
        if (orbitControls) orbitControls.enabled = true;
        commitTransform();
        activeSnapNodeRef.current = null;
        orthoStartLocalRef.current = null;
        orthoLockedAxisRef.current = null;
        dragSourceRef.current = null;
        window.setTimeout(() => {
            if (gizmoDraggingRef) gizmoDraggingRef.current = false;
        }, 0);
    }, [commitTransform, gizmoDraggingRef, onSnapApproachChange, orbitControls]);

    const finishFreeRotationDrag = useCallback(() => {
        const drag = freeRotationDragRef.current;
        freeRotationDragRef.current = null;
        frozenRotationAxisDirInPoseRef.current = null;
        if (drag?.currentAxes) {
            liveAxesRef.current = cloneAxes(drag.currentAxes);
            liveLocalAnglesRef.current = normalizeLocalRotationAngles(drag.currentAngles);
        }
        endDrag();
    }, [endDrag]);

    const handleFreeRotationPointerDown = useCallback((event) => {
        if (!freeRotationEnabled || event.button !== 0) return;
        event.stopPropagation();
        const orientObj = orientGroupRef.current;
        const poseObj = poseGroupRef.current;
        if (!orientObj || !poseObj) return;

        const initialAxes = cloneAxes(liveAxesRef.current || inst.axes || IDENTITY_AXES);
        const referenceAxes = cloneAxes(panelRotationState.referenceAxes || initialAxes);
        const startAngles = enforceSingleAxisAngles(
            normalizeLocalRotationAngles(
                liveLocalAnglesRef.current
                ?? panelRotationState.localRotationAngles
                ?? extractLocalAnglesDeg(referenceAxes, initialAxes),
            ),
            freeRotationAxis,
        );

        frozenRotationAxisDirInPoseRef.current = getRotationAxisDirInPoseParent(
            referenceAxes,
            startAngles,
            freeRotationAxis,
            orientObj,
        );

        freeRotationDragRef.current = createFreeRotationDragState({
            rotationAxisKey: freeRotationAxis,
            initialAxes,
            referenceAxes,
            startAngles,
            faceFlip: Boolean(inst.faceFlip),
            orientObj,
            poseObj,
            hitWorld: event.point,
        });
        beginDrag("freeRotationAxis");
    }, [
        beginDrag,
        freeRotationAxis,
        freeRotationEnabled,
        inst.axes,
        inst.faceFlip,
        panelRotationState,
    ]);

    useEffect(() => {
        if (!freeRotationEnabled) {
            freeRotationDragRef.current = null;
            return undefined;
        }
        const onPointerMove = (event) => {
            const drag = freeRotationDragRef.current;
            if (!drag) return;
            const pivot = new THREE.Vector3();
            drag.poseObj.getWorldPosition(pivot);
            const hit = raycastScreenToCameraPlane(
                camera,
                gl.domElement,
                event.clientX,
                event.clientY,
                pivot,
            );
            if (!hit) return;
            const result = applyFreeRotationDragStep(drag, hit, camera);
            if (result) {
                applyLiveSnap(result);
            }
        };
        const onPointerUp = () => {
            if (!freeRotationDragRef.current) return;
            finishFreeRotationDrag();
        };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerUp);
        return () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
            window.removeEventListener("pointercancel", onPointerUp);
        };
    }, [applyLiveSnap, camera, finishFreeRotationDrag, freeRotationEnabled, gl.domElement]);

    const showSelectionChrome = selected && !suppressSelectionVisual;

    // Пока есть выбор — соседи без pointer-handlers, иначе луч «проваливается»
    // в деталь под стрелкой гизмо и переключает выделение.
    const pickable = Boolean(addingMode || selected || !selectionActive);

    useLayoutEffect(() => {
        const root = poseGroupRef.current;
        if (!root) return undefined;
        const touched = [];
        root.traverse((obj) => {
            // Оси/хелперы никогда не участвуют в пике — иначе перекрывают выделение.
            if (obj.userData?.module3dRole === "guiding-axis-pick") return;
            if (obj.userData?.module3dRole === "helper" || obj.parent?.userData?.module3dRole === "helper") {
                obj.raycast = () => null;
                return;
            }
            if (obj.userData?.module3dRole === "corner-handle") return;
            if (!obj.isMesh && !obj.isLine && !obj.isLineSegments) return;
            if (!pickable) {
                if (!obj.userData.__module3dRaycastSaved) {
                    obj.userData.__module3dRaycastSaved = obj.raycast;
                    obj.raycast = () => null;
                    touched.push(obj);
                }
            } else if (obj.userData.__module3dRaycastSaved) {
                obj.raycast = obj.userData.__module3dRaycastSaved;
                delete obj.userData.__module3dRaycastSaved;
            }
        });
        return () => {
            touched.forEach((obj) => {
                if (obj.userData.__module3dRaycastSaved) {
                    obj.raycast = obj.userData.__module3dRaycastSaved;
                    delete obj.userData.__module3dRaycastSaved;
                }
            });
        };
    }, [pickable, object, showHoles, showGrooves, showOtherMachining, inst.detailData]);

    return (
        <>
            <group
                ref={setPoseGroupRef}
                userData={{
                    module3dRole: "detail",
                    instanceId: inst.instanceId,
                    exportName: inst.detailData?.name || inst.detail?.name || inst.instanceId,
                }}
                onPointerDown={pickable ? (e) => {
                    e.stopPropagation();
                    if (e.button === 2 && selected && !addingMode) {
                        onSelectedRightPointerDown?.(inst.instanceId);
                    }
                    if (addingMode) {
                        onAddAt?.(e.point, e, "detail");
                    }
                } : undefined}
                onClick={pickable ? (e) => {
                    e.stopPropagation();
                    if (addingMode) return;
                    if (gizmoDraggingRef?.current) return;
                    onSelect?.(inst.instanceId);
                } : undefined}
            >
                {isHandleDetail(inst.detailData) ? (
                    <HandleMesh
                        modelBase64={inst.detailData?.modelBase64}
                        sizeMm={inst.detailData?.sizeMm ?? DEFAULT_HANDLE_SIZE_MM}
                        materialId={inst.handleMaterialId ?? DEFAULT_HANDLE_MATERIAL_ID}
                        materialProps={inst.handleMaterialProps ?? null}
                        selected={showSelectionChrome}
                    />
                ) : (
                    <group ref={setOrientGroupRef}>
                        <ModuleDetailMesh
                            detailData={inst.detailData}
                            textureUrl={inst.textureUrl ?? null}
                            textureScaleMm={inst.textureScaleMm ?? DEFAULT_TEXTURE_SCALE_MM}
                            textureRotationDeg={inst.textureRotationDeg ?? DEFAULT_TEXTURE_ROTATION_DEG}
                            selected={showSelectionChrome}
                            showHoles={showHoles}
                            showGrooves={showGrooves}
                            showOtherMachining={showOtherMachining}
                            shellTransparent={shellTransparent || freeRotationEnabled}
                            renderOrder={sceneDrawOrder}
                            onSelect={pickable && !freeRotationEnabled ? (e) => {
                                e.stopPropagation();
                                if (addingMode) {
                                    onAddAt?.(e.point, e, "detail");
                                    return;
                                }
                                if (gizmoDraggingRef?.current) return;
                                onSelect?.(inst.instanceId);
                            } : undefined}
                            onFreeRotationPointerDown={
                                showSelectionChrome && freeRotationEnabled && !interactionLocked
                                    ? handleFreeRotationPointerDown
                                    : undefined
                            }
                        />
                        {showSelectionChrome && !addingMode && !interactionLocked && !freeRotationEnabled && (
                            <DetailCornerHandles
                                inst={inst}
                                enabled
                                groupRef={poseGroupRef}
                                orientationGroupRef={orientGroupRef}
                                orthoEnabled={orthoEnabled}
                                faceFlip={inst.faceFlip}
                                onDragStart={(localPosition) => {
                                    activeSnapNodeRef.current = localPosition;
                                    beginDrag("corner");
                                }}
                                onDrag={applyLiveSnap}
                                onDragEnd={(localPosition) => {
                                    activeSnapNodeRef.current = localPosition;
                                    endDrag();
                                }}
                                shrinkScale={
                                    snapApproach?.sourceInstanceId === inst.instanceId
                                        ? snapApproach.scale
                                        : 1
                                }
                                shrinkLocal={
                                    snapApproach?.sourceInstanceId === inst.instanceId
                                        ? snapApproach.sourceLocal
                                        : null
                                }
                            />
                        )}
                    </group>
                )}
                {showSelectionChrome && !isHandleDetail(inst.detailData) && !freeRotationEnabled && (
                    <DetailPanelAxes
                        detailData={inst.detailData}
                        axes={inst.axes}
                        faceFlip={inst.faceFlip}
                        orientationSourceRef={orientGroupRef}
                        dimmed={interactionLocked}
                    />
                )}
                {showSelectionChrome && !isHandleDetail(inst.detailData) && freeRotationEnabled && (
                    <PanelRotationAxisIndicator
                        inst={inst}
                        orientationSourceRef={orientGroupRef}
                        referenceAxes={panelRotationState.referenceAxes}
                        localRotationAngles={panelRotationState.localRotationAngles}
                        liveLocalAnglesRef={liveLocalAnglesRef}
                        rotationAxis={freeRotationAxis}
                        frozenDirInPose={frozenRotationAxisDirInPoseRef}
                    />
                )}
            </group>
            {showSelectionChrome && object && !addingMode && !interactionLocked && !freeRotationEnabled && (
                <TransformControls
                    ref={transformControlsRef}
                    object={object}
                    mode="translate"
                    space="world"
                    size={0.85}
                    onMouseDown={(e) => {
                        e?.stopPropagation?.();
                        activeSnapNodeRef.current = [0, 0, 0];
                        beginDrag("gizmo");
                    }}
                    onObjectChange={applyLiveSnap}
                    onMouseUp={(e) => {
                        e?.stopPropagation?.();
                        endDrag();
                    }}
                />
            )}
        </>
    );
};

const Module3DScene = ({
    moduleSizeM,
    activeCameraView = CAMERA_VIEW.PERSPECTIVE,
    cameraViewRequest,
    placedDetails,
    placedEmbeddedModules = [],
    selectedInstanceId,
    addingMode,
    snapEnabled,
    orthoEnabled = false,
    showReferenceCube = true,
    showHoles = true,
    showGrooves = true,
    showOtherMachining = true,
    postPendingInstanceId = null,
    postPendingEmbeddedModuleKey = null,
    gizmoDraggingRef,
    sceneRef,
    onAddAt,
    onSelectInstance,
    onTransformEnd,
    suppressSelectionVisual = false,
    shellTransparent = false,
    isolatedDetailInstanceId = null,
    isolatedEmbeddedInstanceId = null,
    onSelectedRightPointerDown = null,
    freeRotationEnabled = false,
    freeRotationAxis = "z",
    freeRotationAxisEmphasis = false,
}) => {
    const extent = Math.max(moduleSizeM.width, moduleSizeM.height, moduleSizeM.depth);
    const useOrtho = isOrthographicView(activeCameraView);
    const rotateEnabled = !isPlanarOrthoView(activeCameraView) && !addingMode;
    const initialPose = useMemo(
        () => getCameraViewPose(moduleSizeM, CAMERA_VIEW.PERSPECTIVE),
        [moduleSizeM],
    );
    const {scene, size, set} = useThree();
    const controlsRef = useRef(null);
    const orthoCamRef = useRef(null);
    const perspCamRef = useRef(null);
    const appliedViewRef = useRef(CAMERA_VIEW.PERSPECTIVE);
    const lastViewTokenRef = useRef(null);
    const viewReadyRef = useRef(false);
    const prevUseOrthoRef = useRef(useOrtho);
    const [snapApproach, setSnapApproach] = useState(null);

    const snapTargetInstances = useMemo(() => {
        const visibleDetails = placedDetails.filter((inst) => (
            !inst.hidden
            && (!isolatedDetailInstanceId || inst.instanceId === isolatedDetailInstanceId)
        ));
        const visibleEmbedded = placedEmbeddedModules.filter((inst) => (
            !inst.hidden
            && (!isolatedEmbeddedInstanceId || inst.instanceId === isolatedEmbeddedInstanceId)
        ));
        return [...visibleDetails, ...visibleEmbedded];
    }, [placedDetails, placedEmbeddedModules, isolatedDetailInstanceId, isolatedEmbeddedInstanceId]);

    useEffect(() => {
        if (sceneRef) sceneRef.current = scene;
        return () => {
            if (sceneRef && sceneRef.current === scene) {
                sceneRef.current = null;
            }
        };
    }, [scene, sceneRef]);

    useLayoutEffect(() => {
        const aspect = size.width / Math.max(size.height, 1);
        const token = cameraViewRequest?.token ?? null;
        const cameraSwitched = prevUseOrthoRef.current !== useOrtho;
        prevUseOrthoRef.current = useOrtho;

        const view = cameraViewRequest?.view || activeCameraView || CAMERA_VIEW.PERSPECTIVE;
        const wantOrtho = isOrthographicView(view);
        const cam = wantOrtho ? orthoCamRef.current : perspCamRef.current;
        if (!cam) return;

        // Явно выбираем камеру: иначе R3F может оставить предыдущую (ortho) активной.
        set({camera: cam});
        if (controlsRef.current) {
            controlsRef.current.object = cam;
        }

        const isNewViewRequest =
            !viewReadyRef.current
            || cameraSwitched
            || (token != null && token !== lastViewTokenRef.current)
            || appliedViewRef.current !== view;

        if (isNewViewRequest) {
            const pose = getCameraViewPose(moduleSizeM, view);
            if (cam.isOrthographicCamera) {
                applyOrthoCameraView(cam, controlsRef.current, pose, aspect);
            } else {
                applyPerspectiveCameraView(cam, controlsRef.current, pose);
            }
            appliedViewRef.current = view;
            lastViewTokenRef.current = token;
            viewReadyRef.current = true;
            return;
        }

        if (cam.isOrthographicCamera && isOrthographicView(appliedViewRef.current)) {
            const pose = getCameraViewPose(moduleSizeM, appliedViewRef.current);
            const halfH = pose.frustumHalf;
            const halfW = halfH * aspect;
            const zoom = cam.zoom;
            cam.left = -halfW;
            cam.right = halfW;
            cam.top = halfH;
            cam.bottom = -halfH;
            cam.zoom = zoom;
            cam.updateProjectionMatrix();
        }
    }, [activeCameraView, cameraViewRequest, moduleSizeM, set, size.height, size.width, useOrtho]);

    return (
        <>
            <OrthographicCamera
                ref={orthoCamRef}
                makeDefault={useOrtho}
                position={initialPose.position}
                near={0.01}
                far={Math.max(500, extent * 100)}
            />
            <PerspectiveCamera
                ref={perspCamRef}
                makeDefault={!useOrtho}
                position={initialPose.position}
                fov={45}
                near={0.05}
                far={Math.max(200, extent * 80)}
            />
            <OrbitControls
                ref={controlsRef}
                makeDefault
                target={initialPose.target}
                enableDamping
                dampingFactor={0.08}
                minZoom={0.25}
                maxZoom={12}
                minDistance={Math.max(0.2, extent * 0.15)}
                maxDistance={Math.max(40, extent * 40)}
                enableZoom
                enablePan
                enableRotate={rotateEnabled}
                screenSpacePanning
                mouseButtons={
                    rotateEnabled
                        ? {
                            LEFT: THREE.MOUSE.ROTATE,
                            MIDDLE: THREE.MOUSE.DOLLY,
                            RIGHT: THREE.MOUSE.PAN,
                        }
                        : {
                            LEFT: THREE.MOUSE.PAN,
                            MIDDLE: THREE.MOUSE.DOLLY,
                            RIGHT: THREE.MOUSE.PAN,
                        }
                }
                touches={
                    rotateEnabled
                        ? {
                            ONE: THREE.TOUCH.ROTATE,
                            TWO: THREE.TOUCH.DOLLY_PAN,
                        }
                        : {
                            ONE: THREE.TOUCH.PAN,
                            TWO: THREE.TOUCH.DOLLY_PAN,
                        }
                }
            />

            <ambientLight intensity={0.75}/>
            <directionalLight position={[5, 8, 4]} intensity={0.95}/>
            <directionalLight position={[-4, 2, -3]} intensity={0.35}/>

            {showReferenceCube && (
                <ReferenceCube
                    sizeM={moduleSizeM}
                    addingMode={addingMode}
                    ignorePointer={freeRotationEnabled}
                />
            )}
            <AnchorPlacementFaces sizeM={moduleSizeM} enabled={addingMode} onAddAt={onAddAt}/>
            <PlacementClickCatcher enabled={addingMode} onAddAt={onAddAt}/>

            {[...placedDetails]
                .filter((inst) => (
                    !inst.hidden
                    && (!isolatedDetailInstanceId || inst.instanceId === isolatedDetailInstanceId)
                ))
                .map((inst, index) => ({inst, index}))
                .sort((a, b) => compareDetailsBySceneDrawOrder(
                    a.inst.detailData ?? a.inst.detail,
                    b.inst.detailData ?? b.inst.detail,
                    a.index,
                    b.index,
                ))
                .map(({inst, index}) => (
                <PlacedDetailInstance
                    key={inst.instanceId}
                    inst={inst}
                    selected={inst.instanceId === selectedInstanceId}
                    selectionActive={Boolean(selectedInstanceId)}
                    addingMode={addingMode}
                    interactionLocked={postPendingInstanceId === inst.instanceId}
                    cubeSize={moduleSizeM}
                    snapEnabled={snapEnabled}
                    orthoEnabled={orthoEnabled}
                    showHoles={showHoles}
                    showGrooves={showGrooves}
                    showOtherMachining={showOtherMachining}
                    shellTransparent={shellTransparent}
                    suppressSelectionVisual={suppressSelectionVisual}
                    gizmoDraggingRef={gizmoDraggingRef}
                    otherInstances={snapTargetInstances.filter((o) => (
                        o.instanceId !== inst.instanceId
                    ))}
                    snapApproach={snapApproach}
                    onSnapApproachChange={setSnapApproach}
                    onAddAt={onAddAt}
                    onSelect={onSelectInstance}
                    onTransformEnd={onTransformEnd}
                    onSelectedRightPointerDown={onSelectedRightPointerDown}
                    freeRotationEnabled={
                        freeRotationEnabled && inst.instanceId === selectedInstanceId
                    }
                    freeRotationAxis={freeRotationAxis}
                    sceneDrawOrder={
                        inst.sceneDrawOrder
                        ?? getDetailSceneDrawOrder(inst.detailData ?? inst.detail, index)
                    }
                />
            ))}

            {placedEmbeddedModules
                .filter((inst) => (
                    !inst.hidden
                    && (!isolatedEmbeddedInstanceId || inst.instanceId === isolatedEmbeddedInstanceId)
                ))
                .map((inst) => (
                <PlacedEmbeddedModuleGroup
                    key={inst.instanceId}
                    inst={inst}
                    parentModuleSizeM={moduleSizeM}
                    selected={inst.instanceId === selectedInstanceId}
                    selectionActive={Boolean(selectedInstanceId)}
                    addingMode={addingMode}
                    snapEnabled={snapEnabled}
                    orthoEnabled={orthoEnabled}
                    otherInstances={snapTargetInstances.filter((o) => (
                        o.instanceId !== inst.instanceId
                    ))}
                    snapApproach={snapApproach}
                    onSnapApproachChange={setSnapApproach}
                    onSelect={onSelectInstance}
                    onTransformEnd={onTransformEnd}
                    gizmoDraggingRef={gizmoDraggingRef}
                    interactionLocked={postPendingEmbeddedModuleKey === inst.moduleKey}
                    suppressSelectionVisual={suppressSelectionVisual}
                    showHoles={showHoles}
                    showGrooves={showGrooves}
                    showOtherMachining={showOtherMachining}
                    shellTransparent={shellTransparent}
                    onSelectedRightPointerDown={onSelectedRightPointerDown}
                />
            ))}

            <CubeAxes
                sizeM={moduleSizeM}
                emphasizedAxis={freeRotationAxisEmphasis ? freeRotationAxis : null}
            />
        </>
    );
};

const SCENE_CONTEXT_MENU_SUBHEADER_SX = {
    lineHeight: 2.2,
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "text.secondary",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    py: 0,
    bgcolor: "transparent",
};

const Module3DView = ({
    moduleId,
    moduleName,
    details = [],
    detailsLoading = false,
    dependModules = [],
    onPacedDetailChange = null,
    onPacedDependModuleChange = null,
    onDetailsReload = null,
    moduleWidth,
    moduleHeight,
    moduleDepth,
    embeddedSceneRefreshKey = 0,
}) => {
    const navigate = useNavigate();
    const moduleSizeM = useMemo(
        () => moduleDimensionsMmToSizeM(moduleWidth, moduleHeight, moduleDepth),
        [moduleWidth, moduleHeight, moduleDepth],
    );
    const parentModuleDimsSig = useMemo(
        () => `${moduleWidth ?? ""}:${moduleHeight ?? ""}:${moduleDepth ?? ""}`,
        [moduleWidth, moduleHeight, moduleDepth],
    );
    const parentModuleDimsSigInitializedRef = useRef(false);
    const [placedDetails, setPlacedDetails] = useState([]);
    const [placedEmbeddedModules, setPlacedEmbeddedModules] = useState([]);
    /** detailKey → pacedModuleId после POST «+», пока by-module-id не обновился */
    const [pacedModuleIdByKey, setPacedModuleIdByKey] = useState({});
    /** moduleKey → pacedModule.id после POST встраиваемого модуля */
    const [pacedEmbeddedIdByKey, setPacedEmbeddedIdByKey] = useState({});
    /** После DELETE размещения не брать stale paced id со строки by-module-id */
    const [clearedPacedKeys, setClearedPacedKeys] = useState(() => new Set());
    const [clearedPacedEmbeddedKeys, setClearedPacedEmbeddedKeys] = useState(() => new Set());
    const [importedDetails, setImportedDetails] = useState([]); // [{detailKey, detail}]
    const [selectedInstanceId, setSelectedInstanceId] = useState(null);
    const [addingCandidate, setAddingCandidate] = useState(null); // {detail, detailKey}
    const [addingEmbeddedCandidate, setAddingEmbeddedCandidate] = useState(null); // {row, moduleKey, rowIndex}
    // ПОКА идёт POST при добавлении детали: блокируем перемещение и “−”/меню этой детали.
    const [postPendingInstanceId, setPostPendingInstanceId] = useState(null);
    const [postPendingDetailKey, setPostPendingDetailKey] = useState(null);
    const [postPendingEmbeddedModuleKey, setPostPendingEmbeddedModuleKey] = useState(null);
    const [isPlacing, setIsPlacing] = useState(false);
    const [snapEnabled, setSnapEnabled] = useState(true);
    const [orthoEnabled, setOrthoEnabled] = useState(false);
    const [freeRotationEnabled, setFreeRotationEnabled] = useState(false);
    const [freeRotationAxis, setFreeRotationAxis] = useState("z");
    const [freeRotationAngleDrafts, setFreeRotationAngleDrafts] = useState({
        x: "0",
        y: "0",
        z: "0",
    });
    const [sceneHistory, setSceneHistory] = useState({past: [], future: []});
    const [showReferenceCube, setShowReferenceCube] = useState(true);
    const [showShellTransparent, setShowShellTransparent] = useState(false);
    const [showHoles, setShowHoles] = useState(true);
    const [showGrooves, setShowGrooves] = useState(true);
    const [showOtherMachining, setShowOtherMachining] = useState(true);
    const [activeCameraView, setActiveCameraView] = useState(CAMERA_VIEW.PERSPECTIVE);
    const [cameraViewRequest, setCameraViewRequest] = useState(null);
    const [activeTextureId, setActiveTextureId] = useState(TEST_TEXTURES[0]?.id ?? null);
    const [activeTextureScaleMm, setActiveTextureScaleMm] = useState(DEFAULT_TEXTURE_SCALE_MM);
    const [activeTextureRotationDeg, setActiveTextureRotationDeg] = useState(DEFAULT_TEXTURE_ROTATION_DEG);
    const [textureCatalog, setTextureCatalog] = useState(() => createDefaultTextureCatalog());
    const [handleMaterialCatalog, setHandleMaterialCatalog] = useState(() =>
        createDefaultHandleMaterialCatalog(),
    );
    const [handleMaterialDialog, setHandleMaterialDialog] = useState(null);
    // null | { mode: 'create' } | { mode: 'edit', material }
    const [liveHandleMaterialPreview, setLiveHandleMaterialPreview] = useState(null);
    const [handleMaterialDeleteTarget, setHandleMaterialDeleteTarget] = useState(null);
    const [uploadSource, setUploadSource] = useState(null);
    const [liveTexturePreview, setLiveTexturePreview] = useState(null);
    const [textureDeleteTarget, setTextureDeleteTarget] = useState(null);
    const [storageReady, setStorageReady] = useState(false);
    const [embeddedSceneHydrating, setEmbeddedSceneHydrating] = useState(false);
    const [dxfImport, setDxfImport] = useState(null);
    // {fileName, detailData, meta} | {fileName, parseError}
    const [dxfEditTarget, setDxfEditTarget] = useState(null); // {detailKey, detail, fromImport}
    const [rowMenu, setRowMenu] = useState(null); // {anchorEl, detailKey, fromImport, detail}
    const [embeddedRowMenu, setEmbeddedRowMenu] = useState(null); // {anchorEl, moduleKey, row}
    const [addOrientationMenu, setAddOrientationMenu] = useState(null); // {anchorEl, row}
    const [sceneContextMenu, setSceneContextMenu] = useState(null); // {mouseX, mouseY, instanceId}
    const [isolatedDetailInstanceId, setIsolatedDetailInstanceId] = useState(null);
    const [isolatedEmbeddedInstanceId, setIsolatedEmbeddedInstanceId] = useState(null);
    const [detailOverrides, setDetailOverrides] = useState({}); // detailKey -> {name, position, height}
    const hydrateGenRef = useRef(0);
    const embeddedHydrateGenRef = useRef(0);
    const hydratedModuleIdRef = useRef(null);
    const hydratedDetailsModuleIdRef = useRef(null);
    const gizmoDraggingRef = useRef(false);
    const addingCandidateRef = useRef(null);
    const addingEmbeddedCandidateRef = useRef(null);
    const isPlacingRef = useRef(false);
    const placedDetailsRef = useRef([]);
    const placedEmbeddedModulesRef = useRef([]);
    const detailsRef = useRef(details);
    const selectedInstanceIdRef = useRef(null);
    const pacedModuleIdByKeyRef = useRef({});
    const pacedEmbeddedIdByKeyRef = useRef({});
    const clearedPacedKeysRef = useRef(new Set());
    const clearedPacedEmbeddedKeysRef = useRef(new Set());
    const embeddedChildrenLoadRef = useRef(new Set());
    const requestEmbeddedModuleChildrenRef = useRef(null);
    const embeddedRefreshPendingRef = useRef(false);
    const historyApplyingRef = useRef(false);
    const dxfFileInputRef = useRef(null);
    const handleFileInputRef = useRef(null);
    const assemblyFileInputRef = useRef(null);
    const sceneRef = useRef(null);
    const sceneHostRef = useRef(null);
    const sceneHostPointerCleanupRef = useRef(null);
    const scenePointerRef = useRef({x: 0, y: 0, dragged: false});
    const rmbOnSelectedRef = useRef(null);
    const [exportingGlb, setExportingGlb] = useState(false);
    const [assemblyFileBusy, setAssemblyFileBusy] = useState(false);
    const [assemblyLoadProgress, setAssemblyLoadProgress] = useState("");
    const [pendingAssemblyFile, setPendingAssemblyFile] = useState(null);
    const [assemblyMenuAnchor, setAssemblyMenuAnchor] = useState(null);
    const [handleImport, setHandleImport] = useState(null);
    // {fileName, meta, modelBase64} | {fileName, parseError}
    const [handleEditTarget, setHandleEditTarget] = useState(null);
    const [importSourceOpen, setImportSourceOpen] = useState(false);

    const [alertData, setAlertData] = useState({open: false, type: "error", message: ""});

    const handleCloseAlert = () => {
        setAlertData((alert) => ({...alert, open: false}));
    };

    const handleModalAlert = (message, type = "error") => {
        setAlertData({
            type,
            open: true,
            message: message?.message
                || message?.response?.data?.message
                || message?.toString?.()
                || String(message),
        });
    };

    const syncPacedDetailChange = useCallback((detailKey, pacedDetailModule) => {
        onPacedDetailChange?.(detailKey, pacedDetailModule ?? null);
    }, [onPacedDetailChange]);

    const syncPacedDependModuleChange = useCallback((moduleKey, pacedModule) => {
        onPacedDependModuleChange?.(moduleKey, pacedModule ?? null);
    }, [onPacedDependModuleChange]);

    const reloadModuleDetails = useCallback(async () => {
        if (moduleId == null || moduleId === "") return null;
        try {
            const response = await DetailCompositionController.byModuleId(moduleId);
            const stamped = stampCompositionDetailList(response.data, detailsRef.current);
            onDetailsReload?.(stamped);
            return stamped;
        } catch (err) {
            console.warn("module-3d: reload details failed", err);
            handleModalAlert(err);
            return null;
        }
    }, [moduleId, onDetailsReload]);

    const syncPlacedDetailsHiddenFromList = useCallback((detailList) => {
        setPlacedDetails((prev) => prev.map((inst) => {
            if (inst.fromImport) return inst;
            const row = (detailList || []).find((detail, index) => (
                getDetailKey(detail, index) === inst.detailKey
            ));
            if (!row) return inst;
            const hidden = resolvePacedDtoHidden(row?.pacedDetailModule, inst.hidden);
            return {
                ...inst,
                hidden,
                pacedDetailModule: row?.pacedDetailModule ?? inst.pacedDetailModule,
                detail: row ?? inst.detail,
                detailData: row ?? inst.detailData,
            };
        }));
    }, []);

    const syncPlacedEmbeddedHiddenFromRows = useCallback((rows) => {
        setPlacedEmbeddedModules((prev) => prev.map((inst) => {
            const row = (rows || []).find((item, index) => (
                String(getDependModuleKey(item, index)) === String(inst.moduleKey)
            ));
            if (!row) return inst;
            const hidden = resolvePacedDtoHidden(row?.pacedModule, inst.hidden);
            return {
                ...inst,
                hidden,
                pacedModule: row?.pacedModule ?? inst.pacedModule,
                row: {...inst.row, pacedModule: row?.pacedModule ?? inst.row?.pacedModule},
            };
        }));
    }, []);

    const applyBulkDetailsHiddenState = useCallback((hidden, exceptDetailKey = null) => {
        (detailsRef.current || []).forEach((detail, index) => {
            const detailKey = getDetailKey(detail, index);
            if (clearedPacedKeysRef.current.has(detailKey)) return;
            if (getPacedDetailModuleId(detail) == null) return;
            const isExcept = exceptDetailKey != null && detailKey === exceptDetailKey;
            const nextHidden = hidden ? !isExcept : false;
            const prevPaced = detail?.pacedDetailModule;
            const nextPaced = prevPaced && typeof prevPaced === "object"
                ? {...prevPaced, hide: nextHidden}
                : prevPaced;
            syncPacedDetailChange(detailKey, nextPaced ?? null);
        });

        setPlacedDetails((prev) => {
            const next = prev.map((inst) => {
                if (inst.fromImport) return inst;
                const isExcept = exceptDetailKey != null && inst.detailKey === exceptDetailKey;
                const nextHidden = hidden ? !isExcept : false;
                const prevPaced = inst.pacedDetailModule
                    ?? inst.detail?.pacedDetailModule
                    ?? inst.detailData?.pacedDetailModule;
                const nextPaced = prevPaced && typeof prevPaced === "object"
                    ? {...prevPaced, hide: nextHidden}
                    : prevPaced;
                return {
                    ...inst,
                    hidden: nextHidden,
                    pacedDetailModule: nextPaced,
                    detail: inst.detail ? {...inst.detail, pacedDetailModule: nextPaced} : inst.detail,
                    detailData: inst.detailData
                        ? {...inst.detailData, pacedDetailModule: nextPaced}
                        : inst.detailData,
                };
            });
            placedDetailsRef.current = next;
            return next;
        });
    }, [syncPacedDetailChange]);

    const applyBulkEmbeddedHiddenState = useCallback((hidden, exceptModuleKey = null) => {
        const nextEmbedded = placedEmbeddedModulesRef.current.map((inst) => {
            const isExcept = exceptModuleKey != null
                && String(inst.moduleKey) === String(exceptModuleKey);
            const nextHidden = hidden ? !isExcept : false;
            const nextPaced = inst.pacedModule
                ? {...inst.pacedModule, hide: nextHidden}
                : inst.pacedModule;
            return {
                ...inst,
                hidden: nextHidden,
                pacedModule: nextPaced,
                row: {...inst.row, pacedModule: nextPaced},
            };
        });
        placedEmbeddedModulesRef.current = nextEmbedded;
        setPlacedEmbeddedModules(nextEmbedded);
        nextEmbedded.forEach((inst) => {
            syncPacedDependModuleChange(inst.moduleKey, inst.pacedModule ?? null);
        });
    }, [syncPacedDependModuleChange]);

    const resolveDetailPacedId = useCallback((detailKey, detail) => {
        if (detailKey == null || clearedPacedKeys.has(detailKey)) return null;
        const inst = placedDetailsRef.current.find((item) => item.detailKey === detailKey);
        return pacedModuleIdByKey[detailKey]
            ?? getPacedDetailModuleId(detail)
            ?? getPacedDetailModuleId(inst?.detail)
            ?? getPacedDetailModuleId(inst?.detailData)
            ?? null;
    }, [pacedModuleIdByKey, clearedPacedKeys]);

    const resolveEmbeddedPacedId = useCallback((moduleKey, row) => {
        if (moduleKey == null || clearedPacedEmbeddedKeys.has(String(moduleKey))) return null;
        return pacedEmbeddedIdByKey[moduleKey]
            ?? getPacedModuleId(row)
            ?? null;
    }, [pacedEmbeddedIdByKey, clearedPacedEmbeddedKeys]);

    const applyDetailHiddenState = useCallback((detailKey, hidden, pacedDto) => {
        setPlacedDetails((prev) => prev.map((inst) => (
            inst.detailKey === detailKey
                ? {
                    ...inst,
                    hidden,
                    pacedDetailModule: pacedDto ?? inst.pacedDetailModule,
                }
                : inst
        )));
        syncPacedDetailChange(detailKey, pacedDto ?? null);
    }, [syncPacedDetailChange]);

    const applyEmbeddedHiddenState = useCallback((moduleKey, hidden, pacedDto) => {
        setPlacedEmbeddedModules((prev) => prev.map((inst) => (
            String(inst.moduleKey) === String(moduleKey)
                ? {
                    ...inst,
                    hidden,
                    pacedModule: pacedDto ?? inst.pacedModule,
                    row: {...inst.row, pacedModule: pacedDto ?? inst.row?.pacedModule},
                }
                : inst
        )));
        syncPacedDependModuleChange(moduleKey, pacedDto ?? null);
    }, [syncPacedDependModuleChange]);

    const toggleDetailVisibility = useCallback(async (detailKey, detail, nextHidden) => {
        const pacedId = resolveDetailPacedId(detailKey, detail);
        const detailId = getPostDetailId(detail);
        if (pacedId == null || detailId == null) return false;
        const inst = placedDetailsRef.current.find((item) => item.detailKey === detailKey) ?? null;
        const existingDto = resolvePacedExistingDto(inst) ?? detail?.pacedDetailModule ?? null;
        const dto = await updatePacedDetailHidden({
            pacedDetailModuleId: pacedId,
            detailId,
            hide: nextHidden,
            existingDto,
        });
        if (!dto) {
            handleModalAlert("Не удалось изменить видимость детали.");
            return false;
        }
        applyDetailHiddenState(detailKey, nextHidden, dto);
        if (nextHidden) {
            setSelectedInstanceId((curr) => {
                const hiddenInst = placedDetailsRef.current.find((item) => item.detailKey === detailKey);
                return hiddenInst?.instanceId === curr ? null : curr;
            });
        }
        return true;
    }, [resolveDetailPacedId, applyDetailHiddenState, handleModalAlert]);

    const toggleEmbeddedVisibility = useCallback(async (moduleKey, row, nextHidden) => {
        const pacedId = resolveEmbeddedPacedId(moduleKey, row);
        if (pacedId == null) return false;
        const inst = placedEmbeddedModulesRef.current.find(
            (item) => String(item.moduleKey) === String(moduleKey),
        ) ?? null;
        const existingDto = resolveEmbeddedPacedExistingDto(inst) ?? row?.pacedModule ?? null;
        const dto = await updatePacedModuleHidden({
            pacedModuleId: pacedId,
            hide: nextHidden,
            existingDto,
            dependModuleId: getDependModuleId(row),
        });
        if (!dto) {
            handleModalAlert("Не удалось изменить видимость модуля.");
            return false;
        }
        applyEmbeddedHiddenState(moduleKey, nextHidden, dto);
        if (nextHidden) {
            setSelectedInstanceId((curr) => {
                const hiddenInst = placedEmbeddedModulesRef.current.find(
                    (item) => String(item.moduleKey) === String(moduleKey),
                );
                return hiddenInst?.instanceId === curr ? null : curr;
            });
        }
        return true;
    }, [resolveEmbeddedPacedId, applyEmbeddedHiddenState, handleModalAlert]);

    const setAllDetailsVisibility = useCallback(async (hidden, exceptDetailKey = null) => {
        if (moduleId == null || moduleId === "") return false;
        let ok = false;
        if (hidden && exceptDetailKey != null) {
            const exceptDetail = details.find((detail, index) => (
                getDetailKey(detail, index) === exceptDetailKey
            ));
            const exceptId = resolveDetailPacedId(exceptDetailKey, exceptDetail);
            ok = await hideAllPacedDetailsExcept(moduleId, exceptId);
        } else if (hidden) {
            ok = await hideAllPacedDetails(moduleId);
        } else {
            ok = await showAllPacedDetails(moduleId);
        }
        if (!ok) {
            handleModalAlert("Не удалось изменить видимость деталей.");
            return false;
        }
        setIsolatedDetailInstanceId(null);
        setSceneContextMenu(null);
        applyBulkDetailsHiddenState(hidden, exceptDetailKey);
        return true;
    }, [
        moduleId,
        details,
        resolveDetailPacedId,
        applyBulkDetailsHiddenState,
        handleModalAlert,
    ]);

    const setAllEmbeddedVisibility = useCallback(async (hidden, exceptModuleKey = null) => {
        if (moduleId == null || moduleId === "") return false;
        let ok = false;
        if (hidden && exceptModuleKey != null) {
            const inst = placedEmbeddedModulesRef.current.find(
                (item) => String(item.moduleKey) === String(exceptModuleKey),
            );
            const exceptId = resolveEmbeddedPacedId(exceptModuleKey, inst?.row);
            ok = await hideAllPacedEmbeddedExcept(moduleId, exceptId);
        } else if (hidden) {
            ok = await hideAllPacedEmbeddedModules(moduleId);
        } else {
            ok = await showAllPacedEmbeddedModules(moduleId);
        }
        if (!ok) {
            handleModalAlert("Не удалось изменить видимость встраиваемых модулей.");
            return false;
        }
        setIsolatedEmbeddedInstanceId(null);
        setSceneContextMenu(null);
        applyBulkEmbeddedHiddenState(hidden, exceptModuleKey);
        return true;
    }, [
        moduleId,
        resolveEmbeddedPacedId,
        handleModalAlert,
        applyBulkEmbeddedHiddenState,
    ]);

    // Восстановление 3D:
    // - обычные детали модуля поднимаем с бэка (details[].pacedDetailModule)
    // - из localStorage берём только импорты и локальные каталоги
    useEffect(() => {
        if (moduleId == null || moduleId === "") {
            hydratedModuleIdRef.current = null;
            hydratedDetailsModuleIdRef.current = null;
            setPlacedDetails([]);
            setPlacedEmbeddedModules([]);
            setImportedDetails([]);
            setDetailOverrides({});
            embeddedChildrenLoadRef.current = new Set();
            setPacedModuleIdByKey({});
            setPacedEmbeddedIdByKey({});
            setClearedPacedKeys(new Set());
            setClearedPacedEmbeddedKeys(new Set());
            setTextureCatalog(createDefaultTextureCatalog());
            setHandleMaterialCatalog(createDefaultHandleMaterialCatalog());
            setSceneHistory({past: [], future: []});
            setStorageReady(true);
            return undefined;
        }

        const moduleRefId = String(moduleId);
        if (hydratedModuleIdRef.current !== moduleRefId) {
            hydratedModuleIdRef.current = moduleRefId;
            hydratedDetailsModuleIdRef.current = null;
        }
        if (detailsLoading) {
            // ModuleCompositionItem монтирует 3D с detailsLoading=false до первого loadDetails —
            // иначе пустая гидратация «закрывает» модуль и details с бэка уже не подхватываются.
            hydratedDetailsModuleIdRef.current = null;
            return undefined;
        }
        if (hydratedDetailsModuleIdRef.current === moduleRefId) return undefined;

        const gen = ++hydrateGenRef.current;
        let cancelled = false;
        setStorageReady(false);
        setSelectedInstanceId(null);
        setPlacedDetails([]);
        setPlacedEmbeddedModules([]);
        setImportedDetails([]);
        setDetailOverrides({});
        setClearedPacedKeys(new Set());
        setClearedPacedEmbeddedKeys(new Set());
        setTextureCatalog(createDefaultTextureCatalog());
        setHandleMaterialCatalog(createDefaultHandleMaterialCatalog());
        setAddingCandidate(null);
        setAddingEmbeddedCandidate(null);
        setPacedModuleIdByKey({});
        setPacedEmbeddedIdByKey({});

        (async () => {
            const saved = loadModule3DState(moduleId);
            const catalog = hydrateTextureCatalog(
                saved?.textureCatalog,
                saved?.customTextures,
            );
            const handleCatalog = hydrateHandleMaterialCatalog(saved?.handleMaterialCatalog);

            const detailRows = sortDetailsBySequenceNumber(
                Array.isArray(details) ? details : [],
            );
            const resolvedDetails = await Promise.all(
                detailRows.map((detail) => resolveModuleDetailWithPlacement(detail)),
            );
            const serverPlaced = sortDetailsBySequenceNumber(resolvedDetails)
                .map((detail, index) => {
                    const inst = buildPlacedInstanceFromModuleDetail(detail, index);
                    if (!inst) {
                        return null;
                    }
                    return {
                        ...inst,
                        sceneDrawOrder: getDetailSceneDrawOrder(detail, index),
                    };
                })
                .filter(Boolean);

            const savedImportedPlaced = (saved?.placedDetails || []).filter((inst) => (
                inst?.detailData?.source === "dxf"
                || inst?.detailData?.source === "3ds"
                || inst?.detailData?.kind === "handle"
                || inst?.detail?.source === "dxf"
                || inst?.detail?.source === "3ds"
                || inst?.detail?.kind === "handle"
            ));

            let restoredImportedPlaced = [];
            if (savedImportedPlaced.length) {
                restoredImportedPlaced = await hydratePlacedDetails(
                    savedImportedPlaced,
                    details,
                    resolveDetailData,
                    getDetailKey,
                    catalog,
                    handleCatalog,
                );
            }
            if (cancelled || gen !== hydrateGenRef.current) return;
            hydratedDetailsModuleIdRef.current = moduleRefId;
            setTextureCatalog(catalog);
            setHandleMaterialCatalog(handleCatalog);
            setPlacedDetails([...serverPlaced, ...restoredImportedPlaced]);

            setPacedModuleIdByKey(() => {
                const ids = {};
                for (const inst of serverPlaced) {
                    if (inst?.fromImport || inst?.detailKey == null) continue;
                    const id = getPacedDetailModuleId(inst)
                        ?? getPacedDetailModuleId(inst.detail)
                        ?? getPacedDetailModuleId(inst.detailData);
                    if (id != null) ids[inst.detailKey] = id;
                }
                return ids;
            });
            setImportedDetails(hydrateImportedDetails(saved?.importedDetails));
            if (saved?.activeTextureId != null) {
                const stillThere = catalog.some((t) => t.id === saved.activeTextureId);
                setActiveTextureId(stillThere ? saved.activeTextureId : (catalog[0]?.id ?? null));
            } else {
                setActiveTextureId(catalog[0]?.id ?? null);
            }
            setStorageReady(true);
            setSceneHistory(loadModule3DHistory(moduleId));
        })();

        return () => {
            cancelled = true;
        };
    }, [moduleId, details, detailsLoading]);

    const invalidatePlacedEmbeddedCaches = useCallback(() => {
        embeddedRefreshPendingRef.current = true;

        for (const inst of placedEmbeddedModulesRef.current) {
            const dependModuleId = inst?.dependModuleId ?? getDependModuleId(inst?.row);
            if (dependModuleId != null && dependModuleId !== "") {
                invalidateEmbeddedModuleDetailsCache(dependModuleId);
                invalidateEmbeddedModulePlacementCache(dependModuleId);
            }
            if (inst?.moduleKey != null) {
                embeddedChildrenLoadRef.current.delete(String(inst.moduleKey));
            }
        }
    }, []);

    // После смены габаритов родителя — сброс кэша и повторная гидрация встраиваемых.
    useEffect(() => {
        if (!embeddedSceneRefreshKey) return;
        invalidatePlacedEmbeddedCaches();
    }, [embeddedSceneRefreshKey, invalidatePlacedEmbeddedCaches]);

    useEffect(() => {
        if (!parentModuleDimsSigInitializedRef.current) {
            parentModuleDimsSigInitializedRef.current = true;
            return;
        }
        invalidatePlacedEmbeddedCaches();
    }, [parentModuleDimsSig, invalidatePlacedEmbeddedCaches]);

    const applyEmbeddedChildrenLoad = useCallback((
        moduleKey,
        {children = [], detailsCount = 0, loaded = false, notFound = false},
    ) => {
        const key = String(moduleKey);
        const childrenHydrated = Boolean(loaded || notFound);
        setPlacedEmbeddedModules((prev) => {
            const next = prev.map((item) => (
                String(item.moduleKey) === key
                    ? {
                        ...item,
                        children,
                        childrenHydrated,
                        embeddedDetailsCount: detailsCount,
                        embeddedDetailsLoaded: childrenHydrated,
                    }
                    : item
            ));
            placedEmbeddedModulesRef.current = next;
            return next;
        });
    }, []);

    const requestEmbeddedModuleChildren = useCallback((
        moduleKey,
        dependModuleId,
        {bypassCache = false} = {},
    ) => {
        if (moduleKey == null || dependModuleId == null || dependModuleId === "") return;
        const key = String(moduleKey);
        const existing = placedEmbeddedModulesRef.current.find(
            (item) => String(item.moduleKey) === key,
        );
        if (existing?.childrenHydrated && !bypassCache) return;
        if (embeddedChildrenLoadRef.current.has(key)) return;
        embeddedChildrenLoadRef.current.add(key);

        void loadEmbeddedModuleChildren(dependModuleId, {bypassCache}).then((result) => {
            embeddedChildrenLoadRef.current.delete(key);
            applyEmbeddedChildrenLoad(moduleKey, result);
        }).catch((err) => {
            console.warn("module-3d: embedded module children load failed", err);
            embeddedChildrenLoadRef.current.delete(key);
            applyEmbeddedChildrenLoad(moduleKey, {
                children: [],
                detailsCount: 0,
                loaded: false,
                notFound: true,
            });
        });
    }, [applyEmbeddedChildrenLoad]);

    requestEmbeddedModuleChildrenRef.current = requestEmbeddedModuleChildren;

    // Встраиваемые модули — отдельно от гидрации деталей, чтобы не отменялось при смене details.
    useEffect(() => {
        if (!storageReady || moduleId == null || moduleId === "" || detailsLoading) {
            return undefined;
        }

        const forceRemote = embeddedRefreshPendingRef.current;
        if (forceRemote) {
            embeddedRefreshPendingRef.current = false;
        }

        const gen = ++embeddedHydrateGenRef.current;
        let cancelled = false;
        const sourceRows = Array.isArray(dependModules) ? dependModules : [];
        const tasks = sourceRows.map((row, index) => ({
            row: normalizeDependModuleRow(row),
            index,
        }));

        if (!tasks.length) {
            setPlacedEmbeddedModules([]);
            setPacedEmbeddedIdByKey({});
            setEmbeddedSceneHydrating(false);
            return undefined;
        }

        setEmbeddedSceneHydrating(true);

        const results = tasks.map(({row, index}) => {
            const moduleKey = getDependModuleKey(row, index);
            const key = String(moduleKey);
            if (clearedPacedEmbeddedKeysRef.current.has(key)) {
                return {
                    moduleKey,
                    inst: null,
                    rowWithPlacement: {...row, pacedModule: null},
                    sourceRow: row,
                };
            }
            try {
                const {inst, rowWithPlacement} = buildPlacedEmbeddedInstanceIfPlaced(row, index);
                return {moduleKey, inst, rowWithPlacement, sourceRow: row, forceRemote};
            } catch (err) {
                console.warn("module-3d: hydrate embedded module failed", err);
                return {moduleKey, inst: null, rowWithPlacement: row, sourceRow: row, forceRemote};
            }
        });

        void Promise.resolve().then(() => {
            if (cancelled || gen !== embeddedHydrateGenRef.current) return;

            const embeddedPlaced = [];
            const embeddedIds = {};
            for (const {moduleKey, inst, rowWithPlacement, sourceRow, forceRemote: rowForceRemote} of results) {
                const key = String(moduleKey);
                if (clearedPacedEmbeddedKeysRef.current.has(key)) {
                    continue;
                }
                const prevInst = placedEmbeddedModulesRef.current.find(
                    (item) => String(item.moduleKey) === key,
                );
                if (!inst) {
                    if (prevInst) {
                        embeddedPlaced.push(prevInst);
                        if (prevInst.pacedModuleId != null) {
                            embeddedIds[moduleKey] = prevInst.pacedModuleId;
                        }
                    }
                    continue;
                }
                if (inst) {
                    if (prevInst && !rowForceRemote) {
                        const mergedPacedModule = inst.pacedModule
                            ?? rowWithPlacement?.pacedModule
                            ?? prevInst.pacedModule;
                        embeddedPlaced.push({
                            ...inst,
                            instanceId: prevInst.instanceId,
                            localPosition: prevInst.localPosition,
                            axes: prevInst.axes,
                            faceFlip: prevInst.faceFlip,
                            euler: prevInst.euler,
                            dependModuleId: getDependModuleId(rowWithPlacement ?? prevInst.row)
                                ?? prevInst.dependModuleId,
                            children: prevInst.children,
                            childrenHydrated: prevInst.childrenHydrated,
                            embeddedDetailsCount: prevInst.embeddedDetailsCount,
                            embeddedDetailsLoaded: prevInst.embeddedDetailsLoaded,
                            hidden: resolvePacedDtoHidden(mergedPacedModule, prevInst.hidden),
                            pacedModuleId: prevInst.pacedModuleId ?? inst.pacedModuleId,
                            pacedModule: mergedPacedModule,
                            row: rowWithPlacement ?? prevInst.row,
                        });
                    } else {
                        embeddedPlaced.push(
                            rowForceRemote && prevInst
                                ? {
                                    ...inst,
                                    instanceId: prevInst.instanceId,
                                    localPosition: prevInst.localPosition,
                                    axes: prevInst.axes,
                                    faceFlip: prevInst.faceFlip,
                                    euler: prevInst.euler,
                                    pacedModuleId: prevInst.pacedModuleId ?? inst.pacedModuleId,
                                    hidden: resolvePacedDtoHidden(
                                        inst.pacedModule ?? prevInst.pacedModule,
                                        prevInst.hidden,
                                    ),
                                }
                                : inst,
                        );
                        embeddedChildrenLoadRef.current.delete(key);
                    }
                    if (inst.pacedModuleId != null) {
                        embeddedIds[moduleKey] = inst.pacedModuleId;
                    }
                }
                if (
                    rowWithPlacement?.pacedModule
                    && !clearedPacedEmbeddedKeysRef.current.has(key)
                    && (rowForceRemote || !isEmbeddedModulePlacedDto(sourceRow))
                ) {
                    syncPacedDependModuleChange(moduleKey, rowWithPlacement.pacedModule);
                }
            }
            const placedKeys = new Set(embeddedPlaced.map((item) => String(item.moduleKey)));
            for (const prevInst of placedEmbeddedModulesRef.current) {
                const key = String(prevInst.moduleKey);
                if (placedKeys.has(key) || clearedPacedEmbeddedKeysRef.current.has(key)) {
                    continue;
                }
                embeddedPlaced.push(prevInst);
                placedKeys.add(key);
                if (prevInst.pacedModuleId != null) {
                    embeddedIds[prevInst.moduleKey] = prevInst.pacedModuleId;
                }
            }
            setPlacedEmbeddedModules(embeddedPlaced);
            placedEmbeddedModulesRef.current = embeddedPlaced;
            setPacedEmbeddedIdByKey((prev) => ({...prev, ...embeddedIds}));
            for (const inst of embeddedPlaced) {
                if (!inst?.localPosition || inst?.hidden) continue;
                const dependModuleId = inst.dependModuleId ?? getDependModuleId(inst.row);
                if (dependModuleId == null || dependModuleId === "") continue;
                if (inst.childrenHydrated && !forceRemote) continue;
                requestEmbeddedModuleChildrenRef.current?.(inst.moduleKey, dependModuleId, {
                    bypassCache: forceRemote,
                });
            }
        }).finally(() => {
            if (cancelled || gen !== embeddedHydrateGenRef.current) return;
            setEmbeddedSceneHydrating(false);
        });

        return () => {
            cancelled = true;
            setEmbeddedSceneHydrating(false);
        };
    }, [
        storageReady,
        moduleId,
        detailsLoading,
        dependModules,
        syncPacedDependModuleChange,
        embeddedSceneRefreshKey,
        parentModuleDimsSig,
    ]);

    const sceneHydrating = detailsLoading || !storageReady;

    useEffect(() => {
        placedEmbeddedModulesRef.current = placedEmbeddedModules;
    }, [placedEmbeddedModules]);

    useEffect(() => {
        pacedEmbeddedIdByKeyRef.current = pacedEmbeddedIdByKey;
    }, [pacedEmbeddedIdByKey]);

    useEffect(() => {
        clearedPacedEmbeddedKeysRef.current = clearedPacedEmbeddedKeys;
    }, [clearedPacedEmbeddedKeys]);

    // Автосохранение после гидрации.
    useEffect(() => {
        if (!storageReady || moduleId == null || moduleId === "") return;
        saveModule3DState(moduleId, {
            placedDetails: placedDetails.filter((inst) => isImportedInstance(inst)),
            activeTextureId,
            textureCatalog,
            handleMaterialCatalog,
            importedDetails,
        });
    }, [storageReady, moduleId, placedDetails, activeTextureId, textureCatalog, handleMaterialCatalog, importedDetails]);

    const canPersistAssembly = useMemo(
        () => Object.keys(placementsFromPlacedDetails(placedDetails)).length > 0,
        [placedDetails],
    );

    const assemblyMenuBusy = exportingGlb || assemblyFileBusy;
    const addingMode = Boolean(addingCandidate || addingEmbeddedCandidate);
    addingCandidateRef.current = addingCandidate;
    addingEmbeddedCandidateRef.current = addingEmbeddedCandidate;
    detailsRef.current = details;
    placedDetailsRef.current = placedDetails;
    selectedInstanceIdRef.current = selectedInstanceId;
    pacedModuleIdByKeyRef.current = pacedModuleIdByKey;
    clearedPacedKeysRef.current = clearedPacedKeys;
    clearedPacedEmbeddedKeysRef.current = clearedPacedEmbeddedKeys;

    const takeSceneSnapshot = useCallback(() => createHistorySnapshot({
        placedDetails: placedDetailsRef.current,
        selectedInstanceId: selectedInstanceIdRef.current,
        pacedModuleIdByKey: pacedModuleIdByKeyRef.current,
        clearedPacedKeys: clearedPacedKeysRef.current,
    }), []);

    /** Перед пользовательским изменением сцены — снимок для «Назад». */
    const recordSceneHistory = useCallback(() => {
        if (historyApplyingRef.current || !storageReady) return;
        try {
            const snapshot = takeSceneSnapshot();
            setSceneHistory((prev) => {
                const next = pushHistorySnapshot(prev, snapshot);
                try {
                    saveModule3DHistory(moduleId, next);
                } catch {
                    // ignore storage errors
                }
                return next;
            });
        } catch (err) {
            // История не должна блокировать PUT позы на бэк.
            console.warn("module-3d: history snapshot failed", err);
        }
    }, [takeSceneSnapshot, storageReady, moduleId]);

    const clearSceneIsolation = useCallback(() => {
        setIsolatedDetailInstanceId(null);
        setIsolatedEmbeddedInstanceId(null);
        setSceneContextMenu(null);
    }, []);

    const revealAllModuleDetails = useCallback(() => {
        setIsolatedDetailInstanceId(null);
        setSceneContextMenu(null);
        void (async () => {
            const ok = await showAllPacedDetails(moduleId);
            if (!ok) {
                handleModalAlert("Не удалось показать все детали.");
                return;
            }
            applyBulkDetailsHiddenState(false);
        })();
    }, [
        moduleId,
        applyBulkDetailsHiddenState,
        handleModalAlert,
    ]);

    const revealAllEmbeddedModules = useCallback(() => {
        setIsolatedEmbeddedInstanceId(null);
        setSceneContextMenu(null);
        void (async () => {
            const ok = await showAllPacedEmbeddedModules(moduleId);
            if (!ok) {
                handleModalAlert("Не удалось показать все встраиваемые модули.");
                return;
            }
            applyBulkEmbeddedHiddenState(false);
        })();
    }, [moduleId, applyBulkEmbeddedHiddenState, handleModalAlert]);

    const markSelectedRightPointerDown = useCallback((instanceId) => {
        rmbOnSelectedRef.current = instanceId;
    }, []);

    const setSceneHostNode = useCallback((node) => {
        sceneHostPointerCleanupRef.current?.();
        sceneHostPointerCleanupRef.current = null;
        sceneHostRef.current = node;
        if (!node) return;
        const onDown = (event) => {
            scenePointerRef.current = {x: event.clientX, y: event.clientY, dragged: false};
            rmbOnSelectedRef.current = null;
        };
        const onMove = (event) => {
            const state = scenePointerRef.current;
            if (state.dragged) return;
            if (Math.hypot(event.clientX - state.x, event.clientY - state.y) > POINTER_CLICK_SLOP_PX) {
                state.dragged = true;
            }
        };
        node.addEventListener("pointerdown", onDown, true);
        window.addEventListener("pointermove", onMove);
        sceneHostPointerCleanupRef.current = () => {
            node.removeEventListener("pointerdown", onDown, true);
            window.removeEventListener("pointermove", onMove);
        };
    }, []);

    useEffect(() => {
        if (!addingMode) return;
        setIsolatedDetailInstanceId(null);
        setIsolatedEmbeddedInstanceId(null);
        setSceneContextMenu(null);
    }, [addingMode]);

    useEffect(() => {
        if (isolatedDetailInstanceId && !placedDetails.some(
            (inst) => inst.instanceId === isolatedDetailInstanceId,
        )) {
            setIsolatedDetailInstanceId(null);
        }
    }, [placedDetails, isolatedDetailInstanceId]);

    useEffect(() => {
        if (isolatedEmbeddedInstanceId && !placedEmbeddedModules.some(
            (inst) => inst.instanceId === isolatedEmbeddedInstanceId,
        )) {
            setIsolatedEmbeddedInstanceId(null);
        }
    }, [placedEmbeddedModules, isolatedEmbeddedInstanceId]);

    const isImportedInstance = useCallback((inst) => (
        Boolean(
            inst?.fromImport
            || inst?.detailData?.source === "dxf"
            || inst?.detailData?.source === "3ds"
            || inst?.detail?.source === "dxf"
            || inst?.detail?.source === "3ds"
            || String(inst?.detailKey || "").startsWith("dxf:")
            || String(inst?.detailKey || "").startsWith("3ds:"),
        )
    ), []);

    const closeAssemblyMenu = useCallback(() => {
        setAssemblyMenuAnchor(null);
    }, []);

    const openAssemblyMenu = useCallback((event) => {
        if (addingMode || isPlacing || assemblyMenuBusy) return;
        setAssemblyMenuAnchor(event.currentTarget);
    }, [addingMode, isPlacing, assemblyMenuBusy]);

    const saveAssemblyFile = useCallback(() => {
        closeAssemblyMenu();
        if (!canPersistAssembly || assemblyFileBusy) return;
        try {
            const assembly = buildModule3dAssemblyFile({
                placedDetails,
                moduleId,
                moduleName,
            });
            if (!Object.keys(assembly.placements || {}).length) {
                // eslint-disable-next-line no-alert
                window.alert("Нет деталей с позицией для сохранения сборки");
                return;
            }
            downloadModule3dAssemblyFile(assembly, {
                fileName: moduleName || `module-${moduleId || "3d"}`,
            });
        } catch (err) {
            // eslint-disable-next-line no-alert
            window.alert(err?.message || "Не удалось сохранить сборку");
        }
    }, [
        closeAssemblyMenu,
        canPersistAssembly,
        assemblyFileBusy,
        placedDetails,
        moduleId,
        moduleName,
    ]);

    const openAssemblyFilePicker = useCallback(() => {
        closeAssemblyMenu();
        if (assemblyMenuBusy || addingMode || isPlacing || detailsLoading) return;
        assemblyFileInputRef.current?.click();
    }, [closeAssemblyMenu, assemblyMenuBusy, addingMode, isPlacing, detailsLoading]);

    const applyAssemblyFile = useCallback(async (file) => {
        if (!file || assemblyFileBusy) return;

        setAssemblyFileBusy(true);
        setAssemblyLoadProgress("");
        const missing = [];
        const failed = [];
        let updated = 0;
        let created = 0;

        try {
            const assembly = await readModule3dAssemblyFile(file);
            const placements = assembly.placements || {};
            const positions = Object.keys(placements);
            if (!positions.length) {
                throw new Error("В файле нет положений деталей по позициям");
            }
            if (moduleId == null || moduleId === "") {
                throw new Error("Не выбран модуль");
            }

            const placedByPosition = new Map();
            for (const inst of placedDetailsRef.current) {
                if (isImportedInstance(inst)) continue;
                const position = getCompositionPosition(inst.detail)
                    || getCompositionPosition(inst.detailData);
                if (position && !placedByPosition.has(position)) {
                    placedByPosition.set(position, inst);
                }
            }

            const detailByPosition = new Map();
            (Array.isArray(details) ? details : []).forEach((detail, index) => {
                const row = stampCompositionDetailId(detail);
                const position = getCompositionPosition(row);
                if (!position || detailByPosition.has(position) || isHandleDetail(row)) return;
                detailByPosition.set(position, {
                    detail: row,
                    detailKey: getDetailKey(row, index),
                });
            });

            const pacedIds = {...pacedModuleIdByKey};
            for (const inst of placedDetailsRef.current) {
                if (inst?.detailKey == null) continue;
                const id = getPacedDetailModuleId(inst)
                    ?? getPacedDetailModuleId(inst.detail)
                    ?? getPacedDetailModuleId(inst.detailData);
                if (id != null) pacedIds[inst.detailKey] = id;
            }
            detailByPosition.forEach(({detail, detailKey}) => {
                if (detailKey == null || pacedIds[detailKey] != null) return;
                const id = getPacedDetailModuleId(detail);
                if (id != null) pacedIds[detailKey] = id;
            });

            const jobs = [];
            for (const position of positions) {
                const parsed = parseAssemblyPose(placements[position]);
                const row = detailByPosition.get(position);
                const inst = placedByPosition.get(position) || null;
                if (!parsed?.localPosition || !parsed?.axes || (!row && !inst)) {
                    missing.push(position);
                    continue;
                }
                jobs.push({
                    position,
                    parsed,
                    inst,
                    detail: inst?.detail || row.detail,
                    detailKey: inst?.detailKey || row.detailKey,
                });
            }

            if (!jobs.length) {
                throw new Error("В файле нет позиций, которые есть в этом модуле");
            }

            for (let index = 0; index < jobs.length; index += 1) {
                const job = jobs[index];
                setAssemblyLoadProgress(`${index + 1} / ${jobs.length}`);

                const tableRow = stampCompositionDetailId(job.detail);
                const detailId = getPostDetailId(tableRow);
                if (detailId == null) {
                    failed.push(job.position);
                    continue;
                }

                let currentInst = placedDetailsRef.current.find((row) => (
                    row.detailKey === job.detailKey
                    || getCompositionPosition(row.detail) === job.position
                    || getCompositionPosition(row.detailData) === job.position
                )) || job.inst;
                const previousInst = currentInst || null;
                let createdThis = false;

                if (!currentInst) {
                    let detailData = tableRow;
                    try {
                        detailData = await resolveDetailData(tableRow);
                    } catch (err) {
                        console.warn("module-3d: assembly resolve detail failed", err);
                    }
                    if (!isReadyToPlace(detailData)) {
                        failed.push(job.position);
                        continue;
                    }
                    currentInst = withAssemblyPose({
                        instanceId: `assembly-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                        detail: tableRow,
                        detailData,
                        detailKey: job.detailKey,
                        textureId: null,
                        textureUrl: null,
                        textureScaleMm: DEFAULT_TEXTURE_SCALE_MM,
                        textureRotationDeg: DEFAULT_TEXTURE_ROTATION_DEG,
                        handleMaterialId: null,
                        pacedUrlId: null,
                        pacedDetailModuleId: null,
                        pacedDetailModule: null,
                        fromImport: false,
                        hidden: false,
                    }, job.parsed);
                    createdThis = true;
                    placedDetailsRef.current = [...placedDetailsRef.current, currentInst];
                    setPlacedDetails((prev) => (
                        prev.some((row) => row.instanceId === currentInst.instanceId)
                            ? prev
                            : [...prev, currentInst]
                    ));
                } else {
                    currentInst = withAssemblyPose(currentInst, job.parsed);
                    placedDetailsRef.current = placedDetailsRef.current.map((row) => (
                        row.instanceId === currentInst.instanceId ? currentInst : row
                    ));
                    setPlacedDetails((prev) => prev.map((row) => (
                        row.instanceId === currentInst.instanceId ? currentInst : row
                    )));
                }

                const pacedBodyId = clearedPacedKeys.has(job.detailKey)
                    ? null
                    : (
                        pacedIds[job.detailKey]
                        ?? getPacedDetailModuleId(previousInst)
                        ?? getPacedDetailModuleId(tableRow)
                        ?? null
                    );

                try {
                    const pacedDto = await persistPacedDetailPlacement({
                        moduleId,
                        detailId,
                        localPosition: job.parsed.localPosition,
                        axes: job.parsed.axes,
                        faceFlip: Boolean(job.parsed.faceFlip),
                        existingDto: resolvePacedExistingDto(previousInst)
                            ?? (
                                tableRow?.pacedDetailModule
                                && typeof tableRow.pacedDetailModule === "object"
                                    ? tableRow.pacedDetailModule
                                    : null
                            ),
                        pacedBodyId,
                    });
                    if (pacedDto == null) {
                        throw new Error("Empty paced-detail-modules response");
                    }
                    const createdId = pacedDto.id
                        ?? extractPacedRecordId(pacedDto, null, detailId)
                        ?? pacedBodyId;
                    if (createdThis) {
                        if (createdId == null) {
                            throw new Error("POST response has no paced-detail-modules id");
                        }
                        created += 1;
                    } else {
                        updated += 1;
                    }
                    if (createdId != null) {
                        pacedIds[job.detailKey] = createdId;
                        setPacedModuleIdByKey((prev) => ({
                            ...prev,
                            [job.detailKey]: createdId,
                        }));
                    }
                    setClearedPacedKeys((prev) => {
                        if (!prev.has(job.detailKey)) return prev;
                        const next = new Set(prev);
                        next.delete(job.detailKey);
                        return next;
                    });
                    const patched = {
                        ...currentInst,
                        hidden: getPacedDtoHide(pacedDto),
                        pacedUrlId: createdId ?? currentInst.pacedUrlId,
                        pacedDetailModuleId: createdId ?? currentInst.pacedDetailModuleId,
                        pacedDetailModule: pacedDto,
                    };
                    placedDetailsRef.current = placedDetailsRef.current.map((row) => (
                        row.instanceId === currentInst.instanceId ? patched : row
                    ));
                    setPlacedDetails((prev) => prev.map((row) => (
                        row.instanceId === currentInst.instanceId ? patched : row
                    )));
                    syncPacedDetailChange(job.detailKey, pacedDto);
                    window.dispatchEvent(new CustomEvent("paced-detail-module-updated", {
                        detail: {detailId},
                    }));
                } catch (err) {
                    console.warn("module-3d: assembly placement persist failed", err);
                    failed.push(job.position);
                    if (createdThis) {
                        placedDetailsRef.current = placedDetailsRef.current.filter((row) => (
                            row.instanceId !== currentInst.instanceId
                        ));
                        setPlacedDetails((prev) => prev.filter((row) => (
                            row.instanceId !== currentInst.instanceId
                        )));
                    } else if (previousInst) {
                        placedDetailsRef.current = placedDetailsRef.current.map((row) => (
                            row.instanceId === previousInst.instanceId ? previousInst : row
                        ));
                        setPlacedDetails((prev) => prev.map((row) => (
                            row.instanceId === previousInst.instanceId ? previousInst : row
                        )));
                    }
                }
            }

            const total = updated + created;
            const extra = [];
            if (missing.length) extra.push(`не найдены: ${missing.join(", ")}`);
            if (failed.length) extra.push(`не сохранены: ${failed.join(", ")}`);
            const summary = total
                ? `Сборка загружена: обновлено ${updated}, добавлено ${created}.`
                : "Не удалось применить положения сборки.";
            handleModalAlert(
                extra.length ? `${summary} ${extra.join("; ")}` : summary,
                total && !failed.length ? "success" : "error",
            );
        } catch (err) {
            handleModalAlert(err?.message || "Не удалось загрузить сборку");
        } finally {
            setAssemblyFileBusy(false);
            setAssemblyLoadProgress("");
        }
    }, [
        assemblyFileBusy,
        moduleId,
        details,
        pacedModuleIdByKey,
        clearedPacedKeys,
        isImportedInstance,
        handleModalAlert,
        syncPacedDetailChange,
    ]);

    const onAssemblyFileChange = useCallback((event) => {
        const file = event.target.files?.[0] || null;
        event.target.value = "";
        if (!file || assemblyFileBusy) return;
        setPendingAssemblyFile(file);
    }, [assemblyFileBusy]);

    const closeAssemblyLoadConfirm = useCallback(() => {
        if (assemblyFileBusy) return;
        setPendingAssemblyFile(null);
    }, [assemblyFileBusy]);

    const confirmAssemblyLoad = useCallback(() => {
        const file = pendingAssemblyFile;
        setPendingAssemblyFile(null);
        if (!file) return;
        void applyAssemblyFile(file);
    }, [pendingAssemblyFile, applyAssemblyFile]);

    const exportGlb = useCallback(async () => {
        closeAssemblyMenu();
        if (exportingGlb || assemblyFileBusy || !placedDetails.length) return;
        setExportingGlb(true);
        try {
            const scene = sceneRef.current;
            if (!scene) {
                throw new Error("Сцена ещё не готова — подождите загрузки");
            }
            await exportModuleSceneToGlb(scene, {
                fileName: moduleName || `module-${moduleId || "3d"}`,
            });
        } catch (err) {
            // eslint-disable-next-line no-alert
            window.alert(err?.message || "Не удалось экспортировать GLB");
        } finally {
            setExportingGlb(false);
        }
    }, [
        closeAssemblyMenu,
        exportingGlb,
        assemblyFileBusy,
        placedDetails.length,
        moduleName,
        moduleId,
    ]);

    const selectedTextureId = useMemo(() => {
        const selected = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
        if (selected) {
            // Деталь без текстуры — ничего не подсвечивать в каталоге.
            return selected.textureId ?? null;
        }
        return activeTextureId;
    }, [placedDetails, selectedInstanceId, activeTextureId]);

    const selectedTextureScaleMm = useMemo(() => {
        const selected = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
        return selected?.textureScaleMm ?? activeTextureScaleMm;
    }, [placedDetails, selectedInstanceId, activeTextureScaleMm]);

    const selectedTextureRotationDeg = useMemo(() => {
        const selected = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
        return selected?.textureRotationDeg ?? activeTextureRotationDeg;
    }, [placedDetails, selectedInstanceId, activeTextureRotationDeg]);

    const suppressSelectionVisual = Boolean(uploadSource || handleMaterialDialog);

    const instructionText = useMemo(() => {
        if (isPlacing) {
            return addingEmbeddedCandidateRef.current
                ? "Загрузка встраиваемого модуля…"
                : "Загрузка геометрии детали…";
        }
        if (addingMode) {
            if (addingEmbeddedCandidate) {
                return snapEnabled
                    ? "Добавление модуля: клик по полу или стенке у осей."
                    : "Добавление модуля: клик по полу или стенке (без привязки).";
            }
            return snapEnabled
                ? "Добавление: клик по полу/стенке или панели. Панели стыкуются торцами; ручка ориентируется по поверхности и привязывается к ней."
                : "Добавление: клик по полу или стенке у осей (без привязки).";
        }
        if (isolatedDetailInstanceId || isolatedEmbeddedInstanceId) {
            return "Показан только выбранный объект. ПКМ — «Показать все» или клик по пустому месту.";
        }
        if (selectedInstanceId) {
            if (orthoEnabled) {
                return snapEnabled
                    ? "Орто: движение по X, Y или Z (без диагоналей). Привязка к контуру и торцам. Esc — снять выбор."
                    : "Орто: движение по X, Y или Z (без диагоналей). Esc — снять выбор.";
            }
            return snapEnabled
                ? "Двигайте стрелками; поворот — X/Y/Z. Esc — снять выбор. Привязка: контур модуля и торцы деталей. ПКМ по детали — скрыть остальное."
                : "Двигайте стрелками; поворот — X/Y/Z. Снять выбор: Esc / клик по пустому. ПКМ по детали — скрыть остальное.";
        }
        if (isPlanarOrthoView(activeCameraView)) {
            return "Плоский вид: ЛКМ/ПКМ — панорама, колёсико — зум. Виды — в плашке над сценой.";
        }
        return "ЛКМ — вращение, ПКМ — панорама, колёсико — зум. Виды — в плашке над сценой.";
    }, [addingMode, addingEmbeddedCandidate, activeCameraView, isPlacing, selectedInstanceId, snapEnabled, orthoEnabled, isolatedDetailInstanceId, isolatedEmbeddedInstanceId]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.target?.closest?.("input, textarea, [contenteditable=true]")) return;
            if (event.key === "Escape") {
                setSelectedInstanceId(null);
                setAddingCandidate(null);
                addingCandidateRef.current = null;
                setAddingEmbeddedCandidate(null);
                addingEmbeddedCandidateRef.current = null;
                setIsolatedDetailInstanceId(null);
                setIsolatedEmbeddedInstanceId(null);
                setSceneContextMenu(null);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const placedCountByKey = useMemo(() => {
        const map = new Map();
        for (const inst of placedDetails) {
            const key = inst.detailKey;
            map.set(key, (map.get(key) ?? 0) + 1);
        }
        return map;
    }, [placedDetails]);

    const pacedIdByKey = useMemo(() => {
        const map = {};
        for (const inst of placedDetails) {
            if (inst?.fromImport || inst?.detailKey == null) continue;
            const id = getPacedDetailModuleId(inst)
                ?? getPacedDetailModuleId(inst.detail)
                ?? getPacedDetailModuleId(inst.detailData)
                ?? pacedModuleIdByKey[inst.detailKey]
                ?? null;
            if (id != null) map[inst.detailKey] = id;
        }
        return map;
    }, [placedDetails, pacedModuleIdByKey]);

    const selectedDetailKey = useMemo(() => {
        const selectedInstance = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
        return selectedInstance?.detailKey ?? null;
    }, [placedDetails, selectedInstanceId]);

    const selectedEmbeddedModuleKey = useMemo(() => {
        const selectedInstance = placedEmbeddedModules.find((inst) => inst.instanceId === selectedInstanceId);
        return selectedInstance?.moduleKey != null ? String(selectedInstance.moduleKey) : null;
    }, [placedEmbeddedModules, selectedInstanceId]);

    const selectPlacedDetailByKey = useCallback((detailKey) => {
        if (addingMode || isPlacing || detailKey == null) return;
        const inst = placedDetails.find((row) => row.detailKey === detailKey);
        if (!inst) return;
        setSelectedInstanceId(inst.instanceId);
    }, [addingMode, isPlacing, placedDetails]);

    const selectPlacedEmbeddedByKey = useCallback((moduleKey) => {
        if (addingMode || isPlacing || moduleKey == null) return;
        const inst = placedEmbeddedModules.find(
            (row) => String(row.moduleKey) === String(moduleKey),
        );
        if (!inst) return;
        setSelectedInstanceId(inst.instanceId);
    }, [addingMode, isPlacing, placedEmbeddedModules]);

    const MM_TO_M = 0.001;
    const metersToMm3d = (meters) => {
        const n = Number(meters);
        if (!Number.isFinite(n)) return 0;
        return Math.round((n / MM_TO_M) * 1000) / 1000;
    };

    const selectedInstance = useMemo(() => (
        placedDetails.find((inst) => inst.instanceId === selectedInstanceId) ?? null
    ), [placedDetails, selectedInstanceId]);

    const selectedEmbeddedInstance = useMemo(() => (
        placedEmbeddedModules.find((inst) => inst.instanceId === selectedInstanceId) ?? null
    ), [placedEmbeddedModules, selectedInstanceId]);

    const basePointMm = useMemo(() => {
        const placement = selectedInstance ?? selectedEmbeddedInstance;
        if (!placement?.localPosition) return null;
        return {
            x: metersToMm3d(placement.localPosition.x),
            y: metersToMm3d(placement.localPosition.y),
            z: metersToMm3d(placement.localPosition.z),
        };
    }, [selectedInstance, selectedEmbeddedInstance]);

    const materialMode = useMemo(() => {
        if (!selectedInstanceId) return "none";
        const selected = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
        if (!selected) return "none";
        return isHandleDetail(selected.detailData) ? "handle" : "panel";
    }, [placedDetails, selectedInstanceId]);

    const showDetailFreeRotation = useMemo(() => {
        if (!selectedInstanceId) return false;
        const selected = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
        if (!selected || isHandleDetail(selected.detailData)) return false;
        return !placedEmbeddedModules.some((inst) => inst.instanceId === selectedInstanceId);
    }, [placedDetails, placedEmbeddedModules, selectedInstanceId]);

    const selectedPanelRotationKey = useMemo(() => {
        if (!showDetailFreeRotation || !selectedInstanceId) return "";
        const inst = placedDetails.find((row) => row.instanceId === selectedInstanceId);
        if (!inst) return "";
        const {localRotationAngles} = resolvePanelRotationState(inst);
        return [
            selectedInstanceId,
            localRotationAngles.x,
            localRotationAngles.y,
            localRotationAngles.z,
        ].join(":");
    }, [placedDetails, selectedInstanceId, showDetailFreeRotation]);

    useEffect(() => {
        if (!showDetailFreeRotation || !selectedInstanceId) return;
        const inst = placedDetails.find((row) => row.instanceId === selectedInstanceId);
        if (!inst) return;
        const {localRotationAngles} = resolvePanelRotationState(inst);
        const lockedAxis = lockedPanelRotationAxisKey(localRotationAngles);
        if (lockedAxis) {
            setFreeRotationAxis(lockedAxis);
        }
    }, [selectedPanelRotationKey, showDetailFreeRotation, selectedInstanceId, placedDetails]);

    useEffect(() => {
        if (!freeRotationEnabled) {
            setFreeRotationAngleDrafts({x: "0", y: "0", z: "0"});
            return;
        }
        if (!selectedPanelRotationKey) {
            setFreeRotationAngleDrafts({x: "0", y: "0", z: "0"});
            return;
        }
        const inst = placedDetails.find((row) => row.instanceId === selectedInstanceId);
        if (!inst) return;
        const {localRotationAngles} = resolvePanelRotationState(inst);
        const formatDraft = (deg) => {
            const n = normalizeDeg(Number(deg) || 0);
            const rounded = Math.round(n * 1000) / 1000;
            return String(rounded);
        };
        setFreeRotationAngleDrafts({
            x: formatDraft(localRotationAngles.x),
            y: formatDraft(localRotationAngles.y),
            z: formatDraft(localRotationAngles.z),
        });
    }, [selectedPanelRotationKey, freeRotationEnabled, placedDetails, selectedInstanceId]);

    const freeRotationAppliedAngles = useMemo(() => {
        if (!selectedInstanceId) {
            return {x: 0, y: 0, z: 0};
        }
        const inst = placedDetails.find((row) => row.instanceId === selectedInstanceId);
        if (!inst) {
            return {x: 0, y: 0, z: 0};
        }
        const {localRotationAngles} = resolvePanelRotationState(inst);
        return normalizeLocalRotationAngles(localRotationAngles);
    }, [placedDetails, selectedInstanceId, selectedPanelRotationKey]);

    const selectedHandleMaterialId = useMemo(() => {
        if (materialMode !== "handle") return null;
        const selected = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
        return selected?.handleMaterialId ?? DEFAULT_HANDLE_MATERIAL_ID;
    }, [placedDetails, selectedInstanceId, materialMode]);

    const applyHandleMaterial = useCallback((material) => {
        if (!material?.id || !selectedInstanceId) return;
        const preset = getHandleMaterialById(material.id, handleMaterialCatalog);
        recordSceneHistory();
        setPlacedDetails((prev) =>
            prev.map((inst) => {
                if (inst.instanceId !== selectedInstanceId) return inst;
                if (!isHandleDetail(inst.detailData)) return inst;
                return {
                    ...inst,
                    handleMaterialId: preset.id,
                };
            }),
        );
    }, [selectedInstanceId, handleMaterialCatalog, recordSceneHistory]);

    const openCreateHandleMaterial = useCallback(() => {
        setHandleMaterialDialog({mode: "create"});
    }, []);

    const openEditHandleMaterial = useCallback((material) => {
        if (!material?.id) return;
        if (material.fromFile) {
            const selected = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
            if (!selected || !isHandleDetail(selected.detailData)) {
                // eslint-disable-next-line no-alert
                window.alert("Выберите ручку на сцене, чтобы править материал из её 3DS");
                return;
            }
            let preview = selected.detailData?.materialPreview;
            if (!preview?.color && selected.detailData?.modelBase64) {
                preview = getHandleMaterialPreviewFromBase64(selected.detailData.modelBase64);
            }
            const handleName = selected.detailData?.name || selected.detail?.name || "Ручка";
            setHandleMaterialDialog({
                mode: "create",
                material: {
                    name: `${handleName} (из файла)`,
                    color: preview?.color || "#B0B0B0",
                    metalness: preview?.metalness ?? 0.45,
                    roughness: preview?.roughness ?? 0.4,
                    mapUrl: preview?.mapUrl || null,
                },
            });
            return;
        }
        setHandleMaterialDialog({mode: "edit", material});
    }, [placedDetails, selectedInstanceId]);

    const fromFileMaterialPreview = useMemo(() => {
        const selected = placedDetails.find((inst) => inst.instanceId === selectedInstanceId);
        if (selected && isHandleDetail(selected.detailData)) {
            let preview = selected.detailData?.materialPreview;
            if (!preview?.color && selected.detailData?.modelBase64) {
                preview = getHandleMaterialPreviewFromBase64(selected.detailData.modelBase64);
            }
            if (preview?.color || preview?.mapUrl) return preview;
        }
        // Любая ручка «из файла» на сцене
        for (const inst of placedDetails) {
            if (!isHandleDetail(inst.detailData)) continue;
            if (!isHandleMaterialFromFile(inst.handleMaterialId)) continue;
            let preview = inst.detailData?.materialPreview;
            if (!preview?.color && inst.detailData?.modelBase64) {
                preview = getHandleMaterialPreviewFromBase64(inst.detailData.modelBase64);
            }
            if (preview?.color || preview?.mapUrl) return preview;
        }
        return null;
    }, [placedDetails, selectedInstanceId]);

    const confirmHandleMaterialDialog = useCallback((record, meta = {}) => {
        if (!record?.id) return;
        setHandleMaterialCatalog((prev) => {
            if (meta.isEdit) {
                return prev.map((m) => (
                    m.id === record.id
                        ? {
                            ...record,
                            builtin: record.builtin != null ? record.builtin : m.builtin,
                        }
                        : m
                ));
            }
            return [...prev, {...record, builtin: false}];
        });
        if (selectedInstanceId) {
            setPlacedDetails((prev) =>
                prev.map((inst) => {
                    if (inst.instanceId !== selectedInstanceId) return inst;
                    if (!isHandleDetail(inst.detailData)) return inst;
                    return {...inst, handleMaterialId: record.id};
                }),
            );
        }
        setHandleMaterialDialog(null);
        setLiveHandleMaterialPreview(null);
    }, [selectedInstanceId]);

    const handleLiveHandleMaterialPreview = useCallback((preview) => {
        setLiveHandleMaterialPreview(preview);
    }, []);

    const requestDeleteHandleMaterial = useCallback((material) => {
        if (!material?.id || material.fromFile) return;
        setHandleMaterialDeleteTarget(material);
    }, []);

    const confirmDeleteHandleMaterial = useCallback(() => {
        const material = handleMaterialDeleteTarget;
        if (!material?.id) return;
        const nextCatalog = handleMaterialCatalog.filter((m) => m.id !== material.id);
        const fallbackId = nextCatalog[0]?.id ?? DEFAULT_HANDLE_MATERIAL_ID;
        setHandleMaterialCatalog(nextCatalog);
        setPlacedDetails((placed) =>
            placed.map((inst) => {
                if (inst.handleMaterialId !== material.id) return inst;
                return {...inst, handleMaterialId: fallbackId};
            }),
        );
        setHandleMaterialDeleteTarget(null);
    }, [handleMaterialDeleteTarget, handleMaterialCatalog]);

    const commitAddCandidate = useCallback((candidate, point, event) => {
        if (!candidate || isPlacingRef.current) return;
        if (placedDetailsRef.current.some((inst) => inst.detailKey === candidate.detailKey)) {
            return;
        }

        addingCandidateRef.current = null;
        isPlacingRef.current = true;
        setAddingCandidate(null);
        setIsPlacing(true);

        const finish = async (detailData) => {
            try {
                let pacedPlacement = candidate.pacedPlacement;
                const pacedUrlId = clearedPacedKeys.has(candidate.detailKey)
                    ? null
                    : (
                        getPacedDetailModuleId(candidate.detail)
                        ?? pacedModuleIdByKey[candidate.detailKey]
                        ?? pacedIdByKey[candidate.detailKey]
                        ?? null
                    );
                if (pacedPlacement === undefined) {
                    pacedPlacement = parsePacedPlacementFromDetailRow(candidate.detail);
                }
                const needsPacedFetch = !candidate.fromImport
                    && !candidate.initialAxes
                    && pacedUrlId != null
                    && pacedPlacement == null;
                if (needsPacedFetch) {
                    pacedPlacement = await fetchPacedDetailPlacement(pacedUrlId);
                } else if (pacedPlacement === undefined) {
                    pacedPlacement = null;
                }

                let snappedPose;
                try {
                    snappedPose = resolvePlacementPose({
                        point,
                        event,
                        snapEnabled,
                        cubeSize: moduleSizeM,
                        others: [
                            ...placedDetailsRef.current,
                            ...placedEmbeddedModulesRef.current,
                        ],
                        detailData,
                        pacedPlacement,
                        initialAxes: candidate.initialAxes,
                    });
                } catch (err) {
                    console.warn("module-3d: placement snap failed", err);
                    snappedPose = resolvePlacementPose({
                        point,
                        event,
                        snapEnabled: false,
                        cubeSize: moduleSizeM,
                        others: [
                            ...placedDetailsRef.current,
                            ...placedEmbeddedModulesRef.current,
                        ],
                        detailData,
                        pacedPlacement,
                        initialAxes: candidate.initialAxes,
                    });
                }
                if (placedDetailsRef.current.some((inst) => inst.detailKey === candidate.detailKey)) {
                    return;
                }

                const instanceId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
                let pacedModuleId = pacedUrlId ?? null;
                const tableRow = stampCompositionDetailId(candidate.detail);
                const detailId = getPostDetailId(tableRow);
                const canCreatePaced = !candidate.fromImport
                    && moduleId != null
                    && moduleId !== ""
                    && detailId != null
                    && pacedModuleId == null
                    && !isHandleDetail(detailData)
                    && !isHandleDetail(candidate.detail);

                const newInst = {
                    instanceId,
                    detail: tableRow,
                    detailData,
                    detailKey: candidate.detailKey,
                    localPosition: snappedPose.localPosition,
                    axes: snappedPose.axes,
                    referenceAxes: cloneAxes(IDENTITY_AXES),
                    localRotationAngles: extractLocalAnglesDeg(
                        IDENTITY_AXES,
                        snappedPose.axes,
                    ),
                    faceFlip: snappedPose.faceFlip ?? false,
                    euler: snappedPose.euler,
                    textureId: null,
                    textureUrl: null,
                    textureScaleMm: DEFAULT_TEXTURE_SCALE_MM,
                    textureRotationDeg: DEFAULT_TEXTURE_ROTATION_DEG,
                    handleMaterialId: isHandleDetail(detailData)
                        ? DEFAULT_HANDLE_MATERIAL_ID
                        : null,
                    pacedUrlId: pacedModuleId,
                    pacedDetailModuleId: pacedModuleId,
                    pacedDetailModule: null,
                    fromImport: Boolean(candidate.fromImport),
                    hidden: false,
                };
                recordSceneHistory();
                setPlacedDetails((prev) => {
                    if (prev.some((inst) => inst.detailKey === candidate.detailKey)) return prev;
                    return [...prev, newInst];
                });
                setSelectedInstanceId(instanceId);
                isPlacingRef.current = false;
                setIsPlacing(false);

                if (canCreatePaced) {
                    setPostPendingInstanceId(instanceId);
                    setPostPendingDetailKey(candidate.detailKey);

                    void persistPacedDetailPlacement({
                        moduleId,
                        detailId,
                        localPosition: snappedPose.localPosition,
                        axes: snappedPose.axes,
                        faceFlip: snappedPose.faceFlip ?? false,
                    }).then((pacedDto) => {
                        if (pacedDto == null) {
                            throw new Error("Empty POST response for paced-detail-modules");
                        }
                        // id должен прийти только из ответа POST.
                        const createdId = pacedDto.id;
                        if (createdId == null || createdId === "" || Number(createdId) === 0) {
                            throw new Error("POST response has no paced-detail-modules id");
                        }
                        setPacedModuleIdByKey((prev) => ({
                            ...prev,
                            [candidate.detailKey]: createdId,
                        }));
                        setClearedPacedKeys((prev) => {
                            if (!prev.has(candidate.detailKey)) return prev;
                            const next = new Set(prev);
                            next.delete(candidate.detailKey);
                            return next;
                        });
                        setPlacedDetails((prev) =>
                            prev.map((inst) => (
                                inst.instanceId === instanceId
                                    ? {
                                        ...inst,
                                        hidden: getPacedDtoHide(pacedDto),
                                        pacedUrlId: createdId,
                                        pacedDetailModuleId: createdId,
                                        pacedDetailModule: pacedDto,
                                    }
                                    : inst
                            )),
                        );
                        syncPacedDetailChange(candidate.detailKey, pacedDto);
                    }).catch((err) => {
                        const status = err?.response?.status;
                        const base = constantsErrorMessages[status]
                            || constantsErrorMessages[404]
                            || "нет связи с сервером";

                        handleModalAlert("Нет связи с сервером. Положение не удалось сохранить.");

                        // POST не создал placement — убираем деталь со сцены.
                        setPlacedDetails((prev) => prev.filter((inst) => inst.instanceId !== instanceId));
                        setSelectedInstanceId((curr) => (curr === instanceId ? null : curr));
                    }).finally(() => {
                        setPostPendingInstanceId(null);
                        setPostPendingDetailKey(null);
                    });
                }
            } catch (err) {
                console.warn("module-3d: add detail failed", err);
            } finally {
                isPlacingRef.current = false;
                setIsPlacing(false);
            }
        };

        const ready = isReadyToPlace(candidate.detail) ? candidate.detail : null;
        if (ready) {
            finish(ready);
            return;
        }

        resolveDetailData(stampCompositionDetailId(candidate.detail))
            .then(finish)
            .catch((err) => {
                console.warn("module-3d: add detail failed", err);
                isPlacingRef.current = false;
                setIsPlacing(false);
            });
    }, [moduleSizeM, snapEnabled, moduleId, pacedModuleIdByKey, pacedIdByKey, clearedPacedKeys, syncPacedDetailChange, recordSceneHistory]);

    const commitAddEmbeddedCandidate = useCallback((candidate, point, event) => {
        if (!candidate || isPlacingRef.current) return;
        const {row, moduleKey, rowIndex} = candidate;
        if (!getDependModuleId(row) || moduleKey == null) return;
        if (placedEmbeddedModulesRef.current.some((inst) => String(inst.moduleKey) === String(moduleKey))) {
            return;
        }

        addingEmbeddedCandidateRef.current = null;
        isPlacingRef.current = true;
        setAddingEmbeddedCandidate(null);
        setIsPlacing(true);

        const finish = async () => {
            try {
                const detailData = embeddedModulePlacementDetailData(row);
                let snappedPose;
                try {
                    snappedPose = resolvePlacementPose({
                        point,
                        event,
                        snapEnabled,
                        cubeSize: moduleSizeM,
                        others: [
                            ...placedDetailsRef.current,
                            ...placedEmbeddedModulesRef.current,
                        ],
                        detailData,
                        pacedPlacement: null,
                        initialAxes: null,
                    });
                } catch (err) {
                    console.warn("module-3d: embedded module placement snap failed", err);
                    snappedPose = resolvePlacementPose({
                        point,
                        event,
                        snapEnabled: false,
                        cubeSize: moduleSizeM,
                        others: [
                            ...placedDetailsRef.current,
                            ...placedEmbeddedModulesRef.current,
                        ],
                        detailData,
                        pacedPlacement: null,
                        initialAxes: null,
                    });
                }

                if (placedEmbeddedModulesRef.current.some((inst) => String(inst.moduleKey) === String(moduleKey))) {
                    return;
                }

                const tempInstanceId = `embedded-temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
                const inst = buildPlacedEmbeddedInstanceShellFromPose({
                    row,
                    index: rowIndex,
                    localPosition: snappedPose.localPosition,
                    axes: snappedPose.axes,
                    faceFlip: snappedPose.faceFlip ?? false,
                    pacedModule: null,
                    instanceId: tempInstanceId,
                });
                if (!inst) {
                    throw new Error("Failed to build embedded module instance");
                }

                recordSceneHistory();
                setPlacedEmbeddedModules((prev) => {
                    if (prev.some((item) => String(item.moduleKey) === String(moduleKey))) return prev;
                    const next = [...prev, inst];
                    placedEmbeddedModulesRef.current = next;
                    return next;
                });
                isPlacingRef.current = false;
                setIsPlacing(false);

                const dependModuleId = getDependModuleId(row);
                invalidateEmbeddedModuleDetailsCache(dependModuleId);
                requestEmbeddedModuleChildren(moduleKey, dependModuleId);
                setPostPendingEmbeddedModuleKey(moduleKey);
                void createEmbeddedModulePlacement(dependModuleId, {
                    localPosition: snappedPose.localPosition,
                    axes: snappedPose.axes,
                    faceFlip: snappedPose.faceFlip ?? false,
                }).then((pacedDto) => {
                    const resolvedId = pacedDto?.id;
                    if (resolvedId == null || resolvedId === "" || Number(resolvedId) === 0) {
                        throw new Error("POST response has no paced-modules id");
                    }
                    setPacedEmbeddedIdByKey((prev) => ({
                        ...prev,
                        [moduleKey]: resolvedId,
                    }));
                    setClearedPacedEmbeddedKeys((prev) => {
                        if (!prev.has(String(moduleKey))) return prev;
                        const next = new Set(prev);
                        next.delete(String(moduleKey));
                        clearedPacedEmbeddedKeysRef.current = next;
                        return next;
                    });
                    setPlacedEmbeddedModules((prev) => {
                        const next = prev.map((item) => (
                            String(item.moduleKey) === String(moduleKey)
                                ? {
                                    ...item,
                                    instanceId: `embedded-paced-${resolvedId}`,
                                    hidden: getPacedDtoHide(pacedDto),
                                    pacedModuleId: resolvedId,
                                    pacedModule: pacedDto,
                                    row: {...item.row, pacedModule: pacedDto},
                                }
                                : item
                        ));
                        placedEmbeddedModulesRef.current = next;
                        return next;
                    });
                    syncPacedDependModuleChange(moduleKey, pacedDto);
                    invalidateEmbeddedModuleDetailsCache(dependModuleId);
                    requestEmbeddedModuleChildren(moduleKey, dependModuleId);
                }).catch((err) => {
                    console.warn("module-3d: embedded module POST failed", err);
                    handleModalAlert("Нет связи с сервером. Положение не удалось сохранить.");
                    setPlacedEmbeddedModules((prev) => prev.filter(
                        (item) => String(item.moduleKey) !== String(moduleKey),
                    ));
                }).finally(() => {
                    setPostPendingEmbeddedModuleKey(null);
                });
            } catch (err) {
                console.warn("module-3d: add embedded module failed", err);
                handleModalAlert("Не удалось добавить встраиваемый модуль на сцену.");
            } finally {
                isPlacingRef.current = false;
                setIsPlacing(false);
            }
        };

        void finish();
    }, [
        moduleSizeM,
        snapEnabled,
        recordSceneHistory,
        syncPacedDependModuleChange,
        handleModalAlert,
        requestEmbeddedModuleChildren,
    ]);

    const addAt = useCallback((point, event) => {
        const embeddedCandidate = addingEmbeddedCandidateRef.current;
        if (embeddedCandidate) {
            commitAddEmbeddedCandidate(embeddedCandidate, point, event);
            return;
        }
        const candidate = addingCandidateRef.current;
        if (!candidate) return;
        commitAddCandidate(candidate, point, event);
    }, [commitAddCandidate, commitAddEmbeddedCandidate]);

    const startAddFromTable = useCallback((row, initialAxes) => {
        const {detail: d, detailKey, fromImport} = row;
        if (!d || !detailKey || isPlacingRef.current) return;
        if (placedDetailsRef.current.some((inst) => inst.detailKey === detailKey)) return;

        setSelectedInstanceId(null);
        if (fromImport) {
            setAddingCandidate({
                detail: d,
                detailKey,
                fromImport: true,
                pacedPlacement: null,
                initialAxes,
            });
            return;
        }

        const pacedId = clearedPacedKeys.has(detailKey)
            ? null
            : (
                pacedIdByKey[detailKey]
                ?? pacedModuleIdByKey[detailKey]
                ?? getPacedDetailModuleId(d)
                ?? null
            );
        if (pacedId == null) {
            setAddingCandidate({
                detail: d,
                detailKey,
                fromImport: false,
                pacedPlacement: null,
                initialAxes,
            });
            return;
        }

        const pacedFromRow = parsePacedPlacementFromDetailRow(d);
        if (pacedFromRow) {
            setAddingCandidate({
                detail: d,
                detailKey,
                fromImport: false,
                pacedPlacement: pacedFromRow,
                initialAxes,
            });
            return;
        }

        setAddingCandidate({
            detail: d,
            detailKey,
            fromImport: false,
            pacedPlacement: undefined,
            initialAxes,
        });
        fetchPacedDetailPlacement(pacedId).then((paced) => {
            setAddingCandidate((curr) => {
                if (!curr || curr.detailKey !== detailKey) return curr;
                return {...curr, pacedPlacement: paced};
            });
        });
    }, [pacedIdByKey, pacedModuleIdByKey, clearedPacedKeys]);

    const startAddEmbeddedFromTable = useCallback((row, moduleKey, rowIndex) => {
        if (!getDependModuleId(row) || moduleKey == null || isPlacingRef.current) return;
        if (postPendingEmbeddedModuleKey != null) return;
        if (placedEmbeddedModulesRef.current.some((inst) => String(inst.moduleKey) === String(moduleKey))) {
            return;
        }

        setSelectedInstanceId(null);
        setAddingEmbeddedCandidate({row, moduleKey, rowIndex});
    }, [postPendingEmbeddedModuleKey]);

    const applyTexture = useCallback((texture) => {
        if (!texture) return;
        setActiveTextureId(texture.id);

        const hasDefaultScale = texture.defaultScaleMm != null;
        const hasDefaultRotation = texture.defaultRotationDeg != null;
        const scaleMm = hasDefaultScale
            ? clampTextureScaleMm(texture.defaultScaleMm)
            : null;
        const rotationDeg = hasDefaultRotation
            ? normalizeTextureRotationDeg(texture.defaultRotationDeg)
            : null;

        if (scaleMm != null) setActiveTextureScaleMm(scaleMm);
        if (rotationDeg != null) setActiveTextureRotationDeg(rotationDeg);

        if (!selectedInstanceId) return;
        recordSceneHistory();
        setPlacedDetails((prev) =>
            prev.map((inst) => {
                if (inst.instanceId !== selectedInstanceId) return inst;
                if (isHandleDetail(inst.detailData)) return inst;
                return {
                    ...inst,
                    textureId: texture.id,
                    textureUrl: texture.url,
                    ...(scaleMm != null ? {textureScaleMm: scaleMm} : null),
                    ...(rotationDeg != null ? {textureRotationDeg: rotationDeg} : null),
                };
            }),
        );
    }, [selectedInstanceId, recordSceneHistory]);

    const uploadCustomTexture = useCallback(async (file) => {
        try {
            const source = await readImageFileAsUploadSource(file);
            setUploadSource({
                mode: "create",
                id: source.id,
                sourceUrl: source.sourceUrl,
                suggestedName: source.suggestedName,
            });
        } catch (err) {
            // eslint-disable-next-line no-alert
            window.alert(err?.message || "Не удалось загрузить текстуру");
        }
    }, []);

    const editCustomTexture = useCallback(async (texture) => {
        if (!texture?.id) return;
        try {
            const sourceUrl = await loadImageUrlAsDataUrl(texture.sourceUrl || texture.url);
            setSelectedInstanceId((prev) => {
                if (prev) return prev;
                const withTex = placedDetails.find((inst) => inst.textureId === texture.id);
                return withTex?.instanceId ?? prev;
            });
            setUploadSource({
                mode: "edit",
                id: texture.id,
                sourceUrl,
                suggestedName: texture.name,
                initial: {
                    name: texture.name,
                    defaultScaleMm: texture.defaultScaleMm,
                    imageZoom: texture.imageZoom ?? texture.previewZoom ?? 1,
                    imageRotationDeg: texture.imageRotationDeg ?? 0,
                    brightness: texture.brightness,
                    contrast: texture.contrast,
                    tintColor: texture.tintColor,
                    tintAmount: texture.tintAmount,
                    previewZoom: texture.previewZoom,
                },
            });
        } catch (err) {
            // eslint-disable-next-line no-alert
            window.alert(err?.message || "Не удалось открыть текстуру для правки");
        }
    }, [placedDetails]);

    const getPlacedDetailLabel = useCallback((inst, indexInAffected = 0) => {
        const base =
            inst?.detail?.name
            || inst?.detailData?.name
            || (inst?.detailKey != null ? `Деталь ${inst.detailKey}` : "Деталь");
        return `${base}`;
    }, []);

    const deleteCustomTexture = useCallback((texture) => {
        if (!texture?.id) return;
        setTextureDeleteTarget(texture);
    }, []);

    const cancelDeleteTexture = useCallback(() => {
        setTextureDeleteTarget(null);
    }, []);

    const confirmDeleteTexture = useCallback(() => {
        const texture = textureDeleteTarget;
        if (!texture?.id) return;

        setTextureCatalog((prev) => prev.filter((t) => t.id !== texture.id));
        setPlacedDetails((placed) =>
            placed.map((inst) => {
                if (inst.textureId !== texture.id) return inst;
                return {
                    ...inst,
                    textureId: null,
                    textureUrl: null,
                };
            }),
        );
        setActiveTextureId((prevId) => {
            if (prevId !== texture.id) return prevId;
            return null;
        });
        setTextureDeleteTarget(null);
    }, [textureDeleteTarget]);

    const textureDeleteAffectedNames = useMemo(() => {
        if (!textureDeleteTarget?.id) return [];
        const byBase = new Map();
        placedDetails.forEach((inst) => {
            if (inst.textureId !== textureDeleteTarget.id) return;
            const base = getPlacedDetailLabel(inst);
            if (!byBase.has(base)) byBase.set(base, []);
            byBase.get(base).push(inst);
        });
        const labeled = [];
        byBase.forEach((list, base) => {
            if (list.length === 1) {
                labeled.push(base);
            } else {
                list.forEach((_, i) => {
                    labeled.push(`${base} #${i + 1}`);
                });
            }
        });
        return labeled;
    }, [textureDeleteTarget, placedDetails, getPlacedDetailLabel]);

    const handleLiveTexturePreview = useCallback((preview) => {
        setLiveTexturePreview(preview);
    }, []);

    const scenePlacedDetails = useMemo(() => {
        let list = placedDetails;
        if (liveTexturePreview?.url) {
            const {mode, textureId, url, scaleMm} = liveTexturePreview;
            list = placedDetails.map((inst) => {
                const matchEdit =
                    mode === "edit"
                    && (
                        inst.textureId === textureId
                        || (selectedInstanceId && inst.instanceId === selectedInstanceId)
                    );
                const matchCreate =
                    mode === "create"
                    && selectedInstanceId
                    && inst.instanceId === selectedInstanceId;
                if (!matchEdit && !matchCreate) return inst;
                return {
                    ...inst,
                    textureUrl: url,
                    textureScaleMm: scaleMm ?? inst.textureScaleMm,
                };
            });
        }
        return list.map((inst) => {
            if (!isHandleDetail(inst.detailData)) return inst;

            const live = liveHandleMaterialPreview;
            if (live?.props) {
                const matchEdit =
                    live.mode === "edit"
                    && live.materialId
                    && (
                        inst.handleMaterialId === live.materialId
                        || inst.instanceId === selectedInstanceId
                    );
                const matchCreate =
                    live.mode === "create"
                    && selectedInstanceId
                    && inst.instanceId === selectedInstanceId;
                if (matchEdit || matchCreate) {
                    return {
                        ...inst,
                        handleMaterialProps: {...live.props, fromFile: false},
                    };
                }
            }

            return {
                ...inst,
                handleMaterialProps: resolveHandleMaterialProps(
                    inst.handleMaterialId ?? DEFAULT_HANDLE_MATERIAL_ID,
                    handleMaterialCatalog,
                ),
            };
        });
    }, [
        placedDetails,
        liveTexturePreview,
        liveHandleMaterialPreview,
        selectedInstanceId,
        handleMaterialCatalog,
    ]);

    const hasLivePreviewTarget = useMemo(() => {
        if (!uploadSource) return false;
        if (selectedInstanceId) return true;
        if (uploadSource.mode === "edit") {
            return placedDetails.some((inst) => inst.textureId === uploadSource.id);
        }
        return false;
    }, [uploadSource, placedDetails, selectedInstanceId]);

    const confirmCustomTexture = useCallback((texture, meta = {}) => {
        const saved = {...texture, builtin: false};
        if (meta.isEdit) {
            setTextureCatalog((prev) =>
                prev.map((t) => (t.id === saved.id ? saved : t)),
            );
            setPlacedDetails((prev) =>
                prev.map((inst) => {
                    if (inst.textureId !== saved.id) return inst;
                    return {
                        ...inst,
                        textureUrl: saved.url,
                        textureScaleMm: clampTextureScaleMm(
                            saved.defaultScaleMm ?? inst.textureScaleMm,
                        ),
                    };
                }),
            );
            applyTexture(saved);
        } else {
            setTextureCatalog((prev) => [...prev, saved]);
            applyTexture(saved);
        }
        setLiveTexturePreview(null);
        setUploadSource(null);
    }, [applyTexture]);

    const rotateTextureBy90 = useCallback(() => {
        if (!selectedInstanceId) {
            setActiveTextureRotationDeg((prev) =>
                normalizeTextureRotationDeg((Number(prev) || 0) + 90),
            );
            return;
        }
        recordSceneHistory();
        setPlacedDetails((prev) =>
            prev.map((inst) => {
                if (inst.instanceId !== selectedInstanceId) return inst;
                return {
                    ...inst,
                    textureRotationDeg: normalizeTextureRotationDeg(
                        (Number(inst.textureRotationDeg) || 0) + 90,
                    ),
                };
            }),
        );
    }, [selectedInstanceId, recordSceneHistory]);

    const stepTextureScale = useCallback((direction) => {
        // +1 = крупнее рисунок → больше мм на повтор; −1 = мельче → меньше мм
        const factor = direction > 0 ? 1.25 : 1 / 1.25;
        const nextScale = (prev) =>
            clampTextureScaleMm((Number(prev) || DEFAULT_TEXTURE_SCALE_MM) * factor);

        if (!selectedInstanceId) {
            setActiveTextureScaleMm((prev) => nextScale(prev));
            return;
        }
        recordSceneHistory();
        setPlacedDetails((prev) =>
            prev.map((inst) => {
                if (inst.instanceId !== selectedInstanceId) return inst;
                return {...inst, textureScaleMm: nextScale(inst.textureScaleMm)};
            }),
        );
    }, [selectedInstanceId, recordSceneHistory]);

    const rollbackInstancePose = useCallback((previousInst) => {
        if (!previousInst?.instanceId) return;
        setPlacedDetails((prev) => prev.map((row) => (
            row.instanceId === previousInst.instanceId
                ? previousInst
                : row
        )));
        syncPacedDetailChange(
            previousInst.detailKey,
            previousInst.pacedDetailModule ?? previousInst.detail?.pacedDetailModule ?? null,
        );
    }, [syncPacedDetailChange]);

    const rollbackEmbeddedInstancePose = useCallback((previousInst) => {
        if (!previousInst?.instanceId) return;
        placedEmbeddedModulesRef.current = placedEmbeddedModulesRef.current.map((row) => (
            row.instanceId === previousInst.instanceId ? previousInst : row
        ));
        setPlacedEmbeddedModules((prev) => prev.map((row) => (
            row.instanceId === previousInst.instanceId ? previousInst : row
        )));
        syncPacedDependModuleChange(
            previousInst.moduleKey,
            previousInst.pacedModule ?? previousInst.row?.pacedModule ?? null,
        );
    }, [syncPacedDependModuleChange]);

    const persistEmbeddedScenePose = useCallback((inst, previousInst = null) => {
        if (!inst) return;
        const currentInst = placedEmbeddedModulesRef.current.find(
            (row) => row.instanceId === inst.instanceId,
        ) ?? inst;
        const pacedId = resolveEmbeddedPacedDeleteId({
            moduleKey: currentInst.moduleKey,
            inst: currentInst,
            pacedEmbeddedIdByKey: pacedEmbeddedIdByKeyRef.current,
        });
        if (pacedId == null) return;

        void updateEmbeddedModuleScenePose({
            pacedModuleId: pacedId,
            localPosition: inst.localPosition,
            axes: inst.axes || IDENTITY_AXES,
            faceFlip: Boolean(inst.faceFlip),
            existingDto: resolveEmbeddedPacedExistingDto(currentInst),
            dependModuleId: currentInst.dependModuleId,
        }).then((data) => {
            if (!data) {
                handleModalAlert("Нет связи с сервером. Положение не удалось сохранить.");
                rollbackEmbeddedInstancePose(previousInst);
                return;
            }
            invalidateEmbeddedModulePlacementCache(currentInst.dependModuleId);
            setPlacedEmbeddedModules((prev) => {
                const next = prev.map((row) => (
                    row.instanceId === inst.instanceId
                        ? {
                            ...row,
                            hidden: getPacedDtoHide(data),
                            pacedModule: data,
                            pacedModuleId: data.id ?? row.pacedModuleId,
                            row: {...row.row, pacedModule: data},
                        }
                        : row
                ));
                placedEmbeddedModulesRef.current = next;
                return next;
            });
            syncPacedDependModuleChange(inst.moduleKey, data);
        });
    }, [rollbackEmbeddedInstancePose, syncPacedDependModuleChange]);

    const persistInstanceScenePose = useCallback((inst, previousInst = null) => {
        if (!inst || inst.fromImport || isHandleDetail(inst.detailData)) return;

        // PUT при перемещении/повороте/зеркале должен идти на id записи paced-detail-modules.
        const currentInst = placedDetailsRef.current.find((row) => row.instanceId === inst.instanceId) ?? inst;
        const pacedId = resolvePlacementPathId({
            detail: currentInst.detail,
            inst: currentInst,
            detailKey: currentInst.detailKey,
            pacedIdByKey,
            pacedModuleIdByKey,
        });
        if (pacedId == null) return;
        const detailId = getPostDetailId(currentInst.detail);
        if (detailId == null) return;

        void updatePacedDetailScenePose({
            pacedDetailModuleId: pacedId,
            detailId,
            localPosition: inst.localPosition,
            axes: inst.axes || IDENTITY_AXES,
            faceFlip: Boolean(inst.faceFlip),
            referenceAxes: inst.referenceAxes ?? null,
            localRotationAngles: inst.localRotationAngles ?? null,
            existingDto: resolvePacedExistingDto(currentInst),
        }).then((data) => {
            if (!data) {
                handleModalAlert("Нет связи с сервером. Положение не удалось сохранить.");
                rollbackInstancePose(previousInst);
                return;
            }
            setPlacedDetails((prev) =>
                prev.map((row) => (
                    row.instanceId === inst.instanceId
                        ? {
                            ...row,
                            hidden: getPacedDtoHide(data),
                            pacedDetailModule: data,
                            pacedDetailModuleId: data.id ?? row.pacedDetailModuleId,
                            pacedUrlId: data.id ?? row.pacedUrlId,
                        }
                        : row
                )),
            );
            syncPacedDetailChange(inst.detailKey, data);

            // Синхронизация страницы "Основные" (PacedDetailModuleItem) по этому detailId.
            // (Там DTO грузится один раз, поэтому без события поля не обновятся.)
            window.dispatchEvent(new CustomEvent("paced-detail-module-updated", {
                detail: {detailId},
            }));
        });
    }, [
        pacedIdByKey,
        pacedModuleIdByKey,
        handleModalAlert,
        rollbackInstancePose,
        syncPacedDetailChange,
    ]);

    const applySceneHistorySnapshot = useCallback((snapshot) => {
        if (!snapshot) return;
        historyApplyingRef.current = true;
        const prevList = placedDetailsRef.current;
        const prevById = new Map(prevList.map((inst) => [inst.instanceId, inst]));
        const prevByKey = new Map(
            prevList
                .filter((inst) => inst?.detailKey != null)
                .map((inst) => [String(inst.detailKey), inst]),
        );
        const nextSlim = Array.isArray(snapshot.placedDetails) ? snapshot.placedDetails : [];
        // Снимок лёгкий (без detailData) — позу накладываем на текущие полные инстансы.
        const nextList = nextSlim.map((slim) => {
            const full = prevById.get(slim.instanceId)
                || (slim.detailKey != null ? prevByKey.get(String(slim.detailKey)) : null)
                || slim;
            return {
                ...full,
                localPosition: slim.localPosition ?? full.localPosition,
                axes: slim.axes ?? full.axes,
                referenceAxes: slim.referenceAxes ?? full.referenceAxes,
                localRotationAngles: slim.localRotationAngles ?? full.localRotationAngles,
                faceFlip: slim.faceFlip ?? full.faceFlip,
                euler: slim.euler ?? full.euler,
                textureId: slim.textureId ?? full.textureId,
                textureUrl: slim.textureUrl ?? full.textureUrl,
                textureScaleMm: slim.textureScaleMm ?? full.textureScaleMm,
                textureRotationDeg: slim.textureRotationDeg ?? full.textureRotationDeg,
                handleMaterialId: slim.handleMaterialId ?? full.handleMaterialId,
                hidden: slim.hidden ?? full.hidden,
                pacedUrlId: slim.pacedUrlId ?? full.pacedUrlId,
                pacedDetailModuleId: slim.pacedDetailModuleId ?? full.pacedDetailModuleId,
            };
        });
        setPlacedDetails(nextList);
        setSelectedInstanceId(snapshot.selectedInstanceId ?? null);
        setPacedModuleIdByKey(snapshot.pacedModuleIdByKey || {});
        setClearedPacedKeys(new Set(snapshot.clearedPacedKeys || []));

        nextList.forEach((inst) => {
            const prev = prevById.get(inst.instanceId);
            if (!prev) return;
            const samePose = (
                prev.localPosition?.x === inst.localPosition?.x
                && prev.localPosition?.y === inst.localPosition?.y
                && prev.localPosition?.z === inst.localPosition?.z
                && Boolean(prev.faceFlip) === Boolean(inst.faceFlip)
                && JSON.stringify(prev.axes) === JSON.stringify(inst.axes)
            );
            if (!samePose) {
                persistInstanceScenePose(inst, prev);
            }
        });

        window.setTimeout(() => {
            historyApplyingRef.current = false;
        }, 0);
    }, [persistInstanceScenePose]);

    const undoSceneChange = useCallback(() => {
        if (historyApplyingRef.current || addingMode || isPlacing) return;
        const current = takeSceneSnapshot();
        const {history: nextHistory, restored} = undoHistory(sceneHistory, current);
        if (!restored) return;
        setSceneHistory(nextHistory);
        saveModule3DHistory(moduleId, nextHistory);
        applySceneHistorySnapshot(restored);
    }, [
        takeSceneSnapshot,
        sceneHistory,
        moduleId,
        applySceneHistorySnapshot,
        addingMode,
        isPlacing,
    ]);

    const redoSceneChange = useCallback(() => {
        if (historyApplyingRef.current || addingMode || isPlacing) return;
        const current = takeSceneSnapshot();
        const {history: nextHistory, restored} = redoHistory(sceneHistory, current);
        if (!restored) return;
        setSceneHistory(nextHistory);
        saveModule3DHistory(moduleId, nextHistory);
        applySceneHistorySnapshot(restored);
    }, [
        takeSceneSnapshot,
        sceneHistory,
        moduleId,
        applySceneHistorySnapshot,
        addingMode,
        isPlacing,
    ]);

    const commitEmbeddedInstancePose = useCallback((instanceId, next) => {
        const previousInst = placedEmbeddedModulesRef.current.find(
            (row) => row.instanceId === instanceId,
        ) ?? null;
        if (!previousInst) return;

        const axes = next.axes != null
            ? cloneAxes(next.axes)
            : cloneAxes(previousInst.axes || IDENTITY_AXES);
        const faceFlip = next.faceFlip != null
            ? Boolean(next.faceFlip)
            : Boolean(previousInst.faceFlip);
        const merged = {
            ...previousInst,
            localPosition: next.localPosition ?? previousInst.localPosition,
            axes,
            faceFlip,
            euler: snapEulerTo90(next.euler ?? axesToEulerDegrees(axes)),
        };

        recordSceneHistory();
        placedEmbeddedModulesRef.current = placedEmbeddedModulesRef.current.map((row) => (
            row.instanceId === instanceId ? merged : row
        ));
        setPlacedEmbeddedModules((prev) => prev.map((row) => (
            row.instanceId === instanceId ? merged : row
        )));
        persistEmbeddedScenePose(merged, previousInst);
    }, [persistEmbeddedScenePose, recordSceneHistory]);

    const commitInstancePose = useCallback((instanceId, next) => {
        // Не полагаемся на side-effects внутри setState (onMouseUp гизмо — не React-событие,
        // updater может отработать после выхода из функции → merged оставался null и PUT не уходил).
        const previousInst = placedDetailsRef.current.find((row) => row.instanceId === instanceId) ?? null;
        if (!previousInst) return;

        let axes = next.axes != null
            ? cloneAxes(next.axes)
            : cloneAxes(previousInst.axes || IDENTITY_AXES);
        let faceFlip = next.faceFlip != null
            ? Boolean(next.faceFlip)
            : Boolean(previousInst.faceFlip);
        let referenceAxes = next.referenceAxes != null
            ? cloneAxes(next.referenceAxes)
            : cloneAxes(previousInst.referenceAxes || axes);
        let localRotationAngles = next.localRotationAngles != null
            ? normalizeLocalRotationAngles(next.localRotationAngles)
            : normalizeLocalRotationAngles(previousInst.localRotationAngles);

        if (!isHandleDetail(previousInst.detailData)) {
            const normalized = normalizeAxesWithFaceFlip(axes, faceFlip);
            axes = normalized.axes;
            faceFlip = normalized.faceFlip;
            if (isCardinalAxes(axes)) {
                const quant = quantizeAxesTo90Degrees(axes, faceFlip);
                axes = quant.axes;
                faceFlip = quant.faceFlip;
                referenceAxes = quant.referenceAxes;
                localRotationAngles = quant.localRotationAngles;
            } else {
                referenceAxes = next.referenceAxes != null
                    ? cloneAxes(next.referenceAxes)
                    : cloneAxes(previousInst.referenceAxes || axes);
                localRotationAngles = normalizeLocalRotationAngles(localRotationAngles);
            }
        }

        const euler = eulerForInstancePersist(
            axes,
            next.euler ?? axesToEulerDegrees(axes),
        );
        const merged = {
            ...previousInst,
            localPosition: next.localPosition ?? previousInst.localPosition,
            axes,
            faceFlip,
            euler,
            referenceAxes,
            localRotationAngles,
        };

        // Снимок «до» — из ref (ещё старое состояние). Ошибки истории не блокируют PUT.
        recordSceneHistory();

        placedDetailsRef.current = placedDetailsRef.current.map((row) => (
            row.instanceId === instanceId ? merged : row
        ));
        setPlacedDetails((prev) => prev.map((row) => (
            row.instanceId === instanceId ? merged : row
        )));
        persistInstanceScenePose(merged, previousInst);
    }, [persistInstanceScenePose, recordSceneHistory]);

    const commitBasePointMm = useCallback((nextMm) => {
        if (selectedInstanceId == null) return;
        const nextLocal = {
            localPosition: {
                x: Number.isFinite(Number(nextMm?.x)) ? Number(nextMm.x) * MM_TO_M : 0,
                y: Number.isFinite(Number(nextMm?.y)) ? Number(nextMm.y) * MM_TO_M : 0,
                z: Number.isFinite(Number(nextMm?.z)) ? Number(nextMm.z) * MM_TO_M : 0,
            },
        };
        if (placedDetailsRef.current.some((row) => row.instanceId === selectedInstanceId)) {
            commitInstancePose(selectedInstanceId, nextLocal);
            return;
        }
        if (placedEmbeddedModulesRef.current.some((row) => row.instanceId === selectedInstanceId)) {
            commitEmbeddedInstancePose(selectedInstanceId, nextLocal);
        }
    }, [selectedInstanceId, commitInstancePose, commitEmbeddedInstancePose, MM_TO_M]);

    const updateInstanceTransform = useCallback((instanceId, next) => {
        if (placedDetailsRef.current.some((row) => row.instanceId === instanceId)) {
            commitInstancePose(instanceId, next);
            return;
        }
        if (placedEmbeddedModulesRef.current.some((row) => row.instanceId === instanceId)) {
            commitEmbeddedInstancePose(instanceId, next);
        }
    }, [commitInstancePose, commitEmbeddedInstancePose]);

    /** +90° вокруг мировой оси куба (как красная/зелёная/синяя на сцене). */
    const rotateSelectedBy90 = useCallback((axis, sign) => {
        if (!selectedInstanceId) return;
        const isDetail = placedDetailsRef.current.some((row) => row.instanceId === selectedInstanceId);
        const isEmbedded = placedEmbeddedModulesRef.current.some(
            (row) => row.instanceId === selectedInstanceId,
        );
        const inst = (isDetail ? placedDetailsRef : placedEmbeddedModulesRef).current.find(
            (row) => row.instanceId === selectedInstanceId,
        );
        if (!inst) return;

        if (isDetail) {
            const {referenceAxes} = resolvePanelRotationState(inst);
            const rotatedRef = rotateAxesBy90(referenceAxes, axis, sign);
            const quant = quantizeAxesTo90Degrees(rotatedRef, Boolean(inst.faceFlip));
            commitInstancePose(selectedInstanceId, {
                axes: quant.axes,
                referenceAxes: quant.referenceAxes,
                localRotationAngles: quant.localRotationAngles,
                faceFlip: quant.faceFlip,
                euler: eulerForInstancePersist(quant.axes, axesToEulerDegrees(quant.axes)),
            });
            return;
        }

        const axes = rotateAxesBy90(inst.axes || IDENTITY_AXES, axis, sign);
        const next = {
            axes,
            faceFlip: Boolean(inst.faceFlip),
            euler: eulerForInstancePersist(axes, axesToEulerDegrees(axes)),
        };
        if (isEmbedded) {
            commitEmbeddedInstancePose(selectedInstanceId, next);
        }
    }, [selectedInstanceId, commitInstancePose, commitEmbeddedInstancePose]);

    const applyFreeRotationByAngle = useCallback((axisKey, nextDeg) => {
        if (!selectedInstanceId || axisKey !== freeRotationAxis) return;
        const inst = placedDetailsRef.current.find((row) => row.instanceId === selectedInstanceId);
        if (!inst || isHandleDetail(inst.detailData)) return;
        const {referenceAxes} = resolvePanelRotationState(inst);
        const localRotationAngles = anglesOnSingleAxis(freeRotationAxis, nextDeg);
        const axes = composePanelAxes(referenceAxes, localRotationAngles, freeRotationAxis);
        commitInstancePose(selectedInstanceId, {
            axes,
            referenceAxes: cloneAxes(referenceAxes),
            localRotationAngles,
            faceFlip: Boolean(inst.faceFlip),
            euler: eulerForInstancePersist(axes, axesToEulerDegrees(axes)),
        });
    }, [commitInstancePose, freeRotationAxis, selectedInstanceId]);

    const handleFreeRotationAxisChange = useCallback((axis) => {
        if (!selectedInstanceId) {
            setFreeRotationAxis(axis);
            return;
        }
        const inst = placedDetailsRef.current.find((row) => row.instanceId === selectedInstanceId);
        if (!inst || isHandleDetail(inst.detailData)) {
            setFreeRotationAxis(axis);
            return;
        }
        const {referenceAxes, localRotationAngles} = resolvePanelRotationState(inst);
        const lockedAxis = lockedPanelRotationAxisKey(localRotationAngles);
        if (lockedAxis && axis !== lockedAxis) {
            setFreeRotationAxis(lockedAxis);
            return;
        }
        setFreeRotationAxis(axis);
        const nextAngles = enforceSingleAxisAngles(localRotationAngles, axis);
        const unchanged = nextAngles.x === localRotationAngles.x
            && nextAngles.y === localRotationAngles.y
            && nextAngles.z === localRotationAngles.z;
        if (unchanged) return;
        const axes = composePanelAxes(referenceAxes, nextAngles, axis);
        commitInstancePose(selectedInstanceId, {
            axes,
            referenceAxes: cloneAxes(referenceAxes),
            localRotationAngles: nextAngles,
            faceFlip: Boolean(inst.faceFlip),
            euler: eulerForInstancePersist(axes, axesToEulerDegrees(axes)),
        });
    }, [commitInstancePose, selectedInstanceId]);

    /** Зеркало относительно плоскости, перпендикулярной мировой оси (через угол детали). */
    const mirrorSelected = useCallback((axis) => {
        if (!selectedInstanceId) return;
        const isDetail = placedDetailsRef.current.some((row) => row.instanceId === selectedInstanceId);
        const isEmbedded = placedEmbeddedModulesRef.current.some(
            (row) => row.instanceId === selectedInstanceId,
        );
        const inst = (isDetail ? placedDetailsRef : placedEmbeddedModulesRef).current.find(
            (row) => row.instanceId === selectedInstanceId,
        );
        if (!inst) return;

        const mirrored = mirrorAxesWithFaceFlip(
            inst.axes || IDENTITY_AXES,
            axis,
            inst.faceFlip,
        );
        const {axes: rightAxes} = normalizeAxesWithFaceFlip(mirrored.axes, false);
        const next = {
            axes: mirrored.axes,
            faceFlip: mirrored.faceFlip,
            euler: isDetail
                ? eulerForInstancePersist(mirrored.axes, axesToEulerDegrees(rightAxes))
                : snapEulerTo90(axesToEulerDegrees(rightAxes)),
        };
        if (isDetail) {
            commitInstancePose(selectedInstanceId, next);
            return;
        }
        if (isEmbedded) {
            commitEmbeddedInstancePose(selectedInstanceId, next);
        }
    }, [selectedInstanceId, commitInstancePose, commitEmbeddedInstancePose]);

    const applyCameraView = useCallback((view) => {
        setActiveCameraView(view);
        setCameraViewRequest({view, token: Date.now()});
    }, []);

    const openDxfPicker = useCallback(() => {
        if (addingMode || isPlacing) return;
        dxfFileInputRef.current?.click();
    }, [addingMode, isPlacing]);

    const openHandlePicker = useCallback(() => {
        if (addingMode || isPlacing) return;
        handleFileInputRef.current?.click();
    }, [addingMode, isPlacing]);

    const openImportSourceDialog = useCallback(() => {
        if (addingMode || isPlacing) return;
        setImportSourceOpen(true);
    }, [addingMode, isPlacing]);

    const closeImportSourceDialog = useCallback(() => {
        setImportSourceOpen(false);
    }, []);

    const confirmImportSource = useCallback((source) => {
        setImportSourceOpen(false);
        if (source === IMPORT_SOURCE_3DS) {
            // Дать модалке закрыться до системного file picker.
            window.setTimeout(() => openHandlePicker(), 0);
            return;
        }
        window.setTimeout(() => openDxfPicker(), 0);
    }, [openDxfPicker, openHandlePicker]);

    const onDxfFileChange = useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        const fileName = file.name || "detail.dxf";
        try {
            const text = await file.text();
            const {detailData, meta} = parseDxfToDetailData(text, {
                name: fileName.replace(/\.[^.]+$/, "") || "DXF деталь",
            });
            setDxfImport({fileName, detailData, meta, parseError: null});
        } catch (err) {
            setDxfImport({
                fileName,
                detailData: null,
                meta: null,
                parseError: err?.message || "Не удалось разобрать DXF",
            });
        }
    }, []);

    const closeDxfImport = useCallback(() => {
        setDxfImport(null);
    }, []);

    const confirmDxfImport = useCallback(({name, position, heightMm, holesThrough, holeDepthMm}) => {
        if (!dxfImport?.detailData) return;
        const height = Math.max(Number(heightMm) || 18, 1);
        const detailName = String(name || "").trim() || "DXF деталь";
        const detailPosition = String(position || "").trim();
        const detailData = {
            ...dxfImport.detailData,
            name: detailName,
            position: detailPosition || null,
            height,
            holes: applyHolesDepth(
                dxfImport.detailData.holes,
                height,
                holesThrough !== false,
                holeDepthMm,
            ),
        };
        const detailKey = `dxf:${Date.now()}-${Math.random().toString(16).slice(2)}`;
        setDxfImport(null);
        setImportedDetails((prev) => [...prev, {detailKey, kind: "dxf", detail: detailData}]);
    }, [dxfImport]);

    const closeDxfEdit = useCallback(() => {
        setDxfEditTarget(null);
    }, []);

    const confirmDxfEdit = useCallback(({name, position, heightMm, holesThrough, holeDepthMm}) => {
        if (!dxfEditTarget?.detailKey) return;
        const height = Math.max(Number(heightMm) || 18, 1);
        const detailName = String(name || "").trim() || "DXF деталь";
        const detailPosition = String(position || "").trim();
        const detailKey = dxfEditTarget.detailKey;
        const fromImport = Boolean(dxfEditTarget.fromImport);

        const patchDetail = (prevDetail) => ({
            ...prevDetail,
            name: detailName,
            position: detailPosition || null,
            height,
            holes: applyHolesDepth(
                prevDetail?.holes,
                height,
                holesThrough !== false,
                holeDepthMm,
            ),
        });

        if (fromImport) {
            setImportedDetails((prev) => prev.map((row) => (
                row.detailKey === detailKey
                    ? {...row, detail: patchDetail(row.detail)}
                    : row
            )));
        } else {
            setDetailOverrides((prev) => ({
                ...prev,
                [detailKey]: {
                    name: detailName,
                    position: detailPosition || null,
                    height,
                },
            }));
        }

        setPlacedDetails((prev) => prev.map((inst) => {
            if (inst.detailKey !== detailKey) return inst;
            const nextData = patchDetail(inst.detailData);
            return {
                ...inst,
                detailData: nextData,
                detail: {...(inst.detail || {}), ...nextData},
            };
        }));
        setDxfEditTarget(null);
    }, [dxfEditTarget]);

    const onHandleFileChange = useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        const fileName = file.name || "handle.3ds";
        try {
            const loaded = await load3dsHandleFromFile(file);
            setHandleImport({
                fileName,
                modelBase64: loaded.modelBase64,
                materialPreview: loaded.materialPreview || null,
                meta: {
                    meshCount: loaded.meshCount,
                    maxDimMeters: loaded.maxDimMeters,
                    defaultSizeMm: loaded.defaultSizeMm,
                },
                parseError: null,
            });
        } catch (err) {
            setHandleImport({
                fileName,
                modelBase64: null,
                materialPreview: null,
                meta: null,
                parseError: err?.message || "Не удалось разобрать 3DS",
            });
        }
    }, []);

    const closeHandleImport = useCallback(() => {
        setHandleImport(null);
    }, []);

    const confirmHandleImport = useCallback(({name, position, sizeMm}) => {
        if (!handleImport?.modelBase64) return;
        const size = Math.max(Number(sizeMm) || DEFAULT_HANDLE_SIZE_MM, 1);
        const detailName = String(name || "").trim() || "Ручка";
        const detailPosition = String(position || "").trim();
        const detailData = {
            name: detailName,
            position: detailPosition || null,
            source: "3ds",
            kind: "handle",
            sizeMm: size,
            modelBase64: handleImport.modelBase64,
            materialPreview: handleImport.materialPreview || null,
            // габариты-заглушки для кода, ожидающего панель
            length: size,
            width: Math.max(size * 0.25, 1),
            height: Math.max(size * 0.25, 1),
        };
        const detailKey = `3ds:${Date.now()}-${Math.random().toString(16).slice(2)}`;
        setHandleImport(null);
        setImportedDetails((prev) => [...prev, {detailKey, kind: "handle", detail: detailData}]);
    }, [handleImport]);

    const closeHandleEdit = useCallback(() => {
        setHandleEditTarget(null);
    }, []);

    const confirmHandleEdit = useCallback(({name, position, sizeMm}) => {
        if (!handleEditTarget?.detailKey) return;
        const size = Math.max(Number(sizeMm) || DEFAULT_HANDLE_SIZE_MM, 1);
        const detailName = String(name || "").trim() || "Ручка";
        const detailPosition = String(position || "").trim();
        const detailKey = handleEditTarget.detailKey;

        const patchDetail = (prevDetail) => ({
            ...prevDetail,
            name: detailName,
            position: detailPosition || null,
            sizeMm: size,
            length: size,
            width: Math.max(size * 0.25, 1),
            height: Math.max(size * 0.25, 1),
        });

        setImportedDetails((prev) => prev.map((row) => (
            row.detailKey === detailKey
                ? {...row, detail: patchDetail(row.detail)}
                : row
        )));
        setPlacedDetails((prev) => prev.map((inst) => {
            if (inst.detailKey !== detailKey) return inst;
            const nextData = patchDetail(inst.detailData);
            return {
                ...inst,
                detailData: nextData,
                detail: {...(inst.detail || {}), ...nextData},
            };
        }));
        setHandleEditTarget(null);
    }, [handleEditTarget]);

    const clearLocalPacedKey = useCallback((detailKey) => {
        if (!detailKey) return;
        setPacedModuleIdByKey((prev) => {
            if (prev[detailKey] == null) return prev;
            const next = {...prev};
            delete next[detailKey];
            return next;
        });
    }, []);

    const requestDeletePacedRecord = useCallback(async (detailKey, detail = null) => {
        if (!detailKey) return true;
        const inst = placedDetailsRef.current.find((row) => row.detailKey === detailKey);
        const deleteId = resolvePlacementPathId({
            detail,
            inst,
            detailKey,
            pacedIdByKey,
            pacedModuleIdByKey,
            allowDetailIdFallback: false,
        });
        if (deleteId == null) {
            handleModalAlert("Нет связи с сервером. Положение не удалось удалить.");
            return false;
        }
        const ok = await deletePacedDetailPlacement(deleteId);
        if (!ok) {
            handleModalAlert("Нет связи с сервером. Положение не удалось удалить.");
            return false;
        }
        return true;
    }, [pacedIdByKey, pacedModuleIdByKey, handleModalAlert]);

    const requestDeleteEmbeddedPacedRecord = useCallback(async (moduleKey, inst = null) => {
        if (moduleKey == null) return true;
        const deleteId = resolveEmbeddedPacedDeleteId({
            moduleKey,
            inst: inst
                ?? placedEmbeddedModulesRef.current.find(
                    (item) => String(item.moduleKey) === String(moduleKey),
                )
                ?? null,
            pacedEmbeddedIdByKey,
        });
        if (deleteId == null) {
            handleModalAlert("Нет связи с сервером. Положение не удалось удалить.");
            return false;
        }
        const dependModuleId = inst?.dependModuleId
            ?? getDependModuleId(inst?.row);
        const ok = await deleteEmbeddedModulePlacement(deleteId, dependModuleId);
        if (!ok) {
            handleModalAlert("Нет связи с сервером. Положение не удалось удалить.");
            return false;
        }
        invalidateEmbeddedModuleDetailsCache(dependModuleId);
        return true;
    }, [pacedEmbeddedIdByKey, handleModalAlert]);

    const applyDeletedPacedState = useCallback((detailKey) => {
        if (!detailKey) return;
        clearLocalPacedKey(detailKey);
        setClearedPacedKeys((prev) => {
            if (prev.has(detailKey)) return prev;
            const next = new Set(prev);
            next.add(detailKey);
            return next;
        });
        syncPacedDetailChange(detailKey, null);
    }, [clearLocalPacedKey, syncPacedDetailChange]);

    const embeddedPlacedByKey = useMemo(() => {
        const set = new Set();
        for (const inst of placedEmbeddedModules) {
            if (inst?.moduleKey != null) set.add(String(inst.moduleKey));
        }
        return set;
    }, [placedEmbeddedModules]);

    const applyDeletedEmbeddedPacedState = useCallback((moduleKey) => {
        if (moduleKey == null) return;
        setPacedEmbeddedIdByKey((prev) => {
            if (prev[moduleKey] == null) return prev;
            const next = {...prev};
            delete next[moduleKey];
            return next;
        });
        setClearedPacedEmbeddedKeys((prev) => {
            const key = String(moduleKey);
            if (prev.has(key)) return prev;
            const next = new Set(prev);
            next.add(key);
            clearedPacedEmbeddedKeysRef.current = next;
            return next;
        });
        syncPacedDependModuleChange(moduleKey, null);
    }, [syncPacedDependModuleChange]);

    const removeEmbeddedByModuleKey = useCallback(async (moduleKey, row = null) => {
        if (moduleKey == null) return;

        const prevPlacedEmbeddedModules = placedEmbeddedModulesRef.current;
        const prevPacedEmbeddedIdByKey = pacedEmbeddedIdByKey;
        const prevClearedPacedEmbeddedKeys = new Set(clearedPacedEmbeddedKeys);
        const prevAddingEmbeddedCandidate = addingEmbeddedCandidate;
        const prevInst = prevPlacedEmbeddedModules.find(
            (inst) => String(inst.moduleKey) === String(moduleKey),
        ) ?? null;
        const prevPacedDto = prevInst?.pacedModule ?? row?.pacedModule ?? null;

        recordSceneHistory();
        applyDeletedEmbeddedPacedState(moduleKey);
        embeddedChildrenLoadRef.current.delete(String(moduleKey));
        setPlacedEmbeddedModules((prev) => {
            const index = prev.findIndex((inst) => String(inst.moduleKey) === String(moduleKey));
            if (index === -1) return prev;
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
        setAddingEmbeddedCandidate((curr) => (
            curr?.moduleKey != null && String(curr.moduleKey) === String(moduleKey) ? null : curr
        ));

        const canRemove = await requestDeleteEmbeddedPacedRecord(moduleKey, prevInst);
        if (canRemove) return;

        setPlacedEmbeddedModules(prevPlacedEmbeddedModules);
        setPacedEmbeddedIdByKey(prevPacedEmbeddedIdByKey);
        setClearedPacedEmbeddedKeys(prevClearedPacedEmbeddedKeys);
        setAddingEmbeddedCandidate(prevAddingEmbeddedCandidate);
        syncPacedDependModuleChange(moduleKey, prevPacedDto);
    }, [
        applyDeletedEmbeddedPacedState,
        requestDeleteEmbeddedPacedRecord,
        addingEmbeddedCandidate,
        pacedEmbeddedIdByKey,
        clearedPacedEmbeddedKeys,
        syncPacedDependModuleChange,
        recordSceneHistory,
    ]);

    const removeOneByDetailKey = useCallback(async (detailKey, detail = null) => {
        if (!detailKey) return;
        const prevPlacedDetails = placedDetailsRef.current;
        const prevSelectedInstanceId = selectedInstanceId;
        const prevAddingCandidate = addingCandidate;
        const prevPacedModuleIdByKey = pacedModuleIdByKey;
        const prevClearedPacedKeys = new Set(clearedPacedKeys);
        const prevInst = prevPlacedDetails.find((inst) => inst.detailKey === detailKey) ?? null;
        const prevPacedDto = prevInst?.pacedDetailModule ?? detail?.pacedDetailModule ?? null;

        recordSceneHistory();
        applyDeletedPacedState(detailKey);
        setPlacedDetails((prev) => {
            const index = prev.findIndex((inst) => inst.detailKey === detailKey);
            if (index === -1) return prev;
            const removed = prev[index];
            setSelectedInstanceId((curr) => (curr === removed.instanceId ? null : curr));
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
        setAddingCandidate((curr) => (curr?.detailKey === detailKey ? null : curr));

        const canRemove = await requestDeletePacedRecord(detailKey, detail);
        if (canRemove) return;

        setPlacedDetails(prevPlacedDetails);
        setSelectedInstanceId(prevSelectedInstanceId);
        setAddingCandidate(prevAddingCandidate);
        setPacedModuleIdByKey(prevPacedModuleIdByKey);
        setClearedPacedKeys(prevClearedPacedKeys);
        syncPacedDetailChange(detailKey, prevPacedDto);
    }, [
        requestDeletePacedRecord,
        applyDeletedPacedState,
        selectedInstanceId,
        addingCandidate,
        pacedModuleIdByKey,
        clearedPacedKeys,
        syncPacedDetailChange,
        recordSceneHistory,
    ]);

    const openRowMenu = useCallback((event, row) => {
        event.stopPropagation();
        setRowMenu({
            anchorEl: event.currentTarget,
            detailKey: row.detailKey,
            fromImport: row.fromImport,
            detail: row.detail,
            kind: row.kind,
        });
    }, []);

    const closeRowMenu = useCallback(() => {
        setRowMenu(null);
    }, []);

    const openEmbeddedRowMenu = useCallback((event, row, moduleKey) => {
        event.stopPropagation();
        setEmbeddedRowMenu({
            anchorEl: event.currentTarget,
            moduleKey,
            row,
        });
    }, []);

    const closeEmbeddedRowMenu = useCallback(() => {
        setEmbeddedRowMenu(null);
    }, []);

    const openCompositionDetail = useCallback((detail) => {
        const detailId = getPostDetailId(detail);
        if (detailId == null) return;
        const pathname = `/organization/requests-cutting/composition/${encodeURIComponent(String(detailId))}`;
        EditLocalStoragePagesData(pathname, "tab", 1);
        navigate(pathname);
    }, [navigate]);

    const openPacedDetailCard = useCallback((detail, pacedDetailIdOverride = null) => {
        if (moduleId == null || moduleId === "") return;
        // GET карточки "Основные" должен идти по id записи paced-detail-modules,
        // а не по id детали из 2D (compositionDetailId).
        const pacedDetailId = pacedDetailIdOverride ?? getPacedDetailModuleId(detail);
        const detailId = getPostDetailId(detail);
        if (pacedDetailId == null || detailId == null) return;
        const nameSeg = encodeURIComponent(moduleName || String(moduleId));
        const posSeg = encodeURIComponent(detail?.position || "-");
        const pathname = `/organization/module/${moduleId}/${nameSeg}/paced-detail/${pacedDetailId}/${posSeg}`;
        EditLocalStoragePagesData(pathname, "tab", 1);
        navigate(`${pathname}?detailId=${encodeURIComponent(String(detailId))}`);
    }, [moduleId, moduleName, navigate]);

    const openEmbeddedPacedModuleCard = useCallback((row) => {
        const dependModuleId = getDependModuleId(row);
        const pathname = buildDependModulePagePath(row, moduleId, moduleName);
        if (dependModuleId == null || dependModuleId === "" || !pathname) return;
        EditLocalStoragePagesData(`/organization/module/${dependModuleId}`, "tab", PACED_MODULE_TAB);
        navigate(pathname);
    }, [moduleId, moduleName, navigate]);

    const placedModuleDetailInstances = useMemo(
        () => placedDetails.filter((inst) => !inst.fromImport),
        [placedDetails],
    );
    const hasVisiblePlacedDetails = useMemo(
        () => placedModuleDetailInstances.some((inst) => !inst.hidden),
        [placedModuleDetailInstances],
    );
    const hasHiddenPlacedDetails = useMemo(
        () => placedModuleDetailInstances.some((inst) => inst.hidden),
        [placedModuleDetailInstances],
    );
    const hasVisiblePlacedEmbedded = useMemo(
        () => placedEmbeddedModules.some((inst) => !inst.hidden),
        [placedEmbeddedModules],
    );
    const hasHiddenPlacedEmbedded = useMemo(
        () => placedEmbeddedModules.some((inst) => inst.hidden),
        [placedEmbeddedModules],
    );

    const tableRows = useMemo(() => {
        const fromModule = (details || [])
            .map((d, idx) => {
                const detailKey = getDetailKey(d, idx);
                const override = detailOverrides[detailKey];
                const base = override
                    ? {
                        ...d,
                        name: override.name ?? d?.name,
                        position: override.position ?? d?.position,
                        height: override.height ?? d?.height,
                    }
                    : d;
                const detail = stampCompositionDetailId(base);
                return {detailKey, detail, fromImport: false};
            })
            .filter(Boolean);
        const fromImport = (importedDetails || []).map((row) => ({
            detailKey: row.detailKey,
            detail: row.detail,
            fromImport: true,
            kind: row.kind || (isHandleDetail(row.detail) ? "handle" : "dxf"),
        }));
        return [...fromModule, ...fromImport];
    }, [details, importedDetails, detailOverrides]);

    const embeddedModuleRows = useMemo(
        () => (Array.isArray(dependModules) ? dependModules : []).map((row, index) => {
            const normalizedRow = normalizeDependModuleRow(row);
            return {
                row: normalizedRow,
                moduleKey: getDependModuleKey(normalizedRow, index),
            };
        }),
        [dependModules],
    );

    const sceneMenuTargetId = sceneContextMenu?.instanceId ?? null;
    const sceneMenuInstance = sceneMenuTargetId
        ? (
            placedDetails.find((row) => row.instanceId === sceneMenuTargetId)
            ?? placedEmbeddedModules.find((row) => row.instanceId === sceneMenuTargetId)
            ?? null
        )
        : null;
    const sceneMenuHasSelection = Boolean(sceneMenuTargetId);
    const sceneMenuIsEmbedded = Boolean(sceneMenuInstance?.moduleKey != null && sceneMenuInstance?.moduleSizeM);
    const hasDependModules = embeddedModuleRows.length > 0;
    const canRevealAllDetails = Boolean(isolatedDetailInstanceId) || hasHiddenPlacedDetails;
    const canRevealAllEmbedded = Boolean(isolatedEmbeddedInstanceId) || hasHiddenPlacedEmbedded;
    const sceneMenuVisiblePeerCount = useMemo(() => {
        if (!sceneMenuInstance) return 0;
        if (sceneMenuIsEmbedded) {
            return placedEmbeddedModules.filter((inst) => !inst.hidden).length;
        }
        return placedDetails.filter((inst) => !inst.hidden).length;
    }, [sceneMenuInstance, sceneMenuIsEmbedded, placedDetails, placedEmbeddedModules]);
    const sceneMenuSelectedHidden = Boolean(sceneMenuInstance?.hidden);
    const sceneMenuCanHideAllDetailsExcept = Boolean(
        sceneMenuTargetId
        && sceneMenuInstance
        && !sceneMenuIsEmbedded
        && !sceneMenuInstance.fromImport
        && !sceneMenuSelectedHidden
        && sceneMenuVisiblePeerCount >= 2
        && resolveDetailPacedId(
            sceneMenuInstance.detailKey,
            sceneMenuInstance.detail ?? sceneMenuInstance.detailData,
        ) != null,
    );
    const sceneMenuCanHideAllEmbeddedExcept = Boolean(
        sceneMenuTargetId
        && sceneMenuInstance
        && sceneMenuIsEmbedded
        && !sceneMenuSelectedHidden
        && sceneMenuVisiblePeerCount >= 2
        && resolveEmbeddedPacedId(sceneMenuInstance.moduleKey, sceneMenuInstance.row) != null,
    );
    const sceneMenuCanEdit = Boolean(
        sceneMenuInstance
        && !sceneMenuIsEmbedded
        && !sceneMenuInstance.fromImport
        && !isHandleDetail(sceneMenuInstance.detailData)
        && !isHandleDetail(sceneMenuInstance.detail)
        && getPostDetailId(sceneMenuInstance.detail ?? sceneMenuInstance.detailData) != null
    );
    const sceneMenuBusy = addingMode || isPlacing;

    return (
        <>
        <Box sx={{
            display: "flex",
            gap: 2,
            alignItems: "stretch",
            paddingBottom: "30px",
        }}>
            {/* Карточка: Детали модуля */}
            <Box
                sx={{
                    width: 620,
                    flexShrink: 0,
                    maxHeight: "calc(100vh - 180px)",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                    padding: 1.5,
                    overflowX: "hidden",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                    <Typography sx={{fontWeight: 700}}>
                        Детали модуля{moduleName ? `: ${moduleName}` : ""}
                    </Typography>
                    <Box sx={{flexShrink: 0}}>
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={<KeyboardArrowDownIcon/>}
                            disabled={addingMode || isPlacing || assemblyMenuBusy}
                            onClick={openAssemblyMenu}
                            sx={{textTransform: "none"}}
                        >
                            {assemblyFileBusy
                                ? (assemblyLoadProgress
                                    ? `Загрузка… ${assemblyLoadProgress}`
                                    : "Загрузка…")
                                : exportingGlb
                                    ? "Экспорт…"
                                    : "Сборка"}
                        </Button>
                        <Menu
                            anchorEl={assemblyMenuAnchor}
                            open={Boolean(assemblyMenuAnchor)}
                            onClose={closeAssemblyMenu}
                            anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                            transformOrigin={{vertical: "top", horizontal: "right"}}
                        >
                            <MenuItem
                                disabled={!canPersistAssembly || assemblyFileBusy}
                                onClick={saveAssemblyFile}
                            >
                                Сохранить сборку…
                            </MenuItem>
                            <MenuItem
                                disabled={assemblyFileBusy || detailsLoading}
                                onClick={openAssemblyFilePicker}
                            >
                                Загрузить сборку…
                            </MenuItem>
                            <Divider/>
                            <MenuItem
                                disabled={!placedDetails.length || exportingGlb || assemblyFileBusy}
                                onClick={exportGlb}
                            >
                                Экспорт GLB
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>
                <input
                    ref={assemblyFileInputRef}
                    type="file"
                    accept=".json,.upme-module3d.json,application/json"
                    hidden
                    onChange={onAssemblyFileChange}
                />
                <input
                    ref={dxfFileInputRef}
                    type="file"
                    accept=".dxf,.DXF"
                    hidden
                    onChange={onDxfFileChange}
                />
                <input
                    ref={handleFileInputRef}
                    type="file"
                    accept=".3ds,.3DS"
                    hidden
                    onChange={onHandleFileChange}
                />
                <Typography color="text.secondary" sx={{fontSize: 13}}>
                    {detailsLoading && tableRows.length === 0
                        ? "Загрузка деталей…"
                        : tableRows.length
                            ? `Деталей: ${tableRows.length}. `
                            : "Деталей пока нет. "}
                    {!(detailsLoading && tableRows.length === 0) ? instructionText : null}
                </Typography>

                <TableContainer
                    sx={{
                        overflow: "visible",
                        width: "100%",
                        minWidth: 0,
                        alignSelf: "stretch",
                    }}
                >
                    <Table
                        size="medium"
                        stickyHeader
                        sx={MODULE3D_TABLE_SX}
                    >
                        <colgroup>
                            <col style={{width: 40}}/>
                            <col style={{width: "60%"}}/>
                            <col style={{width: "40%"}}/>
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{width: 40, fontWeight: 700, px: "4px !important"}}> </TableCell>
                                <TableCell sx={{width: "60%", fontWeight: 700}}>Позиция</TableCell>
                                <TableCell sx={{width: "40%", fontWeight: 700}}>Наименование</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {detailsLoading && tableRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} sx={{border: "none", py: 6}}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                minHeight: 120,
                                            }}
                                        >
                                            <CircularProgress size={32} disableShrink/>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : tableRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} sx={{border: "none", py: 2}}>
                                        <Typography color="text.secondary" sx={{fontSize: 13}}>
                                            Деталей пока нет.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                            <>
                            {tableRows.map((row) => {
                                const {detail: d, detailKey, fromImport} = row;
                                const alreadyPlaced = (placedCountByKey.get(detailKey) ?? 0) > 0;
                                const pacedId = fromImport || clearedPacedKeys.has(detailKey)
                                    ? null
                                    : (
                                        pacedModuleIdByKey[detailKey]
                                        ?? pacedIdByKey[detailKey]
                                        ?? getPacedDetailModuleId(d)
                                        ?? null
                                    );
                                const serverHasPlacement = !fromImport && pacedId != null;
                                const loadingOntoScene = sceneHydrating && serverHasPlacement && !alreadyPlaced;
                                const savingOntoScene = postPendingDetailKey === detailKey;
                                const isLoadingOntoScene = loadingOntoScene || savingOntoScene;
                                const placedInst = placedDetails.find((item) => item.detailKey === detailKey);
                                const isHiddenOnScene = alreadyPlaced && Boolean(
                                    placedInst?.hidden ?? getPacedDtoHide(d?.pacedDetailModule),
                                );
                                const showMinus = alreadyPlaced && !isLoadingOntoScene;
                                const visibilityDisabled = fromImport
                                    || isLoadingOntoScene
                                    || !alreadyPlaced
                                    || (!isHiddenOnScene && (addingMode || isPlacing));
                                const visibilityTooltip = isLoadingOntoScene
                                    ? "Загружается на сцену"
                                    : (!alreadyPlaced && !fromImport
                                        ? "Деталь ещё не добавлена на сцену"
                                        : undefined);
                                const addDisabled = isLoadingOntoScene
                                    || addingMode
                                    || isPlacing
                                    || isHiddenOnScene
                                    || (!showMinus && alreadyPlaced);
                                const addMuted = isLoadingOntoScene || isHiddenOnScene;
                                const addTooltip = isLoadingOntoScene
                                    ? "Загружается на сцену"
                                    : (isHiddenOnScene
                                        ? "Сначала покажите деталь на сцене"
                                        : (showMinus ? "Убрать" : "Добавить"));
                                const rowMenuTooltip = isLoadingOntoScene
                                    ? "Загружается на сцену"
                                    : "Действия";
                                const rowContentFaded = isHiddenOnScene && alreadyPlaced && !fromImport;

                                const posColor = (theme) =>
                                    fromImport
                                        ? theme.palette.text.primary
                                        : d?.modified
                                            ? theme.palette.success.main
                                            : d?.excluded
                                                ? theme.palette.error.main
                                                : d?.ignored
                                                    ? theme.palette.pencil.main
                                                    : "black";

                                return (
                                    <TableRow
                                        key={detailKey}
                                        onClick={() => {
                                            if (!alreadyPlaced) return;
                                            selectPlacedDetailByKey(detailKey);
                                        }}
                                        sx={(theme) => ({
                                            height: "36px",
                                            backgroundColor:
                                                detailKey === selectedDetailKey
                                                    ? theme.palette.primary.light
                                                    : "white",
                                            "&:hover": {
                                                backgroundColor:
                                                    detailKey === selectedDetailKey
                                                        ? theme.palette.primary.light50
                                                        : theme.palette.primary.stroke,
                                            },
                                            "&:nth-of-type(even)": {
                                                backgroundColor:
                                                    detailKey === selectedDetailKey
                                                        ? theme.palette.primary.light
                                                        : theme.palette.primary.lightGrey,
                                            },
                                        })}
                                    >
                                            <TableCell sx={{px: "4px !important", width: 40}}>
                                                <Tooltip title={rowMenuTooltip} arrow placement="top">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={addingMode || isPlacing || isLoadingOntoScene}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openRowMenu(e, row);
                                                            }}
                                                            aria-label="Действия"
                                                            sx={{p: 0.25}}
                                                        >
                                                            <MoreHorizIcon fontSize="small"/>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        <TableCell sx={{width: "60%", overflow: "hidden", maxWidth: 0, opacity: rowContentFaded ? 0.5 : 1}}>
                                            {!fromImport && getPostDetailId(d) != null ? (
                                                <TableTextLink
                                                    title={d?.position || undefined}
                                                    onClick={() => openCompositionDetail(d)}
                                                    sx={TABLE_TEXT_LINK_INLINE_SX}
                                                >
                                                    {d?.position ?? "-"}
                                                </TableTextLink>
                                            ) : (
                                                <Box
                                                    title={d?.position || undefined}
                                                    sx={{
                                                        display: "block",
                                                        color: posColor,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {d?.position ?? "-"}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{width: "40%", overflow: "hidden", maxWidth: 0}}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 1,
                                                    paddingRight: "10px",
                                                    minHeight: "30px",
                                                    width: "100%",
                                                    boxSizing: "border-box",
                                                    minWidth: 0,
                                                }}
                                            >
                                                {pacedId != null ? (
                                                    <Box sx={{flex: 1, minWidth: 0, overflow: "hidden", opacity: rowContentFaded ? 0.5 : 1}}>
                                                        <TableTextLink
                                                            title={d?.name || undefined}
                                                            onClick={() => openPacedDetailCard(d, pacedId)}
                                                            sx={TABLE_TEXT_LINK_INLINE_SX}
                                                        >
                                                            {d?.name ?? "-"}
                                                        </TableTextLink>
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        title={d?.name || undefined}
                                                        sx={{
                                                            display: "block",
                                                            minWidth: 0,
                                                            flex: 1,
                                                            color: posColor,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            opacity: rowContentFaded ? 0.5 : 1,
                                                        }}
                                                    >
                                                        {d?.name ?? "-"}
                                                    </Box>
                                                )}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {!fromImport ? (
                                                        <TableVisibilityButton
                                                            hidden={isHiddenOnScene}
                                                            disabled={visibilityDisabled}
                                                            tooltip={visibilityTooltip}
                                                            onClick={() => {
                                                                void toggleDetailVisibility(
                                                                    detailKey,
                                                                    d,
                                                                    !isHiddenOnScene,
                                                                );
                                                            }}
                                                        />
                                                    ) : null}
                                                    <TableAddButton
                                                    showMinus={showMinus}
                                                    muted={addMuted}
                                                    disabled={addDisabled}
                                                    tooltip={addTooltip}
                                                    ariaLabel={showMinus ? "Убрать со сцены" : "Добавить на сцену"}
                                                    onClick={(event) => {
                                                        if (showMinus) {
                                                            void removeOneByDetailKey(detailKey, d);
                                                            return;
                                                        }
                                                        if (addDisabled) return;
                                                        setAddOrientationMenu({
                                                            anchorEl: event.currentTarget,
                                                            row,
                                                        });
                                                    }}
                                                />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {/*
                            <TableRow
                                onClick={() => {
                                    if (addingMode || isPlacing) return;
                                    openImportSourceDialog();
                                }}
                                sx={(theme) => ({
                                    height: "36px",
                                    cursor: addingMode || isPlacing ? "default" : "pointer",
                                    opacity: addingMode || isPlacing ? 0.55 : 1,
                                    backgroundColor: "white",
                                    "&:hover": addingMode || isPlacing
                                        ? undefined
                                        : {backgroundColor: theme.palette.primary.stroke},
                                    "& td": {
                                        borderTop: "1px dashed #EAEBEA",
                                    },
                                })}
                            >
                                <TableCell colSpan={3}>
                                    <Typography
                                        sx={{
                                            fontSize: "inherit",
                                            color: "text.secondary",
                                            py: 0.75,
                                            pr: 1,
                                        }}
                                    >
                                        Загрузить свою деталь…
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            */}
                            </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography sx={{fontWeight: 700, mt: 1}}>
                    Встраиваемые модули
                </Typography>
                <Typography color="text.secondary" sx={{fontSize: 13}}>
                    {embeddedModuleRows.length
                        ? `Модулей: ${embeddedModuleRows.length}. `
                        : "Встраиваемых модулей пока нет. "}
                    «+» добавляет модуль на сцену, «−» убирает.
                </Typography>

                <TableContainer
                    sx={{
                        overflow: "visible",
                        width: "100%",
                        minWidth: 0,
                        alignSelf: "stretch",
                    }}
                >
                    <Table
                        size="medium"
                        stickyHeader
                        sx={MODULE3D_TABLE_SX}
                    >
                        <colgroup>
                            <col style={{width: 40}}/>
                            <col style={{width: "32%"}}/>
                            <col style={{width: "68%"}}/>
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{width: 40, fontWeight: 700, px: "4px !important"}}> </TableCell>
                                <TableCell sx={{width: "32%", fontWeight: 700}}>Позиция</TableCell>
                                <TableCell sx={{width: "68%", fontWeight: 700}}>Наименование</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {embeddedModuleRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} sx={{border: "none", py: 2}}>
                                        <Typography color="text.secondary" sx={{fontSize: 13}}>
                                            Список пуст — добавьте модули на вкладке «Встраиваемые модули».
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                embeddedModuleRows.map(({row, moduleKey}, rowIndex) => {
                                    const moduleKeyStr = String(moduleKey);
                                    const onScene = embeddedPlacedByKey.has(moduleKeyStr);
                                    const pacedId = clearedPacedEmbeddedKeys.has(moduleKeyStr)
                                        ? null
                                        : (
                                            pacedEmbeddedIdByKey[moduleKey]
                                            ?? getPacedModuleId(row)
                                            ?? null
                                        );
                                    const serverHasPlacement = pacedId != null;
                                    const loadingOntoScene = (embeddedSceneHydrating || sceneHydrating)
                                        && serverHasPlacement
                                        && !onScene;
                                    const savingOntoScene = postPendingEmbeddedModuleKey === moduleKey;
                                    const placedEmbeddedInst = placedEmbeddedModules.find(
                                        (item) => String(item.moduleKey) === moduleKeyStr,
                                    );
                                    const childrenPending = onScene
                                        && placedEmbeddedInst
                                        && !placedEmbeddedInst.childrenHydrated;
                                    const isHiddenOnScene = onScene && Boolean(
                                        placedEmbeddedInst?.hidden ?? getPacedDtoHide(row?.pacedModule),
                                    );
                                    const isLoadingOntoScene = loadingOntoScene
                                        || savingOntoScene
                                        || childrenPending;
                                    const showMinus = onScene && !isLoadingOntoScene;
                                    const visibilityDisabled = isLoadingOntoScene
                                        || !onScene
                                        || (!isHiddenOnScene && (addingMode || isPlacing));
                                    const visibilityTooltip = isLoadingOntoScene
                                        ? "Загружается на сцену"
                                        : (!onScene ? "Модуль ещё не добавлен на сцену" : undefined);
                                    const addDisabled = isLoadingOntoScene
                                        || addingMode
                                        || isPlacing
                                        || isHiddenOnScene
                                        || (!showMinus && onScene);
                                    const addMuted = isLoadingOntoScene || isHiddenOnScene;
                                    const addTooltip = isLoadingOntoScene
                                        ? "Загружается на сцену"
                                        : (isHiddenOnScene
                                            ? "Сначала покажите модуль на сцене"
                                            : (showMinus ? "Убрать" : "Добавить"));
                                    const rowMenuTooltip = isLoadingOntoScene
                                        ? "Загружается на сцену"
                                        : "Действия";
                                    const rowContentFaded = isHiddenOnScene && onScene;
                                    const embeddedModuleTitle = [
                                        row?.name,
                                        formatEmbeddedModuleSize(row) !== "-"
                                            ? formatEmbeddedModuleSize(row)
                                            : null,
                                    ].filter(Boolean).join(" · ") || undefined;
                                    const embeddedPacedModulePagePath = buildDependModulePagePath(
                                        row,
                                        moduleId,
                                        moduleName,
                                    );

                                    return (
                                        <TableRow
                                            key={moduleKey}
                                            onClick={() => {
                                                if (!onScene) return;
                                                selectPlacedEmbeddedByKey(moduleKey);
                                            }}
                                            sx={(theme) => ({
                                                height: "36px",
                                                backgroundColor:
                                                    moduleKeyStr === selectedEmbeddedModuleKey
                                                        ? theme.palette.primary.light
                                                        : "white",
                                                "&:hover": {
                                                    backgroundColor:
                                                        moduleKeyStr === selectedEmbeddedModuleKey
                                                            ? theme.palette.primary.light50
                                                            : theme.palette.primary.stroke,
                                                },
                                                "&:nth-of-type(even)": {
                                                    backgroundColor:
                                                        moduleKeyStr === selectedEmbeddedModuleKey
                                                            ? theme.palette.primary.light
                                                            : theme.palette.primary.lightGrey,
                                                },
                                            })}
                                        >
                                            <TableCell sx={{px: "4px !important", width: 40}}>
                                                <Tooltip title={rowMenuTooltip} arrow placement="top">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={addingMode || isPlacing || isLoadingOntoScene}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openEmbeddedRowMenu(e, row, moduleKey);
                                                            }}
                                                            aria-label="Действия"
                                                            sx={{p: 0.25}}
                                                        >
                                                            <MoreHorizIcon fontSize="small"/>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{width: "32%", overflow: "hidden", maxWidth: 0, opacity: rowContentFaded ? 0.5 : 1}}>
                                                {embeddedPacedModulePagePath ? (
                                                    <Box
                                                        component={Link}
                                                        to={embeddedPacedModulePagePath}
                                                        onClick={(event) => event.stopPropagation()}
                                                        title={embeddedModuleTitle}
                                                        sx={{
                                                            ...TABLE_TEXT_LINK_SX,
                                                            ...TABLE_TEXT_LINK_INLINE_SX,
                                                            textDecoration: "none",
                                                            "&:hover": {
                                                                textDecoration: "underline",
                                                            },
                                                        }}
                                                    >
                                                        {row?.name ?? "-"}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        title={embeddedModuleTitle}
                                                        sx={{
                                                            display: "block",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {row?.name ?? "-"}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{width: "68%", overflow: "hidden", maxWidth: 0}}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                        paddingRight: "10px",
                                                        minHeight: "30px",
                                                        width: "100%",
                                                        boxSizing: "border-box",
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {embeddedPacedModulePagePath ? (
                                                        <Box sx={{flex: 1, minWidth: 0, overflow: "hidden", opacity: rowContentFaded ? 0.5 : 1}}>
                                                            <TableTextLink
                                                                title="Встраиваемый модуль"
                                                                onClick={() => openEmbeddedPacedModuleCard(row)}
                                                                sx={TABLE_TEXT_LINK_INLINE_SX}
                                                            >
                                                                Встраиваемый модуль
                                                            </TableTextLink>
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            title="Встраиваемый модуль"
                                                            sx={{
                                                                display: "block",
                                                                minWidth: 0,
                                                                flex: 1,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                opacity: rowContentFaded ? 0.5 : 1,
                                                            }}
                                                        >
                                                            Встраиваемый модуль
                                                        </Box>
                                                    )}
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <TableVisibilityButton
                                                            hidden={isHiddenOnScene}
                                                            disabled={visibilityDisabled}
                                                            tooltip={visibilityTooltip}
                                                            onClick={() => {
                                                                void toggleEmbeddedVisibility(
                                                                    moduleKey,
                                                                    row,
                                                                    !isHiddenOnScene,
                                                                );
                                                            }}
                                                        />
                                                        <TableAddButton
                                                        showMinus={showMinus}
                                                        muted={addMuted}
                                                        disabled={addDisabled}
                                                        tooltip={addTooltip}
                                                        ariaLabel={showMinus ? "Убрать со сцены" : "Добавить на сцену"}
                                                        onClick={() => {
                                                            if (showMinus) {
                                                                void removeEmbeddedByModuleKey(moduleKey, row);
                                                                return;
                                                            }
                                                            if (addDisabled) return;
                                                            startAddEmbeddedFromTable(row, moduleKey, rowIndex);
                                                        }}
                                                    />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Menu
                    anchorEl={addOrientationMenu?.anchorEl}
                    open={Boolean(addOrientationMenu)}
                    onClose={() => setAddOrientationMenu(null)}
                    anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                    transformOrigin={{vertical: "top", horizontal: "right"}}
                >
                    {DETAIL_ADD_ORIENTATION_OPTIONS.map((option) => (
                        <MenuItem
                            key={option.id}
                            onClick={() => {
                                const row = addOrientationMenu?.row;
                                setAddOrientationMenu(null);
                                if (!row) return;
                                startAddFromTable(row, option.axes);
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>

                <Menu
                    anchorEl={rowMenu?.anchorEl}
                    open={Boolean(rowMenu)}
                    onClose={closeRowMenu}
                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                >
                    <MenuItem
                        disabled={addingMode || isPlacing || (rowMenu?.detailKey != null && postPendingDetailKey === rowMenu.detailKey)}
                        onClick={() => {
                            if (!rowMenu) return;
                            if (isHandleDetail(rowMenu.detail) || rowMenu.kind === "handle") {
                                setHandleEditTarget({
                                    detailKey: rowMenu.detailKey,
                                    detail: rowMenu.detail,
                                });
                            } else {
                                setDxfEditTarget({
                                    detailKey: rowMenu.detailKey,
                                    detail: rowMenu.detail,
                                    fromImport: rowMenu.fromImport,
                                });
                            }
                            closeRowMenu();
                        }}
                    >
                        Редактировать
                    </MenuItem>
                    {rowMenu && !rowMenu.fromImport && resolveDetailPacedId(rowMenu.detailKey, rowMenu.detail) != null ? (
                        <>
                            <MenuItem
                                disabled={addingMode || isPlacing || postPendingDetailKey === rowMenu.detailKey}
                                onClick={() => {
                                    const inst = placedDetails.find(
                                        (item) => item.detailKey === rowMenu.detailKey,
                                    );
                                    const isHidden = Boolean(
                                        inst?.hidden ?? getPacedDtoHide(rowMenu.detail?.pacedDetailModule),
                                    );
                                    void toggleDetailVisibility(
                                        rowMenu.detailKey,
                                        rowMenu.detail,
                                        !isHidden,
                                    );
                                    closeRowMenu();
                                }}
                            >
                                {(() => {
                                    const inst = placedDetails.find(
                                        (item) => item.detailKey === rowMenu.detailKey,
                                    );
                                    const isHidden = Boolean(
                                        inst?.hidden ?? getPacedDtoHide(rowMenu.detail?.pacedDetailModule),
                                    );
                                    return isHidden ? "Показать на сцене" : "Скрыть на сцене";
                                })()}
                            </MenuItem>
                            <MenuItem
                                disabled={addingMode || isPlacing || !hasVisiblePlacedDetails}
                                onClick={() => {
                                    void setAllDetailsVisibility(true);
                                    closeRowMenu();
                                }}
                            >
                                Скрыть все
                            </MenuItem>
                            <MenuItem
                                disabled={addingMode || isPlacing || !hasHiddenPlacedDetails}
                                onClick={() => {
                                    void setAllDetailsVisibility(false);
                                    closeRowMenu();
                                }}
                            >
                                Показать все
                            </MenuItem>
                            <MenuItem
                                disabled={
                                    addingMode
                                    || isPlacing
                                    || !hasVisiblePlacedDetails
                                    || (() => {
                                        const inst = placedDetails.find(
                                            (item) => item.detailKey === rowMenu.detailKey,
                                        );
                                        return Boolean(
                                            inst?.hidden ?? getPacedDtoHide(rowMenu.detail?.pacedDetailModule),
                                        );
                                    })()
                                }
                                onClick={() => {
                                    void setAllDetailsVisibility(true, rowMenu.detailKey);
                                    closeRowMenu();
                                }}
                            >
                                Скрыть кроме текущей
                            </MenuItem>
                        </>
                    ) : null}
                </Menu>

                <Menu
                    anchorEl={embeddedRowMenu?.anchorEl}
                    open={Boolean(embeddedRowMenu)}
                    onClose={closeEmbeddedRowMenu}
                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                >
                    {embeddedRowMenu && resolveEmbeddedPacedId(
                        embeddedRowMenu.moduleKey,
                        embeddedRowMenu.row,
                    ) != null ? (
                        <>
                            <MenuItem
                                disabled={addingMode || isPlacing}
                                onClick={() => {
                                    const moduleKey = embeddedRowMenu.moduleKey;
                                    const row = embeddedRowMenu.row;
                                    const inst = placedEmbeddedModules.find(
                                        (item) => String(item.moduleKey) === String(moduleKey),
                                    );
                                    const isHidden = Boolean(
                                        inst?.hidden ?? getPacedDtoHide(row?.pacedModule),
                                    );
                                    void toggleEmbeddedVisibility(moduleKey, row, !isHidden);
                                    closeEmbeddedRowMenu();
                                }}
                            >
                                {(() => {
                                    const inst = placedEmbeddedModules.find(
                                        (item) => String(item.moduleKey) === String(embeddedRowMenu.moduleKey),
                                    );
                                    const isHidden = Boolean(
                                        inst?.hidden ?? getPacedDtoHide(embeddedRowMenu.row?.pacedModule),
                                    );
                                    return isHidden ? "Показать на сцене" : "Скрыть на сцене";
                                })()}
                            </MenuItem>
                            <MenuItem
                                disabled={addingMode || isPlacing || !hasVisiblePlacedEmbedded}
                                onClick={() => {
                                    void setAllEmbeddedVisibility(true);
                                    closeEmbeddedRowMenu();
                                }}
                            >
                                Скрыть все
                            </MenuItem>
                            <MenuItem
                                disabled={addingMode || isPlacing || !hasHiddenPlacedEmbedded}
                                onClick={() => {
                                    void setAllEmbeddedVisibility(false);
                                    closeEmbeddedRowMenu();
                                }}
                            >
                                Показать все
                            </MenuItem>
                        </>
                    ) : null}
                </Menu>

                <Menu
                    open={Boolean(sceneContextMenu)}
                    onClose={() => setSceneContextMenu(null)}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        sceneContextMenu
                            ? {top: sceneContextMenu.mouseY, left: sceneContextMenu.mouseX}
                            : undefined
                    }
                    slotProps={{
                        paper: {
                            sx: {minWidth: 268},
                        },
                    }}
                >
                    {sceneMenuHasSelection && sceneMenuCanEdit ? (
                        <>
                            <MenuItem
                                disabled={sceneMenuBusy}
                                onClick={() => {
                                    setSceneContextMenu(null);
                                    if (!sceneMenuInstance) return;
                                    openCompositionDetail(sceneMenuInstance.detail ?? sceneMenuInstance.detailData);
                                }}
                            >
                                Редактировать
                            </MenuItem>
                            <Divider />
                        </>
                    ) : null}
                    <ListSubheader disableSticky sx={SCENE_CONTEXT_MENU_SUBHEADER_SX}>
                        Детали
                    </ListSubheader>
                    <MenuItem
                        disabled={sceneMenuBusy || !sceneMenuCanHideAllDetailsExcept}
                        onClick={() => {
                            if (!sceneMenuInstance?.detailKey) return;
                            void setAllDetailsVisibility(true, sceneMenuInstance.detailKey);
                        }}
                    >
                        Скрыть все, кроме выделенной
                    </MenuItem>
                    <MenuItem
                        disabled={sceneMenuBusy || !hasVisiblePlacedDetails}
                        onClick={() => {
                            void setAllDetailsVisibility(true);
                        }}
                    >
                        Скрыть все
                    </MenuItem>
                    <MenuItem
                        disabled={sceneMenuBusy || !canRevealAllDetails}
                        onClick={() => {
                            revealAllModuleDetails();
                        }}
                    >
                        Показать все
                    </MenuItem>
                    {hasDependModules ? (
                        <>
                            <Divider sx={{my: 0.5}} />
                            <ListSubheader disableSticky sx={SCENE_CONTEXT_MENU_SUBHEADER_SX}>
                                Встраиваемые модули
                            </ListSubheader>
                            <MenuItem
                                disabled={sceneMenuBusy || !sceneMenuCanHideAllEmbeddedExcept}
                                onClick={() => {
                                    if (sceneMenuInstance?.moduleKey == null) return;
                                    void setAllEmbeddedVisibility(true, sceneMenuInstance.moduleKey);
                                }}
                            >
                                Скрыть все, кроме выделенного
                            </MenuItem>
                            <MenuItem
                                disabled={sceneMenuBusy || !hasVisiblePlacedEmbedded}
                                onClick={() => {
                                    void setAllEmbeddedVisibility(true);
                                }}
                            >
                                Скрыть все
                            </MenuItem>
                            <MenuItem
                                disabled={sceneMenuBusy || !canRevealAllEmbedded}
                                onClick={() => {
                                    revealAllEmbeddedModules();
                                }}
                            >
                                Показать все
                            </MenuItem>
                        </>
                    ) : null}
                </Menu>

                <Module3DToolbar
                    hasSelection={Boolean(selectedInstanceId)}
                    disabled={addingMode || isPlacing}
                    onRotate90={rotateSelectedBy90}
                    onMirror={mirrorSelected}
                    basePointMm={basePointMm}
                    onBasePointCommit={commitBasePointMm}
                    snapEnabled={snapEnabled}
                    onSnapEnabledChange={setSnapEnabled}
                    orthoEnabled={orthoEnabled}
                    onOrthoEnabledChange={setOrthoEnabled}
                    showReferenceCube={showReferenceCube}
                    onShowReferenceCubeChange={setShowReferenceCube}
                    showShellTransparent={showShellTransparent}
                    onShowShellTransparentChange={setShowShellTransparent}
                    showHoles={showHoles}
                    onShowHolesChange={setShowHoles}
                    showGrooves={showGrooves}
                    onShowGroovesChange={setShowGrooves}
                    showOtherMachining={showOtherMachining}
                    onShowOtherMachiningChange={setShowOtherMachining}
                    materialMode={materialMode}
                    textures={textureCatalog}
                    selectedTextureId={selectedTextureId}
                    onTextureSelect={applyTexture}
                    onTextureUpload={uploadCustomTexture}
                    onTextureEdit={editCustomTexture}
                    onTextureDelete={deleteCustomTexture}
                    texturesDisabled={isPlacing}
                    textureRotationDeg={selectedTextureRotationDeg}
                    onTextureRotate90={rotateTextureBy90}
                    textureScaleMm={selectedTextureScaleMm}
                    onTextureScaleStep={stepTextureScale}
                    handleMaterials={handleMaterialCatalog}
                    selectedHandleMaterialId={selectedHandleMaterialId}
                    fromFileMaterialPreview={fromFileMaterialPreview}
                    onHandleMaterialSelect={applyHandleMaterial}
                    onHandleMaterialAdd={openCreateHandleMaterial}
                    onHandleMaterialEdit={openEditHandleMaterial}
                    onHandleMaterialDelete={requestDeleteHandleMaterial}
                    detailFreeRotationUi="module"
                    freeRotationSelectionOk={showDetailFreeRotation}
                    freeRotationEnabled={freeRotationEnabled}
                    onFreeRotationEnabledChange={setFreeRotationEnabled}
                    freeRotationAxis={freeRotationAxis}
                    onFreeRotationAxisChange={handleFreeRotationAxisChange}
                    freeRotationAngleDrafts={freeRotationAngleDrafts}
                    freeRotationAppliedAngles={freeRotationAppliedAngles}
                    onFreeRotationAngleDraftChange={(axisKey, value) => {
                        setFreeRotationAngleDrafts((prev) => ({
                            ...prev,
                            [axisKey]: value,
                        }));
                    }}
                    onFreeRotationAngleApply={applyFreeRotationByAngle}
                />
            </Box>

            {/* Карточка: Сцена */}
            <Box
                sx={{
                    flex: 1,
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 420,
                }}
            >
                <Module3DViewBar
                    activeCameraView={activeCameraView}
                    onCameraViewChange={applyCameraView}
                    disabled={isPlacing}
                    canUndo={sceneHistory.past.length > 0}
                    canRedo={sceneHistory.future.length > 0}
                    onUndo={undoSceneChange}
                    onRedo={redoSceneChange}
                />
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        padding: 0,
                        backgroundColor: "#f3f4f6",
                        minHeight: 0,
                    }}
                >
                    <Box
                        ref={setSceneHostNode}
                        sx={{
                            width: "100%",
                            height: "100%",
                            flex: 1,
                            overflow: "hidden",
                            position: "relative",
                            backgroundColor: "#fff",
                            minHeight: 0,
                        }}
                    >
                        <Canvas
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                            }}
                            gl={{antialias: true}}
                            dpr={[1, 2]}
                            onPointerMissed={(event) => {
                                if (addingMode) return;
                                if (scenePointerRef.current.dragged) return;
                                if (event.button === 2) return;
                                clearSceneIsolation();
                                setSelectedInstanceId(null);
                            }}
                            onContextMenu={(event) => {
                                event.preventDefault();
                                if (addingMode || isPlacing) return;
                                if (scenePointerRef.current.dragged) return;
                                const hitId = rmbOnSelectedRef.current;
                                const menuTargetId = hitId ?? selectedInstanceId ?? null;
                                if (hitId) {
                                    setSelectedInstanceId(hitId);
                                }
                                setSceneContextMenu({
                                    mouseX: event.clientX,
                                    mouseY: event.clientY,
                                    instanceId: menuTargetId,
                                });
                            }}
                        >
                            <Module3DScene
                                moduleSizeM={moduleSizeM}
                                activeCameraView={activeCameraView}
                                cameraViewRequest={cameraViewRequest}
                                placedDetails={scenePlacedDetails}
                                placedEmbeddedModules={placedEmbeddedModules}
                                selectedInstanceId={selectedInstanceId}
                                addingMode={addingMode && !isPlacing}
                                snapEnabled={snapEnabled}
                                orthoEnabled={orthoEnabled}
                                showReferenceCube={showReferenceCube}
                                showHoles={showHoles}
                                showGrooves={showGrooves}
                                showOtherMachining={showOtherMachining}
                                shellTransparent={showShellTransparent}
                                suppressSelectionVisual={suppressSelectionVisual}
                                postPendingInstanceId={postPendingInstanceId}
                                postPendingEmbeddedModuleKey={postPendingEmbeddedModuleKey}
                                gizmoDraggingRef={gizmoDraggingRef}
                                sceneRef={sceneRef}
                                isolatedDetailInstanceId={isolatedDetailInstanceId}
                                isolatedEmbeddedInstanceId={isolatedEmbeddedInstanceId}
                                onAddAt={addAt}
                                onSelectInstance={setSelectedInstanceId}
                                onTransformEnd={updateInstanceTransform}
                                onSelectedRightPointerDown={markSelectedRightPointerDown}
                                freeRotationEnabled={freeRotationEnabled}
                                freeRotationAxis={freeRotationAxis}
                                freeRotationAxisEmphasis={
                                    freeRotationEnabled && showDetailFreeRotation
                                }
                            />
                        </Canvas>
                    </Box>
                </Box>
            </Box>
        </Box>
        <CustomTextureUploadDialog
            open={Boolean(uploadSource)}
            source={uploadSource}
            onClose={() => {
                setLiveTexturePreview(null);
                setUploadSource(null);
            }}
            onConfirm={confirmCustomTexture}
            onLivePreview={handleLiveTexturePreview}
            hasSceneTarget={hasLivePreviewTarget}
        />
        <TextureDeleteConfirmDialog
            open={Boolean(textureDeleteTarget)}
            textureName={textureDeleteTarget?.name}
            affectedDetailNames={textureDeleteAffectedNames}
            onClose={cancelDeleteTexture}
            onConfirm={confirmDeleteTexture}
        />
        <HandleMaterialDialog
            open={Boolean(handleMaterialDialog)}
            initialValues={handleMaterialDialog?.material ?? null}
            onClose={() => {
                setLiveHandleMaterialPreview(null);
                setHandleMaterialDialog(null);
            }}
            onConfirm={confirmHandleMaterialDialog}
            onLivePreview={handleLiveHandleMaterialPreview}
        />
        <TextureDeleteConfirmDialog
            open={Boolean(handleMaterialDeleteTarget)}
            textureName={handleMaterialDeleteTarget?.name}
            affectedDetailNames={
                placedDetails
                    .filter((inst) => inst.handleMaterialId === handleMaterialDeleteTarget?.id)
                    .map((inst) => inst.detailData?.name || inst.detail?.name || "Ручка")
            }
            onClose={() => setHandleMaterialDeleteTarget(null)}
            onConfirm={confirmDeleteHandleMaterial}
        />
        <ModalConfirm
            title="Загрузить сборку?"
            modalOpen={Boolean(pendingAssemblyFile)}
            handleClose={closeAssemblyLoadConfirm}
            handleSubmit={confirmAssemblyLoad}
            action="Подтвердить"
            loading={assemblyFileBusy}
        >
            Положения деталей с совпадающей позицией будут обновлены и сохранены на сервер.
        </ModalConfirm>
        <ImportSourceDialog
            open={importSourceOpen}
            onClose={closeImportSourceDialog}
            onConfirm={confirmImportSource}
        />
        <DxfImportDialog
            open={Boolean(dxfImport)}
            fileName={dxfImport?.fileName}
            meta={dxfImport?.meta}
            parseError={dxfImport?.parseError}
            onClose={closeDxfImport}
            onConfirm={confirmDxfImport}
        />
        <DxfImportDialog
            open={Boolean(dxfEditTarget)}
            mode="edit"
            initialValues={dxfEditTarget?.detail ? (() => {
                const detail = dxfEditTarget.detail;
                const height = Number(detail.height) || 18;
                const holes = Array.isArray(detail.holes) ? detail.holes : [];
                const firstDepth = Number(holes[0]?.depth);
                const holesThrough = holes.length === 0
                    || holes.every((h) => isThroughFaceHole(h, height));
                return {
                    name: detail.name,
                    position: detail.position,
                    heightMm: height,
                    length: detail.length,
                    width: detail.width,
                    holeCount: holes.length,
                    holeDepthMm: Number.isFinite(firstDepth) ? firstDepth : height * 0.5,
                    holesThrough,
                };
            })() : null}
            onClose={closeDxfEdit}
            onConfirm={confirmDxfEdit}
        />
        <HandleImportDialog
            open={Boolean(handleImport)}
            fileName={handleImport?.fileName}
            meta={handleImport?.meta}
            parseError={handleImport?.parseError}
            onClose={closeHandleImport}
            onConfirm={confirmHandleImport}
        />
        <HandleImportDialog
            open={Boolean(handleEditTarget)}
            mode="edit"
            initialValues={handleEditTarget?.detail ? {
                name: handleEditTarget.detail.name,
                position: handleEditTarget.detail.position,
                sizeMm: handleEditTarget.detail.sizeMm ?? DEFAULT_HANDLE_SIZE_MM,
            } : null}
            onClose={closeHandleEdit}
            onConfirm={confirmHandleEdit}
        />
        <ModalAlert data={alertData} onClose={handleCloseAlert}/>
        </>
    );
};

export default Module3DView;
